import { SUPABASE_PUBLISHABLE_KEY } from '../lib/supabase';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { CANONICAL_SERVICES, normalizeServiceName, fetchCanonicalServices, sameServiceName } from '../lib/services';
import { orderSearchResults, fetchWithTimeout, bookSearchHeaders, SEARCH_BOX_TIMEOUT_MS, SEARCH_FAILED_MESSAGE, outsideSourcesFailed, publicationYear } from '../lib/bookSearch';
import EventsBrowser from '../components/events/EventsBrowser';

// Newsletter and podcast feeds live on hosts we do not run. They get longer
// than a search box, and still a deadline.
const FEED_TIMEOUT_MS = 15000;
import { getCurrentUser } from '../lib/auth';
import * as Sentry from '@sentry/react';
import {
  MAX_CUSTOM_LINKS, MAX_LABEL_LENGTH, MAX_IMAGE_BYTES,
  LINK_IMAGE_BUCKET, LINK_IMAGE_PREFIX,
  normalizeLinkUrl, normalizeLinkLabel, newLinkId, linksFromBlockData,
  customLinkEntityId,
} from '../lib/customLinks';
import { parseNewsletterUrl } from '../lib/newsletterUrl';
import { activeThought } from '../lib/thoughts';
import { hotTakesOf, cleanHotTakes, hotTakesProblem, hotTakesPatch, MAX_HOT_TAKES, HOT_TAKE_MAX_LENGTH } from '../lib/hotTakes';
import { currentlyReadingOf, currentlyReadingPatch, isSameBook, sameCurrentlyReading, MAX_CURRENTLY_READING } from '../lib/currentlyReading';
import { useSwipe } from '../hooks/useSwipe';
import { onlyUpcoming, formatEventDate, eventDayOfMonth } from '../lib/eventDates';
import { excludeRsvpCopies } from '../lib/eventCopies';
import { resolveTagWithBucket, resolveTagToTropeId, submitTropeName, tagNameError, writeReaderGenrePicks } from '../lib/tropeUtils';
import { findOrCreateBook } from '../lib/findOrCreateBook';
import { requestBookReembed } from '../lib/reembedBook';
import { createPortal } from 'react-dom';
import ReportModal, { KebabMenu, KebabItem } from '../components/community/ReportModal';
import { onCoverError } from '../lib/coverFallback';
import { isBookless, bookIdsToLookUp, sameBookEntry, cleanEntryTitle, entryTitleLength, entryTitleProblem, limitEntryTitle, ENTRY_TITLE_MAX } from '../lib/journalEntryBook';
import { readerTagCredit, canRemove, removeTagIAdded } from '../lib/tagCredit';
import { deleteVoiceNote, deleteOrphanedUpload, uploadVoiceNote, getVoiceNoteUrl } from '../lib/voiceNoteStorage';
import { hasGrantedVoiceConsent, grantVoiceConsent, CONSENT_TEXT } from '../lib/voiceConsent';
import { transcribeVoiceNote } from '../lib/voiceTranscription';
import { transcribeRecordingInline } from '../lib/voiceTranscribeInline';
import { encodeWav16, bytesToBase64 } from '../lib/voiceAudio';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { useGenreTaxonomy } from '../hooks/useGenreTaxonomy';
import GenreSubgenreFilter, { genreFilterNames } from '../components/genre/GenreSubgenreFilter';
import GenreSubgenrePicker from '../components/genre/GenreSubgenrePicker';

// The lawyer's consent wording (voiceConsent.js) has to include a link to
// the Voice & Biometric Data Policy inside the checkbox's own label, not
// just somewhere nearby on the page -- see that file's header comment.
// Rather than hand-typing the sentence a second time here with a <a> baked
// into the middle of it (guaranteed to drift from voiceConsent.js's copy
// the first time either one gets edited), this splits CONSENT_TEXT once, at
// module load, on the exact phrase the link belongs on, and the render
// below re-assembles it with that phrase turned into the real link.
//
// The failure modes here were checked, not assumed -- an earlier version of
// this comment claimed a dropped or renamed phrase makes the split "produce
// a one-part array and the link disappears entirely". Run against the
// actual three cases, that is not what happens:
//   0 occurrences  -- split() returns a one-element array. Destructuring
//                     leaves CONSENT_TEXT_AFTER_LINK undefined (renders as
//                     nothing) and CONSENT_TEXT_BEFORE_LINK holding the
//                     WHOLE string -- so the render still prints the link,
//                     just glued onto the end of the untouched text.
//   2+ occurrences -- split() returns a 3+-element array. Destructuring
//                     only the first two elements SILENTLY DROPS everything
//                     after the second occurrence -- the reader would see,
//                     and agree to, a truncated legally-binding statement
//                     with nothing reporting it.
// Neither is survivable silently, so this asserts the shape at module load
// instead: exactly one occurrence, or throw at build/boot rather than in
// front of a reader.
const CONSENT_LINK_PHRASE = 'Voice & Biometric Data Policy';
const CONSENT_PARTS = CONSENT_TEXT.split(CONSENT_LINK_PHRASE);
if (CONSENT_PARTS.length !== 2) {
  throw new Error(`CONSENT_TEXT must contain "${CONSENT_LINK_PHRASE}" exactly once (found ${CONSENT_PARTS.length - 1})`);
}
const [CONSENT_TEXT_BEFORE_LINK, CONSENT_TEXT_AFTER_LINK] = CONSENT_PARTS;

// READER_GENRES was deleted here (PR3, docs/genre-subgenre-build-spec.md):
// the flat 19-name list TagPicker's Genres tab used to toggle through
// `tags`. Now dead -- the Genres tab is Control B (GenreSubgenreFilter),
// backed by the real genres/subgenres tables, with its own id-based state.
const READER_TROPES = ['Enemies to Lovers','Friends to Lovers','Slow Burn','Forced Proximity','Second Chance','Fake Relationship','Forbidden Love','Grumpy x Sunshine','Only One Bed','Marriage of Convenience','Secret Identity','Redemption Arc','Love Triangle','Soulmates','Amnesia','Chosen One','Cozy Mystery','Coming of Age','Dual Timeline','Regency Era'];
const READER_MOODS  = ['Dark','Light & Fluffy','Angsty','Humorous','Emotional','Fast-Paced','Character-Driven','Atmospheric','Witty Banter','Cozy','Hopeful','Melancholic','Suspenseful'];

// The Cancel / Add row of the two TagPicker modals ("If You Like What I Like",
// "To Know Me"), pinned to the bottom of the modal while its body scrolls.
// Both modals scroll inside var(--space-xxl) of padding, so the row pulls down
// into that padding (bottom + marginBottom) and paints over it (paddingBottom
// + background) -- otherwise content scrolling underneath shows through the
// gap below the buttons. Checked in Chromium: the row's bottom edge sits on
// the modal's bottom edge at the top of the scroll and at the end of it.
const TAG_MODAL_FOOTER = {
  display: 'flex', gap: 'var(--space-10)',
  position: 'sticky', bottom: 'calc(-1 * var(--space-xxl))', marginBottom: 'calc(-1 * var(--space-xxl))',
  padding: 'var(--space-md) 0 var(--space-xxl)', background: 'var(--brand-white)', zIndex: 2,
};

function TagPicker({
  tags, setTags, tropes, setTropes,
  selectedGenreIds, setSelectedGenreIds, selectedSubgenreIds, setSelectedSubgenreIds,
  genres, subgenresById, canonSubgenresForGenre, genreLoading, genreError, genreNeeded,
}) {
  const [customInput, setCustomInput] = React.useState('');
  const [tagWarning, setTagWarning] = React.useState('');
  const [activeSection, setActiveSection] = React.useState('tropes');
  const genreCount = selectedGenreIds.length + selectedSubgenreIds.length;
  // Combined trope+tag floor of 3 (genre spec :1814-1818) -- replaces the old
  // "3 tropes AND 5 total" pair, not stacked with it. genre/subgenre are
  // gated by the caller (its own genreNeeded test), not counted here.
  const combinedCount = tropes.length + tags.length;
  function toggleTrope(t) { setTropes(p => p.includes(t) ? p.filter(x=>x!==t) : [...p,t]); }
  function toggleMood(m)  { setTags(p => p.includes(m) ? p.filter(x=>x!==m) : [...p,m]); }
  function addCustom() {
    const v = customInput.trim();
    if (!v) return;
    // Refuse rather than truncate, and leave their text alone so they can edit
    // it down. See tagNameError in tropeUtils for why this is not a maxLength.
    const problem = tagNameError(v);
    if (problem) { setTagWarning(problem); return; }
    if (!tropes.includes(v)) setTropes(p => [...p, v]);
    setCustomInput('');
    setTagWarning('');
  }
  // Genre used to be flat names mixed into `tags` alongside moods
  // (READER_GENRES) -- PR3 (docs/genre-subgenre-build-spec.md) moves it to
  // Control B (GenreSubgenreFilter), backed by the real genres/subgenres
  // tables, with its own id-based state owned by the caller. `tags` now
  // holds moods only. The genre tab stays in the SAME tab bar ("already
  // tabbed, one section at a time -- drop B straight in"), it just renders
  // something else when active.
  const sections = [
    { id:'tropes', label:'🎭 Tropes', color:'var(--brand-rust)', opts:READER_TROPES, selected:tropes, toggle:toggleTrope },
    { id:'moods',  label:'🎭 Mood',   color:'#6b4c3b', opts:READER_MOODS,  selected:tags, toggle:toggleMood  },
  ];
  const TABS = genreNeeded
    ? [sections[0], { id:'genres', label:'📚 Genre *', color:'var(--brand-forest)' }, sections[1]]
    : [sections[0], sections[1]];
  const active = sections.find(s=>s.id===activeSection);
  const selectedGenres = selectedGenreIds.map(id => genres.find(g=>g.id===id)).filter(Boolean);
  const selectedSubgenres = selectedSubgenreIds.map(id => subgenresById.get(id)).filter(Boolean);
  return (

    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'var(--space-sm)' }}>
        <p style={{ fontSize:'var(--font-size-sm)', fontWeight:700, color:'var(--neutral-800)', margin:'var(--space-none)' }}>Tags</p>
        <div style={{ display:'flex', gap:'var(--space-6)' }}>
          <span style={{ fontSize:'var(--font-size-xs)', fontWeight:600, padding:'3px 9px', borderRadius:'var(--radius-md)', background:combinedCount>=3?'var(--feedback-success-bg)':'var(--feedback-warning-bg)', color:combinedCount>=3?'#16a34a':'var(--feedback-warning-ink)' }}>{combinedCount>=3?'✓':combinedCount+'/3'} tropes/tags</span>
        </div>
      </div>
      {(tropes.length>0||tags.length>0||genreCount>0) && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:'var(--space-sm)' }}>
          {tropes.map(t=><span key={`t-${t}`} style={{ display:'inline-flex', alignItems:'center', gap:'var(--space-xs)', padding:'4px 10px', background:'var(--brand-rust)', color:'var(--brand-white)', borderRadius:'var(--radius-xxl)', fontSize:'var(--font-size-xs)', fontWeight:600 }}>{t}<span onClick={()=>setTropes(p=>p.filter(x=>x!==t))} style={{ cursor:'pointer', opacity:0.8 }}>✕</span></span>)}
          {selectedGenres.map(g=><span key={`g-${g.id}`} style={{ display:'inline-flex', alignItems:'center', gap:'var(--space-xs)', padding:'4px 10px', background:'var(--brand-forest)', color:'var(--brand-white)', borderRadius:'var(--radius-xxl)', fontSize:'var(--font-size-xs)', fontWeight:600 }}>{g.name}<span onClick={()=>setSelectedGenreIds(p=>p.filter(x=>x!==g.id))} style={{ cursor:'pointer', opacity:0.8 }}>✕</span></span>)}
          {selectedSubgenres.map(s=><span key={`sg-${s.id}`} style={{ display:'inline-flex', alignItems:'center', gap:'var(--space-xs)', padding:'4px 10px', background:'var(--brand-forest)', color:'var(--brand-white)', borderRadius:'var(--radius-xxl)', fontSize:'var(--font-size-xs)', fontWeight:600 }}>{s.name}<span onClick={()=>setSelectedSubgenreIds(p=>p.filter(x=>x!==s.id))} style={{ cursor:'pointer', opacity:0.8 }}>✕</span></span>)}
          {tags.map(t=><span key={`m-${t}`} style={{ display:'inline-flex', alignItems:'center', gap:'var(--space-xs)', padding:'4px 10px', background:'#6b4c3b', color:'var(--brand-white)', borderRadius:'var(--radius-xxl)', fontSize:'var(--font-size-xs)', fontWeight:600 }}>{t}<span onClick={()=>setTags(p=>p.filter(x=>x!==t))} style={{ cursor:'pointer', opacity:0.8 }}>✕</span></span>)}
        </div>
      )}
      <div style={{ display:'flex', gap:5, marginBottom:'var(--space-sm)' }}>
        {TABS.map(s=><button key={s.id} onClick={()=>setActiveSection(s.id)} style={{ fontSize:'var(--font-size-xs)', fontWeight:600, padding:'5px 10px', borderRadius:'var(--radius-md)', cursor:'pointer', border:'none', background:activeSection===s.id?s.color:'var(--neutral-100)', color:activeSection===s.id?'var(--brand-white)':'var(--neutral-500)' }}>{s.label}</button>)}
      </div>
      {/* No height cap here, deliberately. This was a 130px scroll box inside
          a modal that already scrolls, so the genre tab -- 20 genres, then a
          subgenre panel opening underneath -- showed about four lines at a
          time behind a second scrollbar. The modal scrolls as one now, and
          its Cancel / Add row stays pinned to the bottom so it never scrolls
          away (see TAG_MODAL_FOOTER). */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:'var(--space-sm)' }}>
        {activeSection === 'genres' ? (
          <GenreSubgenreFilter
            Chip={TagPickerChip}
            genres={genres}
            canonSubgenresForGenre={canonSubgenresForGenre}
            loading={genreLoading}
            error={genreError}
            selectedGenreIds={selectedGenreIds}
            setSelectedGenreIds={setSelectedGenreIds}
            selectedSubgenreIds={selectedSubgenreIds}
            setSelectedSubgenreIds={setSelectedSubgenreIds}
          />
        ) : (
          active?.opts.map(o=>{ const on=active.selected.includes(o); return <button key={o} onClick={()=>active.toggle(o)} style={{ padding:'5px 11px', borderRadius:'var(--radius-xxl)', fontSize:'var(--font-size-sm)', fontWeight:on?700:500, border:'none', cursor:'pointer', background:on?active.color:'var(--neutral-100)', color:on?'var(--brand-white)':'var(--neutral-600)' }}>{o}</button>; })
        )}
      </div>
      <div style={{ display:'flex', gap:'var(--space-6)' }}>
        <input value={customInput}
          onChange={e=>{ setCustomInput(e.target.value); if (tagWarning) setTagWarning(''); }}
          onKeyDown={e=>e.key==='Enter'&&addCustom()}
          placeholder="Add custom trope..."
          aria-invalid={tagWarning ? 'true' : undefined}
          aria-describedby={tagWarning ? 'tagpicker-custom-warning' : undefined}
          style={{ flex:1, padding:'7px 10px', border: tagWarning ? '1px solid var(--feedback-warning-border)' : '1px solid var(--brand-sand)', borderRadius:'var(--radius-md)', fontSize:'var(--font-size-sm)', outline:'none' }} />
        <button onClick={addCustom} style={{ padding:'7px 12px', background:'var(--brand-cream)', border:'none', borderRadius:'var(--radius-md)', fontSize:'var(--font-size-sm)', color:'var(--feedback-warning-ink)', cursor:'pointer', fontWeight:600 }}>+ Add</button>
      </div>
      {tagWarning && (
        <p id="tagpicker-custom-warning" role="status" style={{ fontSize:'var(--font-size-xs)', color:'var(--feedback-warning-ink)', margin:'6px 0 0', lineHeight:1.4 }}>
          {tagWarning}
        </p>
      )}
    </div>
  );
}

// The tab bar's own flat-toggle look, handed to GenreSubgenreFilter as its
// injected Chip -- same dependency-injection pattern as everywhere else
// this component is reused (AddBook.jsx's ChipD/ChipM, FilterSheet.jsx's
// own Chip). Matches the existing "active.opts.map" button style exactly.
function TagPickerChip({ on, onClick, children }) {
  return (
    <button onClick={onClick} style={{ padding:'5px 11px', borderRadius:'var(--radius-xxl)', fontSize:'var(--font-size-sm)', fontWeight:on?700:500, border:'none', cursor:'pointer', background:on?'var(--brand-forest)':'var(--neutral-100)', color:on?'var(--brand-white)':'var(--neutral-600)' }}>{children}</button>
  );
}

function ensureUrl(url) {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
}

// ── Read-only blocks ──────────────────────────────────────────────────────────
// Every block takes `readOnly`, and PublicProfile.jsx passes it for every
// block. It used to pass only a no-op onUpdate, which stopped nothing: most
// blocks write to supabase directly, so a signed-in visitor could post the
// owner's showcase piece, services or shelf to the community under their own
// name, and one tap on the owner's Reading Goal "+" overwrote the visitor's
// own goal (audit 0.1). With readOnly a block shows its content and none of
// its owner controls: no pencil, no add, no share, no click-to-edit (0.5).
//
// What a visitor sees in a block the owner has not filled in yet. The owner's
// empty states are invitations ("+ Add your services") a visitor cannot take.
function VisitorEmpty({ onDark = false }) {
  return (
    <p style={{ fontSize: 'var(--font-size-sm)', color: onDark ? 'var(--neutral-500)' : 'var(--neutral-400)', fontStyle: 'italic', margin: 'var(--space-none)' }}>
      Nothing here yet.
    </p>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────────
// onDark is the light pencil, for a pencil that sits on a dark block: Ask Me
// About, and Amazon Storefront's dark header. The usual grey one was almost
// invisible there (audit 0.6). Its colours are Reading Stats' CSV button's,
// which sits in the same spot on a dark block.
const EDIT_BTN = ({ onClick, onDark = false }) => {
  const rest  = onDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const hover = onDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)';
  return (
    <button onClick={onClick} style={{
      position: 'absolute', top: 8, right: 36,
      width: 28, height: 28, borderRadius: 'var(--radius-circle)',
      background: rest, border: 'none',
      cursor: 'pointer', display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: 'var(--font-size-base)', color: onDark ? 'var(--neutral-300)' : 'var(--neutral-500)',
      transition: 'background 0.15s', zIndex: 5,
    }}
      onMouseEnter={e => e.currentTarget.style.background = hover}
      onMouseLeave={e => e.currentTarget.style.background = rest}
    >✎</button>
  );
};

// Two controls sit over a block's top-right corner, drawn on top of it rather
// than taking room in it: EDIT_BTN (top 8, right 36, 28 wide) and the ✕ that
// removes the block (ReaderProfile.jsx, BlockWrapper: top 8, right 8, 22 wide;
// always shown on a phone, on desktop while the pointer is over the block).
// So a block's title row has to stop short of them itself, or its last words
// run under them. These are how far from the block's right edge it must stop:
// each control's left edge, plus a 6px gap.
const EDGE_CLEAR_OF_EDIT   = 64 + 6;
const EDGE_CLEAR_OF_REMOVE = 30 + 6;
// The same for a title row inside a block's 16px padding, which most blocks
// have. A visitor sees neither control, so a readOnly block leaves it out.
//
// A title that has to stop short wraps sooner, so each title row's emoji is
// flexShrink: 0. index.css gives every element min-width: 0, which lets a
// flex row squeeze its emoji until it sits on the title's first letters.
const TITLE_CLEAR_OF_EDIT = EDGE_CLEAR_OF_EDIT - 16;

// Returns true once the post is in, false otherwise, so a caller that asked the
// reader "share this?" can say when it did not go out.
async function postToCommunity(userId, content, bookId = null) {
  try {
    const { error } = await supabase.from('posts').insert({
      user_id: userId,
      post_type: bookId ? 'book' : 'text',
      content,
      book_id: bookId,
    });
    if (error) {
      console.error('Community post error:', error.message, error.hint);
      return false;
    }
    return true;
  } catch (err) {
    console.error('Community post failed:', err.message);
    return false;
  }
}

// ── Google Books + Reanlo combined search ─────────────────────
async function searchBooksWithSource(query) {
  if (!query.trim()) return [];

  const results = [];

  const { data: reanloBooks } = await supabase
    .from('books')
    .select('id, title, author_name, cover_image_url, book_tags(tag_type, tropes(canonical_name)), book_genres(genre_id)')
    .or(`title.ilike.%${query}%,author_name.ilike.%${query}%`)
    .eq('status', 'live')
    .limit(5);

  (reanloBooks || []).forEach(b => {
    results.push({
      id: b.id,
      title: b.title,
      author_name: b.author_name,
      cover_image_url: b.cover_image_url,
      book_tags: b.book_tags || [],
      source: 'reanlo',
    });
  });

  // Outside books come through the edge function, which asks Google Books and
  // Open Library together. Calling either one straight from here would be a
  // second search path, and would put the Google key in the browser bundle.
  for (const b of await searchExternalBooks(query)) {
    const duplicate = results.some(r =>
      r.title?.toLowerCase() === b.title?.toLowerCase() &&
      r.author_name?.toLowerCase() === b.author_name?.toLowerCase()
    );
    if (duplicate) continue;
    results.push({
      id: null,
      google_id: b.google_id || null,
      title: b.title,
      author_name: b.author_name || 'Unknown Author',
      cover_image_url: b.cover_image_url || null,
      book_tags: [],
      source: b.source || 'openlibrary',
    });
  }

  return results;
}

// ── Source badge pill ─────────────────────────────────────────
function SourceBadge({ source }) {
  const config = {
    reanlo:      { bg: '#292524', label: 'REANLO' },
    openlibrary: { bg: '#e63c2f', label: 'OPEN LIB' },
    google:      { bg: '#4285f4', label: 'GOOGLE' },
  };
  const c = config[source] || config.openlibrary;
  return (
    <span style={{
      fontSize: 'var(--font-size-tiny)', fontWeight: 700, padding: '2px 6px', borderRadius: 'var(--radius-sm)',
      background: c.bg, color: 'var(--brand-white)', letterSpacing: 0.3, flexShrink: 0,
    }}>
      {c.label}
    </span>
  );
}

// ── Did that save? ────────────────────────────────────────────
// updateProfile (useUserProfile.js) reports a failed save by returning
// { error }, never by throwing, so `await onUpdate(...)` on its own cannot
// notice one: the editor closed, the block went back to the old value, and the
// reader was told nothing (audit 0.2). onUpdateBlock answers true or false
// (ReaderProfile.jsx, saveBlocks). Anything else is not a save either -- no
// result at all is the no-op onUpdate the public profile hands its blocks.
async function saved(pending) {
  try {
    const result = await pending;
    return result === true || (!!result && typeof result === 'object' && !result.error);
  } catch {
    return false;
  }
}

const SAVE_FAILED = "That didn't save. Check your connection and try again.";

// For a write that goes straight to supabase rather than through onUpdate,
// which updateProfile already reports.
function reportSaveFailure(where, error) {
  console.error(`${where} failed:`, error?.message || error);
  Sentry.captureException(error instanceof Error ? error : new Error(`${where}: ${error?.message || String(error)}`));
}

function SaveError({ message, onDark = false, style }) {
  if (!message) return null;
  return (
    <p role="alert" onClick={e => e.stopPropagation()}
      style={{ fontSize: 'var(--font-size-xs)', color: onDark ? '#fca5a5' : 'var(--feedback-error)', margin: '6px 0 0', lineHeight: 1.4, ...style }}>
      {message}
    </p>
  );
}

// "Profile only" after an earlier "Post to Community". The feed reads the text
// live from the reader's profile (useReaderCorner.js), so leaving the earlier
// post public puts the new text in the feed anyway (audit 1.2, 11.4). Making
// the row private takes it off. Posting again turns it back on. Answers true
// or false; no row yet is true, there is nothing to take off.
const STILL_IN_FEED = "Saved to your profile, but it is still in the community feed. Try again.";

async function takeOffFeed(postType) {
  const user = await getCurrentUser();
  if (!user) return true;
  const { error } = await supabase.from('community_thought_posts')
    .update({ is_public: false })
    .eq('user_id', user.id)
    .eq('post_type', postType);
  if (error) {
    reportSaveFailure(`Take ${postType} off the community feed`, error);
    return false;
  }
  return true;
}

// ═══════════════════════════════════════════════════════════════
// 💭 CURRENT THOUGHT
// ═══════════════════════════════════════════════════════════════




export function CurrentThoughtBlock({ profile, onUpdate, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(activeThought(profile) || '');
  const [saving, setSaving] = useState(false);
  // Which button started the save in flight, so only that one says so.
  const [via, setVia] = useState(null);
  const [saveError, setSaveError] = useState('');

  const thought = activeThought(profile);

  useEffect(() => {
    setText(activeThought(profile) || '');
  }, [profile]);

  async function handlePost() {
    if (!text.trim()) return;
    setVia('post');
    setSaving(true);
    setSaveError('');
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24);
    if (!(await saved(onUpdate({ current_thought: text, thought_expires_at: expiresAt.toISOString() })))) {
      // Nothing is posted for a thought that did not save, and the editor
      // stays open with what the reader wrote.
      setSaving(false);
      setSaveError(SAVE_FAILED);
      return;
    }

    // Post to community as plain 'text' type so it shows in the feed
const user = await getCurrentUser();
    if (user) {
      const { error } = await supabase.from('community_thought_posts').upsert(
        {
          user_id: user.id, post_type: 'thought', is_public: true,
          expires_at: expiresAt.toISOString(), updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,post_type' }
      );
      if (error) {
        console.error('Thought post error:', error.message, error.hint, error.code);
        alert('Community post failed: ' + error.message);
      }
    }
    setSaving(false);
    setEditing(false);
  }

  // The Remove button in the editor. Clearing the thought also drops its card
  // from the feed, which reads the text live from the profile.
  async function handleDelete() {
    if (saving) return;
    setSaving(true);
    setSaveError('');
    const ok = await saved(onUpdate({ current_thought: null, thought_expires_at: null }));
    setSaving(false);
    if (!ok) {
      setSaveError(SAVE_FAILED);
      return;
    }
    setText('');
    setEditing(false);
  }

  // "Profile only" used to fire the save without waiting for it and close the
  // editor straight away, so a failure could never be shown (audit 1.5).
  async function handleProfileOnly() {
    if (!text.trim() || saving) return;
    setVia('profile');
    setSaving(true);
    setSaveError('');
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24);
    const ok = await saved(onUpdate({ current_thought: text, thought_expires_at: expiresAt.toISOString() }));
    if (!ok) { setSaving(false); setSaveError(SAVE_FAILED); return; }
    // An earlier "Post to Community" would otherwise show this new text in the
    // feed. The editor stays open on a failure: trying again is safe.
    const offFeed = await takeOffFeed('thought');
    setSaving(false);
    if (!offFeed) { setSaveError(STILL_IN_FEED); return; }
    setEditing(false);
  }

  return (
    <div onClick={() => !readOnly && !editing && setEditing(true)}
      style={{ background: 'var(--feedback-warning-bg)', borderRadius: 14, padding: '18px 18px 14px', cursor: editing || readOnly ? 'default' : 'pointer', minHeight: 120, position: 'relative', border: '1px solid var(--feedback-warning-border)' }}>
      {/* No pencil here, only the ✕ to keep the countdown clear of. 18 is this block's padding. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-10)', paddingRight: readOnly ? 0 : EDGE_CLEAR_OF_REMOVE - 18 }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>💭</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--feedback-warning)', letterSpacing: 1, textTransform: 'uppercase' }}>Current Thought</span>
        {thought && (
          <span style={{ marginLeft: 'auto', fontSize: 'var(--font-size-xxs)', color: '#b45309', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {(() => {
              const diff = new Date(profile.thought_expires_at) - new Date();
              const hrs = Math.floor(diff / 3600000);
              return hrs > 0 ? `${hrs}h left` : 'expiring soon';
            })()}
          </span>
        )}
      </div>
      {editing ? (
        <>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onClick={e => e.stopPropagation()}
            placeholder="What's on your reading mind?"
            maxLength={280}
            autoFocus
            rows={3}
            style={{ width: '100%', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-md)', padding: '8px 10px', fontSize: 'var(--font-size-md)', background: 'rgba(255,255,255,0.6)', outline: 'none', resize: 'none', fontFamily: 'var(--font-display)', fontStyle: 'italic', color: 'var(--neutral-800)', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)' }}>
            <button onClick={e => { e.stopPropagation(); handlePost(); }} disabled={!text.trim() || saving}
              style={{ flex: 1, padding: '10px 5px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 7, fontWeight: 700, cursor: 'pointer', textAlign: 'center', lineHeight: 1.4 }}>
              {saving && via === 'post' ? 'Posting...' : 'Post to Community'}
            </button>
            <button onClick={e => { e.stopPropagation(); handleProfileOnly(); }} disabled={!text.trim() || saving}
              style={{ flex: 1, padding: '10px 8px', background: 'rgba(255,255,255,0.7)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-tiny)', fontWeight: 600, cursor: 'pointer', color: 'var(--neutral-800)', textAlign: 'center' }}>
              {saving && via === 'profile' ? 'Saving...' : 'Profile only'}
            </button>
            <button onClick={e => { e.stopPropagation(); setEditing(false); setSaveError(''); setText(thought || ''); }}
              style={{ flex: 1, padding: '10px 8px', background: 'rgba(255,255,255,0.5)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-tiny)', cursor: 'pointer', color: 'var(--neutral-400)', textAlign: 'center' }}>
              Cancel
            </button>
          </div>
          {thought && (
            <button onClick={e => { e.stopPropagation(); handleDelete(); }} disabled={saving}
              style={{ display: 'block', margin: '10px auto 0', padding: '4px 8px', background: 'none', border: 'none', fontSize: 'var(--font-size-tiny)', fontWeight: 600, cursor: saving ? 'default' : 'pointer', color: 'var(--feedback-error)', textDecoration: 'underline' }}>
              Remove
            </button>
          )}
          <SaveError message={saveError} />
        </>
      ) : (
        thought ? (
          <>
            <p style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 'var(--font-size-md)', color: 'var(--neutral-800)', lineHeight: 1.6, margin: '0 0 10px' }}>"{thought}"</p>
            <p style={{ fontSize: 'var(--font-size-xxs)', color: '#b45309', margin: 'var(--space-none)', opacity: 0.7 }}>
              {profile?.thought_expires_at ? (() => {
                const posted = new Date(profile.thought_expires_at);
                posted.setHours(posted.getHours() - 24);
                return posted.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
              })() : ''}
            </p>
          </>
        ) : readOnly ? <VisitorEmpty /> : <p style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 'var(--font-size-base)', color: 'var(--feedback-warning)', opacity: 0.6, margin: 'var(--space-none)' }}>What's on your reading mind? Click to share...</p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 📖 CURRENTLY READING
// ═══════════════════════════════════════════════════════════════
// ─────────────────────────────────────────────────────────────────────────────
// Shared helper — used by CurrentlyReadingBlock & RecommendMeBlock
// Ensures external books (non-Reanlo) land in the catalog as pending
// Returns the resolved book_id (existing or newly inserted), or null
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Debounce hook
// ─────────────────────────────────────────────────────────────────────────────
function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared catalog helper
// ─────────────────────────────────────────────────────────────────────────────
async function ensureBookInCatalog(book, entry_point = null) {
  if (book.source === 'reanlo' || book.id) return book.id || null;

  // A missing cover does NOT stop the book being catalogued.
  //
  // This used to `return null` on a cover-less book with only a console.warn,
  // and the cost was much bigger than "no cover recorded". Every caller does
  // `resolvedId || selectedBook.id || null`, so the profile entry still saved
  // with book_id: null, and insertReaderTropes is gated on a truthy id -- so
  // the reader's tropes were dropped too, and the book never entered the
  // catalogue at all. Three things lost, nothing said, on a book the reader
  // had just deliberately chosen.
  //
  // A cover-less book belongs in the catalogue as an incomplete record, not
  // nowhere: findOrCreateBook inserts at status 'pending', and the publish
  // gate (book_meets_publish_requirements) requires a cover, so it correctly
  // stays unpublished until somebody adds one. That is "awaiting a cover",
  // which is a state, rather than a silent deletion. It keeps the row
  // reachable so the cover can be filled in later --
  // fill_book_details_if_null (20260904000000) is what makes that possible.
  const user = await getCurrentUser();
  if (!user) return null;

  const bookId = await findOrCreateBook(book, {
    submission_type: 'reader',
    extra: { entry_point: entry_point || null },
  });

  if (bookId) {
    supabase.from('book_submissions').insert({
      submitted_by: user.id,
      title:        book.title,
      author_name:  book.author_name,
      cover_url:    book.cover_image_url || null,
      status:       'pending',
    }).then(({ error: e }) => { if (e) console.error('Submission record error:', e.message); });
  }

  return bookId;
}

// ── Insert reader-selected tropes into book_tags ─────────────────────────────
//
// These blocks never ask a reader which of the eight buckets a tag belongs to,
// and they should not: someone naming what they love is not filing paperwork.
// So the code decided, and it decided with two hardcoded lists — nineteen genre
// names, thirteen mood names, everything else a trope. "graphic violence" was a
// trope. "sapphic" was a trope. "Nigeria" was a trope.
//
// resolveTagWithBucket asks the matcher instead and takes the bucket off the tag
// it lands on, which an author already filled in. See its comment in
// src/lib/tropeUtils.js for what it can and cannot do.
//
// This is for the TROPE BOX ONLY. The journal's genre pick does NOT go
// through here -- it routes by id straight into book_genres/book_subgenres
// (writeReaderGenrePicks, PR5) because the bucket is already known, which
// is the wrong question for resolveTagWithBucket to be answering: it
// answers "what bucket is this unlabelled name for", not "where does this
// already-labelled pick go".
async function insertReaderTropes(bookId, allTags = []) {
  if (!bookId || !allTags.length) return;
  for (const name of allTags) {
    if (!name?.trim()) continue;
    try {
      // resolveTagWithBucket asks process-trope first and falls back to a
      // direct lookup of ACTIVE canonicals and aliases. Neither branch
      // creates a row. It returns the bucket as well as the id.
      //
      // What was here before was a blind create: look the name up, and if it
      // was missing, upsert it as is_active false and then point the book at
      // it. Nothing ever compared it to what already existed, so a reader
      // writing "HEA" got a second row beside "Happily Ever After" — and the
      // book_tags row pointed at the inactive one, which is not in discovery.
      // The book looked correctly tagged and could not be found. Same bug the
      // author portal had, same fix.
      const { trope_id, tagType: tag_type } = await resolveTagWithBucket(name.trim());

      // No id means the matcher did not answer and the name is not already in
      // the catalogue. Skipping is deliberate: the alternative is minting the
      // unmatched duplicate this function exists to stop. Silent here is
      // correct for a trope: it is optional (canSubmit does not require one),
      // and a typed name going to moderation instead of tagging immediately
      // is a normal outcome, not a failure the reader needs told about.
      if (!trope_id) {
        console.warn(`insertReaderTropes: no trope for "${name.trim()}" — not tagged, and no row created.`);
        continue;
      }

      // added_by is what makes this tag removable by the reader who added it.
      // Read once outside the loop below would be nicer, but this function is
      // called with a handful of names and getCurrentUser() is cached; the
      // important thing is that it is the SAME helper every reader-side write
      // uses, so no one screen ends up as the odd one out that forgets it.
      const { error: upsertError } = await supabase.from('book_tags').upsert(
        { book_id: bookId, trope_id, tag_type, source: 'reader', is_custom: false, ...(await readerTagCredit()) },
        { onConflict: 'book_id,trope_id', ignoreDuplicates: true }
      );
      if (upsertError) {
        console.error('insertReaderTropes upsert error for', name, upsertError.message);
      }
    } catch (err) {
      console.error('insertReaderTropes error for', name, err.message);
    }
  }
}

// ── Insert the reader's journal genre pick into book_tags ────────────────────
//
// NOT routed through insertReaderTropes()/resolveTagWithBucket() above, even
// though it writes the same table. Caught in review, correcting an earlier
// instruction to route it through there:
//
// resolveTagWithBucket exists to answer "which of the eight buckets is this
// unlabelled name for", by reading `category` off whatever row the matcher
// lands on -- and it falls back to its 'trope' default in four separate
// cases, including the one that matters here: a name new to the catalogue.
// On a fuzzy near-match, process-trope creates a new pending row carrying
// PR5 (docs/genre-subgenre-build-spec.md): insertReaderGenre used to live
// here, writing a reader's genre/subgenre picks into book_tags (tag_type:
// 'genre') alongside writeReaderGenrePicks's book_genres/book_subgenres
// write below. Removed, not merely stopped from being called: book_tags is
// no longer a genre store for any reader-facing surface, and
// writeReaderGenrePicks is now the ONLY place a reader's genre/subgenre
// pick lands. genreNames/subgenreNames are still computed at both call
// sites below (genreFilterNames) for other UI purposes and are simply
// unread by this function now -- see its own comment for how a required
// genre's success/failure is reported without them.

// ── Every tag write a Recommend Me / To Know Me save makes ──────────────────
//
// Two writes, independent of each other, run SIDE BY SIDE -- that was most
// of why "Add Recommendation" sat on "Saving..." for so long.
//
//   1. the typed tropes and moods -> insertReaderTropes, bucket INFERRED,
//      because nobody knows what kind of thing a typed name is
//   2. the genre/subgenre picks, by id -> book_genres/book_subgenres
//      (writeReaderGenrePicks). Bucket is never in question here -- the
//      reader picked these from the genre picker, not typed them.
//
// Returns genreTagFailed: a required genre that did not land. genreIds is
// only non-empty when the reader actually picked a genre (handleConfirmAdd/
// confirmAdd both block submission on genreNeeded && selectedGenreIds.length
// === 0), so a non-empty genreIds with a book_genres-specific error is
// treated as the genre write failing. Checked by table name, not just
// truthiness of the combined error string: writeReaderGenrePicks can fail on
// book_subgenres alone, and a subgenre never blocks (docs/genre-subgenre-
// build-spec.md) -- a subgenre-only failure must not raise the same alert a
// failed genre does.
async function writeReaderPicksToBook(bookId, { tropeNames, genreIds, subgenreIds, logLabel }) {
  const [, { error: genreTablesErr }] = await Promise.all([
    insertReaderTropes(bookId, tropeNames),
    writeReaderGenrePicks(bookId, genreIds, subgenreIds),
  ]);
  // Secondary to the entry itself, so it never blocks the save, but not
  // thrown away either -- see writeReaderGenrePicks's own comment.
  if (genreTablesErr) console.error(`${logLabel}: book_genres/book_subgenres write failed:`, genreTablesErr);

  const genreTagFailed = genreIds.length > 0
    && !!genreTablesErr
    && genreTablesErr.includes('book_genres:');

  return { genreTagFailed };
}

// ── Shared journal-entry save path (ticket 27) ──────────────────────────────
//
// Factored out of JournalBlock's own handleDone below so a second entry
// point -- ReaderHomepage's craving dead-end offer, ticket 27 bullet 2 --
// writes a reflection through the exact same book/tropes/genre resolution
// and the exact same reflections insert, rather than a second copy of this
// that quietly drifts from it over time. handleDone still owns everything
// isEdit-specific (the update path, its own local state) -- these two only
// ever handle a NEW entry, and neither touches React state itself, so
// either caller can use them with its own local UI (JournalBlock's
// reflections/books/toast state, or ReaderHomepage's own).
//
// resolveJournalEntryFacts does everything a save needs BEFORE the actual
// reflections row is written: catalog the book (or confirm it's already
// catalogued -- ensureBookInCatalog short-circuits on an existing book.id,
// which is what a craving's already-live closest match always has), the
// same for every comp title, then the tropes and genre/subgenre writes.
// Returns null only when the book itself couldn't be resolved -- the one
// failure serious enough to stop the entry from saving at all; tropes and
// genre failures are reported back via failedWrites instead; see
// writeReaderPicksToBook's own comment for why neither of those blocks.
//
// An entry the reader described in their own words (payload.entryTitle, ticket 94)
// has no book: nothing is catalogued for it, and there is no book to file tropes or a
// genre against, so resolvedId comes back null. Its comp titles are real books and
// are resolved below as for any entry.
export async function resolveJournalEntryFacts(payload) {
  const described = payload.entryTitle != null;
  const resolvedId = described ? null : await ensureBookInCatalog(payload.book, 'journal');
  if (!described && !resolvedId) return null;

  const failedWrites = [];

  const resolvedComps = await Promise.all((payload.comps || []).map(async c => {
    const book_id = await ensureBookInCatalog(c.book, 'journal');
    if (!book_id) return null;
    return { book_id, title: c.book.title, author_name: c.book.author_name, cover_image_url: c.book.cover_image_url, bridge: (c.bridge || '').trim() || null };
  }));
  const comp_titles = resolvedComps.filter(Boolean);

  // Tropes only -- resolveTagWithBucket (inside insertReaderTropes) is
  // right for these because nobody knows what kind of thing a typed
  // trope name is.
  if (!described && (payload.tropes || []).length) {
    try {
      await insertReaderTropes(resolvedId, payload.tropes);
    } catch (err) {
      console.error('Journal trope tagging failed:', err.message);
      failedWrites.push('tropes');
    }
  }

  // PR5 (docs/genre-subgenre-build-spec.md): genre and subgenre used to
  // route through insertReaderGenre into book_tags, separately from this
  // mirror into book_genres/book_subgenres (PR3 Finding 3) -- now this is
  // the ONLY place either one lands, same change as RecommendMeBlock/
  // ToKnowMeBlock's writeReaderPicksToBook. 'genre' is pushed only for a
  // book_genres-specific failure on a REQUIRED pick (payload.genreId set);
  // a subgenre never blocks (docs/genre-subgenre-build-spec.md), so a
  // book_subgenres-only failure still gets a note but not the same
  // "required field didn't save" wording.
  if (!described && (payload.genreId || (payload.subgenreIds || []).length)) {
    const { error: genreTablesErr } = await writeReaderGenrePicks(resolvedId, payload.genreId ? [payload.genreId] : [], payload.subgenreIds || []);
    if (genreTablesErr) {
      console.error('Journal book_genres/book_subgenres write failed:', genreTablesErr);
      if (payload.genreId && genreTablesErr.includes('book_genres:')) {
        failedWrites.push('genre');
      } else {
        failedWrites.push('genre filters');
      }
    }
  }

  return { resolvedId, comp_titles, failedWrites };
}

// The actual reflections insert -- NEW entries only (isEdit's update lives
// in handleDone itself, which never had this much to share with a second
// caller: an edit only ever happens from JournalBlock's own deck, never
// from ReaderHomepage). userId is passed in rather than re-resolved here
// because both callers already have it from their own getCurrentUser()
// check, done before resolveJournalEntryFacts even runs.
export async function insertNewJournalReflection(userId, payload, resolvedId, comp_titles) {
  // An entry described in the reader's own words has a title and no book, and is always
  // private (the database refuses it shared, reflections_bookless_is_private).
  const described = payload.entryTitle != null;
  const row = {
    reader_id:       userId,
    book_id:         resolvedId,
    entry_title:     described ? cleanEntryTitle(payload.entryTitle) : null,
    prompt_id:       payload.prompt.id,
    prompt_kind:     payload.prompt.kind,
    prompt_label:    payload.promptLabel,
    prompt_emoji:    payload.prompt.emoji,
    prompt_color:    payload.prompt.color,
    reflection_text: payload.reflection || null,
    comp_titles,
    visibility:      !described && payload.shared ? 'shared' : 'private',
    audio_url:        payload.voiceNote?.objectPath || null,
    transcript:       payload.voiceNote?.transcript || null,
    duration_seconds: payload.voiceNote?.durationSeconds != null ? Math.round(payload.voiceNote.durationSeconds) : null,
  };

  const { data: inserted, error } = await supabase.from('reflections').insert(row).select().single();
  if (error) {
    console.error('Journal entry save failed:', error.message);
    // No orphans (9.3) still holds, just not from here: the entry stays
    // open with the recording attached so the reader can retry, and
    // JournalEntryFlow deletes it if they close without saving. Deleting
    // it here made the retry save a row pointing at a deleted recording.
    // Both callers (JournalBlock.handleDone, and ReaderHomepage's dead-end
    // journal) rely on this.
    return { error: 'insert_failed' };
  }

  return { inserted };
}

/**
 * The tags on one book, with an ✕ on the ones this reader added.
 *
 * WHY IT RE-READS THE BOOK'S TAGS INSTEAD OF USING WHAT THE CARD ALREADY HAS.
 *
 * The recommendation card carries `rec.tropes`, an array of canonical NAMES
 * built when the block loaded. A name is not enough to delete a row — that
 * needs the book_tags id — and the card's copy is a snapshot that says nothing
 * about who added what. Reading fresh also means the reader is deciding
 * against the book's real current state rather than against whatever was true
 * when the page loaded, which matters on a row somebody else may have removed.
 *
 * WHY EVERY TAG IS LISTED, NOT ONLY THE REMOVABLE ONES. Showing a reader only
 * their own tags would read as "this book has two tags" when it has nine, and
 * the point of the panel is the book. The ones they cannot remove are shown
 * plainly with the reason, rather than with a dead ✕ that does nothing.
 */
function BookTagPanel({ bookId, title, onClose }) {
  const [rows,    setRows]    = useState(null);   // null = still loading
  const [me,      setMe]      = useState(null);
  const [problem, setProblem] = useState('');
  const [busyId,  setBusyId]  = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = await getCurrentUser();
      const { data, error } = await supabase
        .from('book_tags')
        .select('id, tag_type, added_by, trope:tropes(canonical_name), custom_name')
        .eq('book_id', bookId)
        .order('id');
      if (cancelled) return;
      setMe(user?.id ?? null);
      // An empty list and a failed read are different things and must not look
      // the same: "this book has no tags" is an answer, "we could not ask" is
      // a fault, and only one of them means stop trying.
      if (error) { setProblem(`Could not load this book's tags, ${error.message}`); setRows([]); return; }
      setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [bookId]);

  async function remove(row) {
    setBusyId(row.id);
    setProblem('');
    const result = await removeTagIAdded(row.id);
    setBusyId(null);
    if (!result.ok) { setProblem(result.reason); return; }
    setRows(prev => prev.filter(r => r.id !== row.id));
  }

  const mine = (rows || []).filter(r => canRemove(r, me)).length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 460, width: '100%', padding: 'var(--space-xxl)', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-sm)', marginBottom: 'var(--space-6)' }}>
          <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: 0 }}>Tags on {title}</p>
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-md)', cursor: 'pointer', color: 'var(--neutral-400)', lineHeight: 1 }}>✕</button>
        </div>

        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 var(--space-md)' }}>
          You can remove tags you added. The rest stay, a tag is a fact about the book, not yours to take off.
        </p>

        {problem && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-error)', background: 'var(--feedback-error-bg)', padding: 'var(--space-6) var(--space-10)', borderRadius: 'var(--radius-md)', margin: '0 0 var(--space-10)' }}>{problem}</p>
        )}

        {rows === null && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 0 }}>Loading…</p>}

        {rows !== null && rows.length === 0 && !problem && (
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 0 }}>This book has no tags yet.</p>
        )}

        {rows !== null && rows.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
            {rows.map(row => {
              const name    = row.trope?.canonical_name || row.custom_name || 'unnamed tag';
              const canGo   = canRemove(row, me);
              const working = busyId === row.id;
              return (
                <span key={row.id}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '4px 10px',
                    background: canGo ? 'var(--brand-rust)' : 'var(--brand-sand)',
                    color: canGo ? 'var(--brand-white)' : 'var(--neutral-600)',
                    borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xs)', fontWeight: 600, opacity: working ? 0.5 : 1 }}>
                  {name}
                  {canGo && (
                    <button onClick={() => remove(row)} disabled={working}
                      aria-label={`Remove the tag ${name}`}
                      style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: working ? 'default' : 'pointer', fontSize: 'inherit', lineHeight: 1 }}>✕</button>
                  )}
                </span>
              );
            })}
          </div>
        )}

        {rows !== null && rows.length > 0 && mine === 0 && !problem && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: 'var(--space-10) 0 0' }}>
            None of these are recorded as yours. Tags added before Reanlo started recording who added them cannot be removed by anyone but an admin.
          </p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared search helpers
// ─────────────────────────────────────────────────────────────────────────────
async function searchPlatformBooks(q) {
  const safe = q.replace(/[^\w\s]/g, c => '%_()\'"`'.includes(c) ? '' : c).trim();
  if (!safe) return [];
  // book_tags(tag_type) is what "If You Like What I Like" and "To Know Me"
  // read to decide genreNeeded -- "does this book already have a genre from
  // anyone". Without it every platform result arrived with no book_tags, so
  // genreNeeded was true for EVERY book, and a reader was asked for a genre
  // on The Fifth Season, which already had three. tag_type only, on purpose:
  // those blocks' autoTropes also reads bt.tropes?.canonical_name, and
  // leaving the name out keeps that pre-fill empty exactly as it has always
  // been here, rather than changing what a reader has to add to reach the
  // 3-tropes-and-tags floor as a side effect of this fix.
  const { data, error } = await supabase
    .from('books')
    .select('id, title, author_name, cover_image_url, publication_date, publication_year, book_tags(tag_type), book_genres(genre_id)')
    .or(`title.ilike.%${safe}%,author_name.ilike.%${safe}%`)
    .eq('status', 'live')
    .order('publication_date', { ascending: false, nullsFirst: false }) // ← was published_date
    .limit(10);
  if (error) return [];
  return (data || []).map(b => ({ ...b, source: 'reanlo' }));
}

// Returns a discriminated result, not a bare array. "Asked and got nothing"
// and "never successfully asked" are different facts and the caller has to be
// able to tell them apart — collapsing both to [] is what let a timeout render
// as "No results found".
async function searchExternalBooks(q) {
  if (!q.trim()) return { ok: true, results: [] };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetchWithTimeout(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/book-search`,
      {
        method: 'POST',
        headers: bookSearchHeaders(session, SUPABASE_PUBLISHABLE_KEY),
        body: JSON.stringify({ query: q }),
      },
      SEARCH_BOX_TIMEOUT_MS
    );
    if (!res.ok) return { ok: false, results: [], reason: `http_${res.status}` };
    const data = await res.json();
    // A 200 is not proof anything was reached — see outsideSourcesFailed.
    return {
      ok: !outsideSourcesFailed(data),
      results: (data.results || []).map(b => ({ ...b, source: b.source || 'openlibrary' })),
      reason: outsideSourcesFailed(data) ? 'upstream_unreachable' : undefined,
    };
  } catch (err) {
    // An AbortError here is the deadline, not a cancelled search, so it is
    // worth saying out loud: it is the signal that the outside source is down.
    console.warn(
      err.name === 'AbortError'
        ? 'Book search timed out; showing Reanlo results only'
        : `Book search edge function unavailable: ${err.message}`
    );
    return { ok: false, results: [], reason: err.name === 'AbortError' ? 'timeout' : 'unreachable' };
  }
}

// `setOutsideFailed` tells the caller whether the outside source answered at
// all, so an empty list can be rendered as the right sentence. Optional, so
// callers that genuinely do not show an empty state can omit it.
async function searchBooks(q, setResults, setSearching, setOutsideFailed = () => {}) {
  if (!q.trim()) { setResults([]); setOutsideFailed(false); return; }
  setSearching(true);
  try {
    const [platformRes, externalRes] = await Promise.allSettled([
      searchPlatformBooks(q),
      searchExternalBooks(q),
    ]);
    const platform = platformRes.status === 'fulfilled' ? platformRes.value : [];
    const ext      = externalRes.status  === 'fulfilled'
      ? externalRes.value
      : { ok: false, results: [], reason: 'threw' };
    const external = ext.results;
    setOutsideFailed(!ext.ok);

    // Deduplicate external against platform results
    const seen = new Set(
      platform.map(b => `${b.title?.toLowerCase()}||${b.author_name?.toLowerCase()}`)
    );
    const combined = [
      ...platform,
      ...external.filter(b =>
        !seen.has(`${b.title?.toLowerCase()}||${b.author_name?.toLowerCase()}`)
      ),
    ];

    // Relevance first, then Reanlo books, then newest. Sorting on year alone
    // buried anything without a publication_date and let an unrelated reprint
    // outrank the book the reader actually typed.
    setResults(orderSearchResults(combined, q).slice(0, 15));
  } catch {
    // Reaching here means the whole search fell over, not that the catalogue
    // is empty. Say so rather than letting it read as "no such book".
    setResults([]);
    setOutsideFailed(true);
  } finally {
    setSearching(false);
  }
}


// ═══════════════════════════════════════════════════════════════════════════
// 📖 CURRENTLY READING BLOCK
// ═══════════════════════════════════════════════════════════════════════════
// Up to MAX_CURRENTLY_READING books, one on screen at a time, with arrows
// between them and a finger swipe (useSwipe), the same way the Hot Take
// block works. Read and saved only through src/lib/currentlyReading.js,
// which knows the old one-book shape of the column as well as the list.
//
// + Add puts a new book at the end of the list. The pencil swaps the book on
// screen for another, which is what it always did when there was one book.
// The X removes the book on screen, with Undo.
//
// Every book is rendered in the same grid cell and only the current one is
// visible, so the block is always as tall as its tallest book and does not
// resize under the reader's finger as they move between them.

// How long Undo is offered after a book is removed. The same as
// HOT_TAKE_UNDO_MS, so the two blocks behave alike.
const CURRENTLY_READING_UNDO_MS = 6000;
// The empty float in the header that the title wraps around: the pencil's
// corner, so no word of the title runs under it.
// Its width is TITLE_CLEAR_OF_EDIT: this block is padded 16 like most.
const CURRENTLY_READING_CLEAR_OF_EDIT = TITLE_CLEAR_OF_EDIT;
// Its height: EDIT_BTN's bottom edge is 36px from the block's top (top 8 +
// 28), and the block's top padding is 14, so the header's first 22px sit
// beside it.
const CURRENTLY_READING_PENCIL_FLOAT_H = 22;

const currentlyReadingArrow = disabled => ({
  width: 30, height: 30, borderRadius: 'var(--radius-circle)', flexShrink: 0,
  border: '1.5px solid var(--brand-sand)', background: 'var(--brand-paper)',
  color: disabled ? 'var(--neutral-300)' : 'var(--neutral-700)',
  cursor: disabled ? 'default' : 'pointer', fontSize: 15, lineHeight: 1, padding: 0,
});

export function CurrentlyReadingBlock({ profile, onUpdate, readOnly = false }) {
  // books is what is on screen. It follows the profile, except while a save
  // is in flight, when it holds the reader's new list until the save answers.
  const [books,           setBooks]           = useState(() => currentlyReadingOf(profile));
  const [index,           setIndex]           = useState(0);
  const [saving,          setSaving]          = useState(false);
  // null when the search is closed. 'new' when it is adding a book, or the
  // slide number of the book it will replace.
  const [searchFor,       setSearchFor]       = useState(null);
  const [searchQuery,     setSearchQuery]     = useState('');
  const [searchResults,   setSearchResults]   = useState([]);
  const [searching,       setSearching]       = useState(false);
  const [outsideFailed,   setOutsideFailed]   = useState(false);
  const [searchError,     setSearchError]     = useState('');
  // The book picked in the search, waiting on "Share update" or "Keep
  // private". pendingAt is where it goes, the same values as searchFor.
  const [pendingBook,     setPendingBook]     = useState(null);
  const [pendingAt,       setPendingAt]       = useState(null);
  // Set once the picked book has saved. A retry after a failed share then
  // only posts the share, and does not add the book a second time.
  const [pendingEntry,    setPendingEntry]    = useState(null);
  const [confirming,      setConfirming]      = useState(false);
  // null, or { at, text }: the slide whose reaction is being written.
  const [reaction,        setReaction]        = useState(null);
  const [saveError,       setSaveError]       = useState('');
  // The last book removed, for Undo: { books, index } from before, and
  // after, the list it left. Cleared on a timer, and by anything that can
  // change the list after it (dropUndo).
  const [undo,            setUndo]            = useState(null);
  const undoTimer        = useRef(null);
  const reactionSaving   = useRef(false);
  // reactionSaving for the render: while a reaction saves, x and + Add stay
  // tappable, so a tap on them can wait for it (whenReactionSaved).
  const [reactionBusy,    setReactionBusy]    = useState(false);
  // null, or a tap on x, the pencil or + Add that landed while a reaction
  // was saving: { kind: 'remove', at, book } or { kind: 'search', at }.
  const [afterReaction,   setAfterReaction]   = useState(null);

  useEffect(() => {
    if (saving) return;
    const next = currentlyReadingOf(profile);
    setBooks(next);
    setIndex(i => Math.min(i, Math.max(next.length - 1, 0)));
  }, [profile, saving]);

  useEffect(() => () => clearTimeout(undoTimer.current), []);

  // The tap that blurs the reaction box is what starts its save, so a tap on
  // x, the pencil or + Add used to land on a block that was busy saving, and
  // did nothing. It waits instead, and runs once the reaction has saved and
  // its box has closed. A failed save drops it (saveReaction), and the box
  // stays open with what the reader typed.
  useEffect(() => {
    if (!afterReaction || saving || reaction) return;
    const tap = afterReaction;
    setAfterReaction(null);
    if (tap.kind === 'search') openSearch(tap.at);
    else if (isSameBook(books[tap.at], tap.book)) removeAt(tap.at);
  }, [afterReaction, saving, reaction]);

  const count  = books.length;
  const canAdd = !readOnly && count < MAX_CURRENTLY_READING;
  // A finger swipe does what the arrows do. Off while a reaction is being
  // written, so a finger selecting text never moves the book away.
  const swipe = useSwipe({
    onNext: () => setIndex(i => Math.min(count - 1, i + 1)),
    onPrev: () => setIndex(i => Math.max(0, i - 1)),
    enabled: count > 1 && !reaction,
  });
  const showSearch = searchFor !== null;
  const showSharePrompt = !!pendingBook;

  // ── Debounced search ──────────────────────────────────────────────────────
  const debouncedQuery = useDebounce(searchQuery, 400);
  const searchIdRef    = useRef(0);

  useEffect(() => {
    if (!showSearch) return;
    const myId = ++searchIdRef.current;

    searchBooks(
      debouncedQuery,
      (results) => { if (searchIdRef.current === myId) setSearchResults(results); },
      (val)     => { if (searchIdRef.current === myId) setSearching(val); },
      (failed)  => { if (searchIdRef.current === myId) setOutsideFailed(failed); }
    );

    return () => { searchIdRef.current++; };
  }, [debouncedQuery, showSearch]);

  // Saves the whole list. Answers true or false; on false the screen is put
  // back to what was there and the reason is shown.
  async function saveList(next, { nextIndex = index, rollbackTo = books, rollbackIndex = index } = {}) {
    setSaving(true);
    setSaveError('');
    setBooks(next);
    // Never below 0: removing the last book asks for slide -1.
    setIndex(Math.max(0, Math.min(nextIndex, next.length - 1)));
    const ok = await saved(onUpdate(currentlyReadingPatch(next)));
    if (!ok) {
      setBooks(rollbackTo);
      setIndex(rollbackIndex);
      setSaveError(SAVE_FAILED);
    }
    setSaving(false);
    return ok;
  }

  // Undo is only for the book just removed. Once a reader adds, swaps or
  // writes a reaction, the offer goes, or Undo would save the list from
  // before over what they have saved since.
  function dropUndo() {
    clearTimeout(undoTimer.current);
    setUndo(null);
  }

  // While a reaction is saving, keeps the tap for afterwards and answers
  // true. Otherwise answers false and the caller acts at once.
  function whenReactionSaved(tap) {
    if (!reactionSaving.current) return false;
    setAfterReaction(tap);
    return true;
  }

  function openSearch(at) {
    if (readOnly || saving) return;
    dropUndo();
    setSaveError('');
    setSearchError('');
    setSearchQuery('');
    setSearchResults([]);
    setSearchFor(at);
  }

  function closeSearch() {
    setSearchFor(null);
    setSearchQuery('');
    setSearchResults([]);
    setSearchError('');
  }

  function handleSelectBook(book) {
    // A book can be on the list once. When swapping, the book being swapped
    // out does not count: picking it again keeps its reaction.
    const others = books.filter((_, i) => i !== searchFor);
    if (others.some(b => isSameBook(b, book))) {
      setSearchError(`${book.title} is already in your Currently Reading.`);
      return;
    }
    setPendingBook(book);
    setPendingAt(searchFor);
    setPendingEntry(null);
    setSaveError('');
    closeSearch();
  }

  function closePrompt() {
    setPendingBook(null);
    setPendingAt(null);
    setPendingEntry(null);
    setSaveError('');
  }

  async function confirmBook(shareToFeed) {
    // One tap, one save: every button is disabled while this runs, so a
    // double tap does not post the update twice (audit 2.4).
    if (confirming || !pendingBook) return;
    setConfirming(true);
    setSaveError('');

    let entry = pendingEntry;
    if (!entry && pendingAt === 'new' && books.length >= MAX_CURRENTLY_READING) {
      // + Add is hidden at the limit, but the list can fill up from another
      // tab while this prompt is open.
      setConfirming(false);
      setSaveError(`You can have up to ${MAX_CURRENTLY_READING} books in Currently Reading.`);
      return;
    }
    if (!entry) {
      const resolvedId = await ensureBookInCatalog(pendingBook, 'currently-reading');
      const bookId = resolvedId || pendingBook.id || null;
      const old = pendingAt === 'new' ? null : books[pendingAt];
      // A reaction belongs to the book it was written about, so a different
      // book starts with none (audit 2.1). Swapping a book for itself keeps it.
      const sameBook = !!old && ((bookId && old.book_id === bookId) || isSameBook(old, pendingBook));
      entry = {
        book_id:   bookId,
        title:     pendingBook.title,
        author:    pendingBook.author_name,
        cover_url: pendingBook.cover_image_url,
        reaction:  sameBook ? (old.reaction || '') : '',
        source:    pendingBook.source || 'external',
      };
      const next = pendingAt === 'new'
        ? [...books, entry]
        : books.map((b, i) => (i === pendingAt ? entry : b));
      const nextIndex = pendingAt === 'new' ? books.length : pendingAt;
      // Nothing is shared for a book that did not save. The prompt stays up
      // so the reader can try again.
      if (!(await saveList(next, { nextIndex }))) {
        setConfirming(false);
        return;
      }
      setPendingEntry(entry);
    }

    // The book is saved by now. A failed share keeps the prompt up so the
    // reader can try again or keep it private (audit 2.7).
    if (shareToFeed) {
      const user = await getCurrentUser();
      if (user) {
        const { error: shareError } = await supabase.from('shelf_activity_events').insert({
          user_id:        user.id,
          event_type:     'currently_reading',
          book_id:        entry.book_id || null,
          book_title:     entry.title,
          book_author:    entry.author,
          book_cover_url: entry.cover_url || null,
        });
        if (shareError) {
          reportSaveFailure('currently reading share', shareError);
          setConfirming(false);
          setSaveError("Your book is saved, but the update didn't post. Try again, or keep it private.");
          return;
        }
      }
    }

    setConfirming(false);
    closePrompt();
  }

  function startReaction(i) {
    if (readOnly || saving) return;
    dropUndo();
    setSaveError('');
    setReaction({ at: i, text: books[i]?.reaction || '' });
  }

  // Called by the input's blur only. Enter blurs the input rather than
  // saving itself, so Enter and the blur that follows it save once, not
  // twice (audit 2.5).
  async function saveReaction() {
    if (!reaction || reactionSaving.current) return;
    const { at, text } = reaction;
    const clean = text.trim();
    if (!books[at] || clean === (books[at].reaction || '').trim()) {
      setReaction(null);
      setSaveError('');
      return;
    }
    reactionSaving.current = true;
    setReactionBusy(true);
    const next = books.map((b, i) => (i === at ? { ...b, reaction: clean } : b));
    const ok = await saveList(next, { nextIndex: at });
    reactionSaving.current = false;
    setReactionBusy(false);
    // On a failure the box stays open with what the reader typed, and a tap
    // that was waiting on this save is dropped, not acted on.
    if (ok) setReaction(null);
    else setAfterReaction(null);
  }

  async function removeAt(i) {
    if (saving) return;
    const before = books;
    const beforeIndex = index;
    const next = books.filter((_, j) => j !== i);
    if (!(await saveList(next, { nextIndex: Math.min(i, next.length - 1) }))) return;
    setReaction(null);
    clearTimeout(undoTimer.current);
    setUndo({ books: before, index: beforeIndex, after: next });
    undoTimer.current = setTimeout(() => setUndo(null), CURRENTLY_READING_UNDO_MS);
  }

  async function undoRemove() {
    if (!undo || saving) return;
    const back = undo;
    dropUndo();
    // A second guard for what dropUndo cannot see, such as the profile
    // reloading with a change from elsewhere: only put the old list back over
    // the same list the removal left. Book by book, not as JSON text: the
    // list here is read back from the database, which reorders each entry's
    // keys (sameCurrentlyReading says why).
    if (!sameCurrentlyReading(books, back.after)) {
      setSaveError("Couldn't undo: your Currently Reading has changed since then.");
      return;
    }
    if (!(await saveList(back.books, { nextIndex: back.index }))) setUndo(back);
  }

  const stop = e => e.stopPropagation();
  const undoLine = undo && (
    <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-500)', margin: '10px 0 0', textAlign: count > 0 ? 'center' : 'left' }}>
      Book removed. <button onClick={undoRemove} disabled={saving} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--brand-forest)', fontWeight: 700, cursor: 'pointer', fontSize: 'inherit' }}>Undo</button>
    </p>
  );
  const adding = searchFor === 'new' || pendingAt === 'new';

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', overflow: 'hidden', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onClick={() => {
        const at = count === 0 ? 'new' : index;
        if (!whenReactionSaved({ kind: 'search', at })) openSearch(at);
      }} />}
      <div style={{ padding: '14px 16px' }}>
        {/* The title alone. + Add and "4 max" used to sit here too, and no
            desktop column is wide enough for all three: columns run
            132-276px (ReaderProfile.jsx, three columns beside the 260px
            palette), the emoji and title take about 149, and the pencil
            needs CURRENTLY_READING_CLEAR_OF_EDIT. The title broke onto two
            lines and "+ Add" onto two more. They live under the books now
            (see the end of this block).
            The empty float is the pencil's corner: the words wrap around it,
            so the title is one line in a column 239px or wider (a window
            about 1090px or wider), two lines below that, and below about
            185px "📖 CURRENTLY" drops under the pencil instead of running
            into it. The emoji and "Currently" share a no-wrap span, so the
            emoji never ends up on a line of its own. The emoji gets a fixed
            18px box plus a 6px gap, and the header indents every line but
            the first by those 24px, so "READING" lines up under
            "CURRENTLY" rather than under the emoji. */}
        <div style={{ display: 'flow-root', marginBottom: 'var(--space-md)', lineHeight: '18px', paddingLeft: 24, textIndent: -24, fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>
          {!readOnly && <span aria-hidden="true" style={{ float: 'right', width: CURRENTLY_READING_CLEAR_OF_EDIT, height: CURRENTLY_READING_PENCIL_FLOAT_H }} />}
          <span style={{ whiteSpace: 'nowrap' }}>
            <span style={{ display: 'inline-block', width: 18, marginRight: 'var(--space-6)', textIndent: 0, textAlign: 'center', fontSize: 'var(--font-size-md)', letterSpacing: 0 }}>📖</span>Currently
          </span>{' '}Reading
        </div>

        {count === 0 ? (
          <>
            {readOnly ? <VisitorEmpty /> : (
              <button onClick={() => openSearch('new')} disabled={saving}
                style={{ width: '100%', padding: 'var(--space-xl)', background: 'var(--brand-paper)', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', textAlign: 'center' }}>
                + Set what you're reading now
              </button>
            )}
            {undoLine}
            {!showSharePrompt && <SaveError message={saveError} />}
          </>
        ) : (
          <>
            <div {...swipe}>
              <div style={{ display: 'grid' }}>
                {books.map((cr, i) => (
                  <div key={i} aria-hidden={i !== index}
                    style={{ gridArea: '1 / 1', visibility: i === index ? 'visible' : 'hidden', position: 'relative', display: 'flex', gap: 'var(--space-md)', paddingRight: readOnly ? 0 : 28 }}>
                    <div style={{ width: 56, height: 80, borderRadius: '3px 8px 8px 3px', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0, boxShadow: '2px 2px 8px rgba(0,0,0,0.1)' }}>
                      {cr.cover_url
                        ? <img src={cr.cover_url} alt={cr.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xxl)' }}>📚</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px', fontFamily: 'var(--font-display)', overflowWrap: 'anywhere' }}>{cr.title}</p>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 8px' }}>by {cr.author}</p>
                      {reaction && reaction.at === i ? (
                        <input
                          value={reaction.text}
                          onChange={e => setReaction({ at: i, text: e.target.value })}
                          onBlur={saveReaction}
                          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                          autoFocus
                          placeholder="Page 247 and I'm not okay..."
                          style={{ width: '100%', border: '1px solid var(--brand-sand)', borderRadius: 6, padding: '5px 8px', fontSize: 'var(--font-size-sm)', outline: 'none', boxSizing: 'border-box' }}
                        />
                      ) : (
                        <p onClick={readOnly ? undefined : () => startReaction(i)}
                          style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', fontStyle: 'italic', cursor: readOnly ? 'default' : 'pointer', margin: 'var(--space-none)', overflowWrap: 'anywhere' }}>
                          {cr.reaction || (!readOnly && <span style={{ borderBottom: '1px dashed var(--neutral-300)' }}>Add a reaction...</span>)}
                        </p>
                      )}
                    </div>
                    {!readOnly && (
                      <button onClick={e => { stop(e); if (!whenReactionSaved({ kind: 'remove', at: i, book: cr })) removeAt(i); }}
                        disabled={(saving && !reactionBusy) || i !== index}
                        aria-label={`Remove ${cr.title} from Currently Reading`} title="Remove this book"
                        style={{ position: 'absolute', top: -2, right: 0, width: 24, height: 24, borderRadius: 'var(--radius-circle)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: 'var(--neutral-400)', fontSize: 13, lineHeight: 1, padding: 0, cursor: 'pointer' }}>
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {count > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-10)', marginTop: 'var(--space-md)' }}>
                  <button onClick={() => setIndex(i => Math.max(0, i - 1))} disabled={index === 0}
                    aria-label="Previous book" style={currentlyReadingArrow(index === 0)}>‹</button>
                  <div style={{ display: 'flex', gap: 6 }} aria-label={`Book ${index + 1} of ${count}`}>
                    {books.map((_, i) => (
                      <button key={i} onClick={() => setIndex(i)} aria-label={`Show book ${i + 1}`}
                        style={{ width: 7, height: 7, borderRadius: 'var(--radius-circle)', border: 'none', padding: 0, cursor: 'pointer', background: i === index ? 'var(--brand-forest)' : 'var(--brand-sand)' }} />
                    ))}
                  </div>
                  <button onClick={() => setIndex(i => Math.min(count - 1, i + 1))} disabled={index === count - 1}
                    aria-label="Next book" style={currentlyReadingArrow(index === count - 1)}>›</button>
                </div>
              )}
            </div>

            {/* Outside the swipe area, so a swipe that starts here can't
                also press it. */}
            {canAdd && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: count > 1 ? 'var(--space-sm)' : 'var(--space-md)' }}>
                <button onClick={() => { if (!whenReactionSaved({ kind: 'search', at: 'new' })) openSearch('new'); }}
                  disabled={saving && !reactionBusy}
                  style={{ background: 'transparent', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-xxl)', padding: '4px 12px', fontSize: 'var(--font-size-tiny)', fontWeight: 600, color: 'var(--neutral-500)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  + Add another book
                </button>
              </div>
            )}
            {!readOnly && count >= MAX_CURRENTLY_READING && (
              <p style={{ textAlign: 'center', margin: 'var(--space-sm) 0 0', fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)' }}>
                {MAX_CURRENTLY_READING} books max
              </p>
            )}

            {undoLine}
            {!showSharePrompt && <SaveError message={saveError} />}
          </>
        )}
      </div>

      {/* ── Search Modal — matches ToKnowMeBlock style ── */}
      {showSearch && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}
          onClick={closeSearch}
        >
          <div
            style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 520, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--neutral-100)' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: '0 0 4px' }}>
                {adding ? '📖 What are you reading?' : '📖 Swap this book'}
              </h3>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 14px' }}>
                {!adding
                  ? `Search for the book to put in place of ${books[searchFor]?.title || 'this one'}`
                  : count > 0
                    ? `Search for another book you're reading. Up to ${MAX_CURRENTLY_READING} at once.`
                    : "Search for the book you're currently reading"}
              </p>
              <input
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setSearchError(''); }}
                placeholder="Search by title or author..."
                autoFocus
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-xs)' }}
              />
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Searches Reanlo + Open Library</p>
              <SaveError message={searchError} />
            </div>

            {/* Results */}
            <div style={{ overflowY: 'auto', padding: '10px 20px', flex: 1, scrollbarWidth: 'none' }}>
              {searching && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '12px 0' }}>
                  <div style={{ width: 14, height: 14, border: '2px solid var(--brand-sand)', borderTopColor: 'var(--feedback-warning)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.8s linear infinite' }} />
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Searching...</p>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
                {searchResults.map((book, idx) => (
                  <button key={book.id || idx} onClick={() => handleSelectBook(book)}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '10px 12px', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', cursor: 'pointer', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <div style={{ width: 32, height: 46, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0 }}>
                      {book.cover_image_url
                        ? <img src={book.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)' }}>📚</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.title}</p>
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {book.author_name}{publicationYear(book) ? ` · ${publicationYear(book)}` : ''}</p>
                    </div>
                    <SourceBadge source={book.source || 'openlibrary'} />
                  </button>
                ))}
                {!searching && searchQuery.trim() && searchResults.length === 0 && (
                  <p style={{ fontSize: 'var(--font-size-sm)', color: outsideFailed ? '#b85c38' : 'var(--neutral-400)', textAlign: 'center', padding: '16px 0' }}>
                    {outsideFailed ? SEARCH_FAILED_MESSAGE : 'No results found'}
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--neutral-100)' }}>
              <button
                onClick={closeSearch}
                style={{ width: '100%', padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Share Prompt ── */}
      {/* A click on the backdrop is Cancel (audit 2.2). Before the book has
          saved, Cancel saves nothing. After it has saved and only the share
          failed, Cancel leaves the book saved, which the message says. */}
      {showSharePrompt && (
        <div
          onClick={() => { if (!confirming) closePrompt(); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}>
          <div onClick={stop} style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', maxWidth: 380, width: '100%', padding: 'var(--space-xxl)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', gap: 'var(--space-md)', padding: '12px 14px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-lg)', marginBottom: 18, border: '1px solid var(--brand-sand)' }}>
              <div style={{ width: 38, height: 54, borderRadius: '3px 6px 6px 3px', overflow: 'hidden', background: '#f0f0ef', flexShrink: 0, boxShadow: '2px 2px 6px rgba(0,0,0,0.1)' }}>
                {pendingBook.cover_image_url
                  ? <img src={pendingBook.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)' }}>📚</div>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-display)' }}>{pendingBook.title}</p>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {pendingBook.author_name}</p>
                {pendingBook.source !== 'reanlo' && (
                  <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning)', margin: '4px 0 0', fontWeight: 600 }}>Will be added to Reanlo catalog</p>
                )}
              </div>
            </div>

            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Share to community?</p>
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 18px', lineHeight: 1.5 }}>
              Let your followers know you're reading <strong>{pendingBook.title}</strong>
            </p>

            <SaveError message={saveError} style={{ margin: '0 0 12px', fontSize: 'var(--font-size-sm)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              <button onClick={() => confirmBook(true)} disabled={confirming}
                style={{ width: '100%', padding: 'var(--space-md)', background: 'var(--neutral-800)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: confirming ? 'default' : 'pointer', color: 'var(--brand-white)', opacity: confirming ? 0.6 : 1 }}>
                📣 Share update
              </button>
              <button onClick={() => confirmBook(false)} disabled={confirming}
                style={{ width: '100%', padding: 'var(--space-md)', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', cursor: confirming ? 'default' : 'pointer', color: 'var(--neutral-500)' }}>
                Keep private
              </button>
              <button onClick={closePrompt} disabled={confirming}
                style={{ width: '100%', padding: 'var(--space-sm)', background: 'transparent', border: 'none', fontSize: 'var(--font-size-sm)', cursor: confirming ? 'default' : 'pointer', color: 'var(--neutral-400)' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Paste this helper near the top of ProfileBlocks.jsx (alongside other helpers) ───

async function submitNewTropesToAdmin(tropes = []) {
  if (!tropes.length) return;
  for (const name of tropes) {
    const clean = name?.trim();
    if (!clean) continue;
    // submitTropeName routes the name through process-trope, which decides
    // whether it is an alias of something we have, a merge, a review case, or
    // genuinely new — and only it creates rows. This used to insert an
    // is_active false row itself whenever the exact string was missing, which
    // is how near-duplicates reached the admin queue for a human to untangle.
    await submitTropeName(clean, 'trope');
  }
}
// ═══════════════════════════════════════════════════════════════════════════
// 📚 RECOMMEND ME BLOCK  (max 3)
// ═══════════════════════════════════════════════════════════════════════════
const MAX_RECS = 3;

export function RecommendMeBlock({ profile, onUpdate, readOnly = false }) {
  const [showSearch,      setShowSearch]      = useState(false);
  const [searchQuery,     setSearchQuery]     = useState('');
  const [searchResults,   setSearchResults]   = useState([]);
  const [searching,       setSearching]       = useState(false);
  const [outsideFailed,   setOutsideFailed]   = useState(false);
  const [selectedBook,    setSelectedBook]    = useState(null);
  const [tropes,          setTropes]          = useState([]);
  const [tags,            setTags]            = useState([]);
  const [pitch,           setPitch]           = useState('');
  const [saving,          setSaving]          = useState(false);
  const [showSharePrompt, setShowSharePrompt] = useState(false);
  const [pendingEntry,    setPendingEntry]    = useState(null);
  const [removeError,     setRemoveError]     = useState('');
  const [shareError,      setShareError]      = useState('');
  const [sharingPick,     setSharingPick]     = useState(false);
  // The book whose tags are open. Only books that exist in Reanlo have a
  // book_id — a free-text recommendation has none, and there is nothing to
  // open for it, so the card below only becomes clickable when it has one.
  const [tagPanelFor,     setTagPanelFor]     = useState(null);
  const {
    genres, subgenresById, canonSubgenresForGenre,
    loading: genreTaxonomyLoading, error: genreTaxonomyError,
  } = useGenreTaxonomy();
  const [selectedGenreIds, setSelectedGenreIds] = useState([]);
  const [selectedSubgenreIds, setSelectedSubgenreIds] = useState([]);

  const recommendations = profile?.recommend_me || [];
  const atMax           = recommendations.length >= MAX_RECS;

  // ── Debounced search ──────────────────────────────────────────────────────
  // ── Debounced search with stale-request guard ─────────────────────────────
// ── Debounced search ──────────────────────────────────────────────────────
const debouncedQuery = useDebounce(searchQuery, 400);
const searchIdRef = useRef(0);

useEffect(() => {
  if (!showSearch || selectedBook) return;
  const myId = ++searchIdRef.current;

  searchBooks(
    debouncedQuery,
    (results) => { if (searchIdRef.current === myId) setSearchResults(results); },
    (val)     => { if (searchIdRef.current === myId) setSearching(val); },
    (failed)  => { if (searchIdRef.current === myId) setOutsideFailed(failed); }
  );

  return () => { searchIdRef.current++; };
}, [debouncedQuery, showSearch, selectedBook]);

  function handleSelectBook(book) {
    const autoTropes = (book.book_tags || [])
      .filter(bt => bt.tag_type === 'trope')
      .map(bt => bt.tropes?.canonical_name)
      .filter(Boolean);
    setSelectedBook(book);
    setTropes(autoTropes);
    setPitch('');
  }

  const pitchWordCount = pitch.trim().split(/\s+/).filter(Boolean).length;
const pitchValid = pitchWordCount >= 5;
  // Combined trope+tag floor of 3 (genre spec :1814-1818) -- replaces the old
  // "3 tropes AND 5 total", not stacked with it. genre/subgenre are no
  // longer counted into this floor; they get their own gate below.
  const combinedCount = tropes.length + tags.length;
  // Same "never ask twice" test the journal uses (bookGenres/genreNeeded)
  // -- reused here off book_genres already embedded on selectedBook (the
  // search queries above now select it alongside book_tags), not a second
  // query. PR5 (docs/genre-subgenre-build-spec.md): this used to read
  // book_tags, which stopped being a genre store in this PR -- every live
  // book already has its genre mirrored into book_genres (PR1/PR2), so this
  // switches over rather than checking both. No selectedBook, or an
  // external one with no id, means no book_genres to have found a genre in,
  // so genre is needed by default.
  const genreNeeded = !(selectedBook?.book_genres || []).length;

async function handleConfirmAdd() {
  if (!pitchValid || saving || combinedCount < 3 || (genreNeeded && selectedGenreIds.length === 0)) return;
    setSaving(true);

    const resolvedId = await ensureBookInCatalog(selectedBook, 'recommend-me');

    // The tag writes and the pitch write below are independent, so they run
    // side by side -- see writeReaderPicksToBook for what each tag lane does
    // and why neither one is split any further.
    const tagsWrite = resolvedId
      ? writeReaderPicksToBook(resolvedId, {
          tropeNames:    [...tropes, ...tags],
          genreNames:    genreFilterNames(selectedGenreIds, [], genres, subgenresById),
          subgenreNames: genreFilterNames([], selectedSubgenreIds, genres, subgenresById),
          genreIds:      selectedGenreIds,
          subgenreIds:   selectedSubgenreIds,
          logLabel:      'recommend_me',
        })
      : Promise.resolve({ genreTagFailed: false });

    // The pitch is the reader's description of the book, so put it ON THE
    // BOOK, not only in this reader's profile JSON.
    //
    // Before this it lived solely in the entry below, which meant the
    // three-state description model was fiction: a reader could write real
    // copy about a book and the book's own page stayed blank, with nothing to
    // feed pairing. "If You Like What I Like" captures text and "To Know Me"
    // does not, and yet both produced the identical book state.
    //
    // set_reader_description (20260904040000) decides what actually happens:
    // it archives into reader_description, and promotes to books.description
    // with description_source = 'reader' ONLY when the book has no
    // description yet. A reader never overwrites an author or an earlier
    // reader, and a reader's words never make a book swipe-eligible -- the
    // deck gates on is_author_described, which only an author claim sets.
    //
    // Failure is surfaced, not logged and forgotten: the pitch is the whole
    // point of this block, so losing it silently is the bug class this
    // codebase keeps paying for.
    if (resolvedId && pitch?.trim()) {
      const { data: pitchRes, error: pitchErr } = await supabase.rpc('set_reader_description', {
        p_book_id: resolvedId,
        p_text:    pitch.trim(),
      });
      if (pitchErr) {
        console.error('recommend_me: reader description write failed:', pitchErr.message);
        // Let the tag writes finish first, as they did when they ran before
        // the pitch -- the reader is told to try again, and a retry should
        // not race writes from this attempt that are still in flight.
        await tagsWrite;
        setSaving(false);
        alert('Your recommendation could not be saved to the book. Please try again.');
        return;
      }

      // Ticket 66: the reader's words are now part of the text this book is
      // matched on, so its stored embedding no longer matches it and the
      // author's My Books would say "Readers can't find this book yet".
      // Re-embed it now. The pitch HAS saved at this point, so this must not
      // hold up the reader or read as the save failing: it runs on its own,
      // never throws, and a real failure goes to Sentry (the reader can't act
      // on it; the author's Retry and the batch run are the safety net).
      // `archived`/`promoted` are what set_reader_description returns when it
      // changed the book's text -- neither means nothing changed, so nothing
      // to embed.
      if (pitchRes?.archived || pitchRes?.promoted) {
        requestBookReembed(resolvedId).then((r) => {
          if (!r.ok && !r.limited) {
            Sentry.captureMessage(`reader pitch saved but book ${resolvedId} was not re-embedded: ${r.reason}`, 'warning');
          }
        });
      }
    }

    const { genreTagFailed } = await tagsWrite;

    const entry = {
      book_id:   resolvedId || selectedBook.id || null,
      title:     selectedBook.title,
      author:    selectedBook.author_name,
      cover_url: selectedBook.cover_image_url,
      tropes,
      pitch,
      source:    selectedBook.source || 'external',
    };

    if (!(await saved(onUpdate({ recommend_me: [...recommendations, entry] })))) {
      // The pick, pitch and tags all stay in the form for a retry.
      setSaving(false);
      alert("Your recommendation didn't save to your profile. Check your connection and try again.");
      return;
    }
    // Not awaited: every one of these names already went through
    // process-trope in writeReaderPicksToBook a moment ago, so this is a
    // second pass that only matters for a name the matcher did not answer
    // the first time. The reader should not wait on it -- awaiting it held
    // "Saving..." for another round trip per trope, after everything the
    // reader asked for had already saved.
    submitNewTropesToAdmin(tropes).catch(err => console.error('recommend_me: trope queue submit failed:', err?.message));

    if (genreNeeded && genreTagFailed) {
      alert('Your recommendation was saved, but the genre could not be saved to the book -- please try again.');
    }

    setPendingEntry({ ...entry, _bookObj: selectedBook });
    setShowSearch(false);
    setSelectedBook(null);
    setTropes([]);
    setTags([]);
    setSelectedGenreIds([]);
    setSelectedSubgenreIds([]);
    setPitch('');
    setSearchQuery('');
    setSaving(false);
    setShowSharePrompt(true);
  }

  // The share used to fire without waiting and close the prompt either way,
  // so a post that failed looked exactly like one that went out (audit
  // 13.6). The pick itself is already saved by now; only the share is in
  // question, so a failure keeps the prompt up to try again or skip.
  async function handleShare(shareToFeed) {
    if (sharingPick) return;
    setShareError('');
    if (shareToFeed && pendingEntry) {
      const user = await getCurrentUser();
      if (user) {
        setSharingPick(true);
        const { error } = await supabase.from('shelf_activity_events').insert({
          user_id:        user.id,
          event_type:     'recommend_me',
          book_id:        pendingEntry.book_id || null,
          book_title:     pendingEntry.title,
          book_author:    pendingEntry.author,
          book_cover_url: pendingEntry.cover_url || null,
        });
        setSharingPick(false);
        if (error) {
          reportSaveFailure('recommendation share', error);
          setShareError("Your pick is saved, but the share didn't post. Try again, or keep it on your profile only.");
          return;
        }
      }
    }
    setShowSharePrompt(false);
    setPendingEntry(null);
  }

  async function removeRec(idx) {
    setRemoveError('');
    if (!(await saved(onUpdate({ recommend_me: recommendations.filter((_, i) => i !== idx) })))) {
      setRemoveError("That didn't remove. Check your connection and try again.");
    }
  }

  function openSearch() {
    if (atMax) return;
    setShowSearch(true);
    setSelectedBook(null);
    setSearchQuery('');
    setSearchResults([]);
  }

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onClick={openSearch} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>📚</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>If You Like What I Like</span>
      </div>

      {recommendations.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-10)' }}>
          <SaveError message={removeError} style={{ margin: 0 }} />
          {recommendations.map((rec, i) => (
            <div key={i} style={{ display: 'flex', gap: 'var(--space-10)', padding: 'var(--space-10)', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', border: '1px solid #f0f0ef', position: 'relative' }}>
              <div style={{ width: 44, height: 62, borderRadius: '3px 7px 7px 3px', overflow: 'hidden', background: 'var(--brand-sand)', flexShrink: 0, boxShadow: '2px 2px 6px rgba(0,0,0,0.1)' }}>
                {rec.cover_url
                  ? <img src={rec.cover_url} alt={rec.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xl)' }}>📚</div>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rec.title}</p>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 4px' }}>{rec.author}</p>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', fontStyle: 'italic', margin: 'var(--space-none)', fontFamily: 'var(--font-display)' }}>"{rec.pitch}"</p>
                {rec.tropes?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, marginTop: 'var(--space-xs)' }}>
                    {rec.tropes.slice(0, 2).map((t, j) => (
                      <span key={j} style={{ fontSize: 'var(--font-size-xxs)', padding: '2px 7px', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', borderRadius: 'var(--radius-md)' }}>{t}</span>
                    ))}
                    {/* The card shows two names and the book may carry twenty,
                        so removing one has to happen somewhere that shows all
                        of them. This opens that, and it is the only way in. */}
                    {rec.book_id && !readOnly && (
                      <button onClick={() => setTagPanelFor({ id: rec.book_id, title: rec.title })}
                        style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', textDecoration: 'underline' }}>
                        edit tags
                      </button>
                    )}
                  </div>
                )}
              </div>
              {!readOnly && (
              <button onClick={() => removeRec(i)}
                style={{ position: 'absolute', top: 6, right: 6, background: 'none', border: 'none', color: 'var(--neutral-300)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}
                onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                onMouseLeave={e => e.currentTarget.style.color = '#d6d3d1'}>✕</button>
              )}
            </div>
          ))}

          {readOnly ? null : !atMax ? (
            <button onClick={openSearch}
              style={{ width: '100%', padding: 'var(--space-sm)', background: 'transparent', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', cursor: 'pointer' }}>
              + Add another ({MAX_RECS - recommendations.length} slot{MAX_RECS - recommendations.length !== 1 ? 's' : ''} left)
            </button>
          ) : (
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: 'var(--space-none)', textAlign: 'center' }}>
              Max {MAX_RECS} recommendations, remove one to add another
            </p>
          )}
          {!readOnly && <ShareRecButton recommendations={recommendations} />}
        </div>
      ) : readOnly ? <VisitorEmpty /> : (
        <button onClick={openSearch}
          style={{ width: '100%', padding: 'var(--space-lg)', background: 'var(--brand-paper)', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>
          + Add book recommendations with pitches
        </button>
      )}

      {tagPanelFor && (
        <BookTagPanel bookId={tagPanelFor.id} title={tagPanelFor.title} onClose={() => setTagPanelFor(null)} />
      )}

      {/* ── Search / Pitch Modal ── */}
      {showSearch && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}
          onClick={() => { setShowSearch(false); setSelectedBook(null); setSearchQuery(''); setSearchResults([]); }}
        >
          <div
            style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 520, width: '100%', padding: 'var(--space-xxl)', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            {!selectedBook ? (
              /* ── Step 1: Search ── */
              <>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: '0 0 4px' }}>📚 Add a Recommendation</h3>
                <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 14px' }}>Share a book you'd stake your reputation on</p>

                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by title or author..."
                  autoFocus
                  style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-xs)' }}
                />
                <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '0 0 10px' }}>Searches Reanlo + Open Library</p>

                {searching && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '12px 0' }}>
                    <div style={{ width: 14, height: 14, border: '2px solid var(--brand-sand)', borderTopColor: 'var(--feedback-warning)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.8s linear infinite' }} />
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Searching...</p>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', maxHeight: 280, overflowY: 'auto', scrollbarWidth: 'none' }}>
                  {searchResults.map((book, idx) => (
                    <button key={book.id || idx} onClick={() => handleSelectBook(book)}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '10px 12px', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', cursor: 'pointer', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <div style={{ width: 32, height: 46, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0 }}>
                        {book.cover_image_url
                          ? <img src={book.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)' }}>📚</div>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.title}</p>
                        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {book.author_name}{publicationYear(book) ? ` · ${publicationYear(book)}` : ''}</p>
                      </div>
                      <SourceBadge source={book.source || 'openlibrary'} />
                    </button>
                  ))}
                  {!searching && searchQuery.trim() && searchResults.length === 0 && (
                    <p style={{ fontSize: 'var(--font-size-sm)', color: outsideFailed ? '#b85c38' : 'var(--neutral-400)', textAlign: 'center', padding: '16px 0' }}>
                      {outsideFailed ? SEARCH_FAILED_MESSAGE : 'No results found'}
                    </p>
                  )}
                </div>
              </>
            ) : (
              /* ── Step 2: Pitch + Tropes ── */
              <>
                <button onClick={() => setSelectedBook(null)}
                  style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', cursor: 'pointer', marginBottom: 'var(--space-md)' }}>← Back</button>

                <div style={{ display: 'flex', gap: 'var(--space-md)', padding: '12px 14px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--space-lg)', border: '1px solid var(--brand-sand)' }}>
                  <div style={{ width: 44, height: 62, borderRadius: '3px 7px 7px 3px', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0 }}>
                    {selectedBook.cover_image_url
                      ? <img src={selectedBook.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xl)' }}>📚</div>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px', fontFamily: 'var(--font-display)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedBook.title}</p>
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {selectedBook.author_name}</p>
                    {selectedBook.source !== 'reanlo' && (
                      <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning)', margin: '4px 0 0', fontWeight: 600 }}> Will be added to Reanlo catalog</p>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: 'var(--space-14)' }}>
                  <TagPicker
                    tags={tags} setTags={setTags} tropes={tropes} setTropes={setTropes}
                    selectedGenreIds={selectedGenreIds} setSelectedGenreIds={setSelectedGenreIds}
                    selectedSubgenreIds={selectedSubgenreIds} setSelectedSubgenreIds={setSelectedSubgenreIds}
                    genres={genres} subgenresById={subgenresById} canonSubgenresForGenre={canonSubgenresForGenre}
                    genreLoading={genreTaxonomyLoading} genreError={genreTaxonomyError} genreNeeded={genreNeeded}
                  />
                </div>
                <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>
                  Your pitch <span style={{ color: 'var(--feedback-error)' }}>*</span>
                </p>
               <input
  value={pitch}
  onChange={e => setPitch(e.target.value)}
  placeholder="If you want to feel like magic is real..."
  style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${pitchWordCount === 0 ? '#e8e4dc' : pitchValid ? '#10b981' : '#fecaca'}`, borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-xs)', fontFamily: 'var(--font-display)', fontStyle: 'italic' }}
/>
<p style={{ fontSize: 'var(--font-size-xxs)', color: pitchValid ? '#10b981' : 'var(--neutral-400)', margin: '0 0 12px', textAlign: 'right' }}>
  {pitchValid ? '✓ Good pitch' : `${pitchWordCount}/5 words minimum`}
</p>

                <div style={TAG_MODAL_FOOTER}>
                  <button onClick={() => setSelectedBook(null)}
                    style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>
                    Cancel
                  </button>
               <button onClick={handleConfirmAdd} disabled={!pitchValid || combinedCount < 3 || (genreNeeded && selectedGenreIds.length === 0) || saving}
  style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 700, background: pitchValid && combinedCount >= 3 && (!genreNeeded || selectedGenreIds.length > 0) && !saving ? 'var(--neutral-800)' : 'var(--brand-sand)', color: pitchValid && combinedCount >= 3 && (!genreNeeded || selectedGenreIds.length > 0) ? 'var(--brand-white)' : 'var(--neutral-400)' }}>
  {saving ? 'Saving...' : combinedCount < 3 ? `Add ${3-combinedCount} more trope${3-combinedCount!==1?'s':''} or tag${3-combinedCount!==1?'s':''} ↑` : (genreNeeded && selectedGenreIds.length === 0) ? 'Pick a genre ↑' : !pitchValid ? 'Add your pitch (5+ words) ↑' : 'Add Recommendation →'}
</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Share Prompt ── */}
      {showSharePrompt && pendingEntry && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}>
          <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', maxWidth: 380, width: '100%', padding: 'var(--space-xxl)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', gap: 'var(--space-md)', padding: '12px 14px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-lg)', marginBottom: 18, border: '1px solid var(--brand-sand)' }}>
              <div style={{ width: 38, height: 54, borderRadius: '3px 6px 6px 3px', overflow: 'hidden', background: '#f0f0ef', flexShrink: 0 }}>
                {pendingEntry.cover_url
                  ? <img src={pendingEntry.cover_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)' }}>📚</div>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-display)' }}>{pendingEntry.title}</p>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 4px' }}>by {pendingEntry.author}</p>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-600)', fontStyle: 'italic', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{pendingEntry.pitch}"</p>
              </div>
            </div>

            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Share your recommendation?</p>
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 18px', lineHeight: 1.5 }}>
              Post this to the community feed so your followers can discover it.
            </p>

            <SaveError message={shareError} style={{ margin: '0 0 12px', fontSize: 'var(--font-size-sm)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              <button onClick={() => handleShare(true)} disabled={sharingPick}
                style={{ width: '100%', padding: 'var(--space-md)', background: 'var(--neutral-800)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: sharingPick ? 'default' : 'pointer', color: 'var(--brand-white)', opacity: sharingPick ? 0.6 : 1 }}>
                {sharingPick ? 'Sharing…' : '📣 Share to community'}
              </button>
              <button onClick={() => handleShare(false)} disabled={sharingPick}
                style={{ width: '100%', padding: 'var(--space-md)', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', cursor: sharingPick ? 'default' : 'pointer', color: 'var(--neutral-500)' }}>
                Keep on profile only
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════
// 💛 TO KNOW ME IS TO LOVE ME
// ═══════════════════════════════════════════════════════════════
export function ToKnowMeBlock({ profile, onUpdate, readOnly = false }) {
  const [showSearch,    setShowSearch]    = useState(false);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [outsideFailed, setOutsideFailed] = useState(false);
  const [selectedBook,  setSelectedBook]  = useState(null);
  const [tropes,        setTropes]        = useState([]);
  const [tags,          setTags]          = useState([]);
  const [prefilling,    setPrefilling]    = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [removeError,   setRemoveError]   = useState('');

  const books = profile?.to_know_me || [];
  // Same panel as the other profile block. Both are profile blocks, so both
  // offer removal; only books that exist in Reanlo have a book_id to open it
  // with. See src/lib/tagCredit.js for why the other two reader entry points
  // do not get this.
  const [tagPanelFor, setTagPanelFor] = useState(null);
  const {
    genres: tkmGenres, subgenresById: tkmSubgenresById, canonSubgenresForGenre: tkmCanonSubgenresForGenre,
    loading: tkmGenreTaxonomyLoading, error: tkmGenreTaxonomyError,
  } = useGenreTaxonomy();
  const [selectedGenreIds, setSelectedGenreIds] = useState([]);
  const [selectedSubgenreIds, setSelectedSubgenreIds] = useState([]);
  // Combined trope+tag floor of 3 (genre spec :1814-1818) -- see the identical
  // comment in RecommendMeBlock above.
  const combinedCount = tropes.length + tags.length;
  // Same "never ask twice" test as RecommendMeBlock, off the same embedded
  // selectedBook.book_genres selectBook's autoTropes already reads. PR5
  // (docs/genre-subgenre-build-spec.md): switched from book_tags, see
  // RecommendMeBlock's identical comment above.
  const genreNeeded = !(selectedBook?.book_genres || []).length;

  // ── Debounced search ──────────────────────────────────────────────────────
 const debouncedQuery = useDebounce(searchQuery, 400);
const searchIdRef    = useRef(0);

useEffect(() => {
  if (!showSearch || selectedBook) return;
  const myId = ++searchIdRef.current;

  searchBooks(
    debouncedQuery,
    (results) => { if (searchIdRef.current === myId) setSearchResults(results); },
    (val)     => { if (searchIdRef.current === myId) setSearching(val); },
    (failed)  => { if (searchIdRef.current === myId) setOutsideFailed(failed); }
  );

  return () => { searchIdRef.current++; };
}, [debouncedQuery, showSearch, selectedBook]);

  // ── Prefill from loved shelf if empty ────────────────────────────────────
  useEffect(() => {
    async function prefillFromLovedShelf() {
      // Never on someone else's profile: it reads the signed-in reader's own
      // shelf, which on a visit is the visitor's. Only the public profile's
      // no-op onUpdate kept that from being written.
      if (readOnly || books.length > 0) return;
      setPrefilling(true);
      try {
        const user = await getCurrentUser();
        if (!user) return;
        const { data: lovedBooks } = await supabase
          .from('user_library')
          .select(`book_id, books ( id, title, author_name, cover_image_url, book_tags ( tag_type, tropes ( canonical_name ) ) )`)
          .eq('user_id', user.id)
          .eq('shelf', 'loved')
          .not('book_id', 'is', null)
          .limit(3);

        if (!lovedBooks?.length) return;

        const prefilled = lovedBooks
          .map(item => item.books)
          .filter(Boolean)
          .map(b => ({
            book_id:   b.id,
            title:     b.title,
            author:    b.author_name,
            cover_url: b.cover_image_url,
            tropes:    (b.book_tags || [])
              .filter(bt => bt.tag_type === 'trope')
              .map(bt => bt.tropes?.canonical_name)
              .filter(Boolean),
            source: 'reanlo',
          }));

        if (prefilled.length > 0) await onUpdate({ to_know_me: prefilled });
      } catch (err) {
        console.warn('ToKnowMe prefill error:', err.message);
      } finally {
        setPrefilling(false);
      }
    }
    prefillFromLovedShelf();
  }, []);

  function selectBook(book) {
    const autoTropes = (book.book_tags || [])
      .filter(bt => bt.tag_type === 'trope')
      .map(bt => bt.tropes?.canonical_name)
      .filter(Boolean);
    setSelectedBook(book);
    setTropes(autoTropes);
  }

  async function confirmAdd() {
    if (saving || combinedCount < 3 || (genreNeeded && selectedGenreIds.length === 0)) return;
    setSaving(true);

    // Ensure external books land in catalog
    const resolvedId = await ensureBookInCatalog(selectedBook, 'to-know-me');

    // Same three tag lanes as RecommendMeBlock, side by side -- see
    // writeReaderPicksToBook.
    const { genreTagFailed } = resolvedId
      ? await writeReaderPicksToBook(resolvedId, {
          tropeNames:    [...tropes, ...tags],
          genreNames:    genreFilterNames(selectedGenreIds, [], tkmGenres, tkmSubgenresById),
          subgenreNames: genreFilterNames([], selectedSubgenreIds, tkmGenres, tkmSubgenresById),
          genreIds:      selectedGenreIds,
          subgenreIds:   selectedSubgenreIds,
          logLabel:      'to_know_me',
        })
      : { genreTagFailed: false };

    const entry = {
      book_id:   resolvedId || selectedBook.id || null,
      title:     selectedBook.title,
      author:    selectedBook.author_name,
      cover_url: selectedBook.cover_image_url,
      tropes,
      source:    selectedBook.source || 'external',
    };

    if (!(await saved(onUpdate({ to_know_me: [...books, entry] })))) {
      // Nothing is posted for a pick that did not save, and the form keeps it.
      setSaving(false);
      alert("Your pick didn't save to your profile. Check your connection and try again.");
      return;
    }
    // Not awaited, for the same reason as RecommendMeBlock's identical line.
    submitNewTropesToAdmin(tropes).catch(err => console.error('to_know_me: trope queue submit failed:', err?.message));

    // Post to community
   const user = await getCurrentUser();
    if (user) {
      supabase.from('shelf_activity_events').insert({
        user_id:        user.id,
        event_type:     'to_know_me',
        book_id:        entry.book_id || null,
        book_title:     selectedBook.title,
        book_author:    selectedBook.author_name,
        book_cover_url: selectedBook.cover_image_url || null,
      }).then(({ error }) => { if (error) console.error('ToKnowMe event error:', error.message); });
    }

    if (genreNeeded && genreTagFailed) {
      alert('Your pick was saved, but the genre could not be saved to the book -- please try again.');
    }

    setShowSearch(false);
    setSelectedBook(null);
    setTropes([]);
    setTags([]);
    setSelectedGenreIds([]);
    setSelectedSubgenreIds([]);
    setSearchQuery('');
    setSaving(false);
  }

  // A phone has no hover, so a ✕ that waits for one never shows (audit 3.1).
  // There it is always on screen.
  const [noHover] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && !window.matchMedia('(hover: hover)').matches);

  async function removeBook(idx) {
    setRemoveError('');
    if (!(await saved(onUpdate({ to_know_me: books.filter((_, i) => i !== idx) })))) {
      setRemoveError("That didn't remove. Check your connection and try again.");
    }
  }

  function openSearch() {
    setShowSearch(true);
    setSelectedBook(null);
    setSearchQuery('');
    setSearchResults([]);
  }

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onClick={openSearch} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>💛</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>To Know Me is to Love Me</span>
      </div>
      <SaveError message={removeError} style={{ margin: '0 0 8px' }} />

      {books.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-sm)' }}>
          {books.map((book, i) => (
            <div key={i} style={{ position: 'relative' }}
              onMouseEnter={noHover ? undefined : (e => { const btn = e.currentTarget.querySelector('.remove-btn'); if (btn) btn.style.display = 'flex'; })}
              onMouseLeave={noHover ? undefined : (e => { const btn = e.currentTarget.querySelector('.remove-btn'); if (btn) btn.style.display = 'none'; })}>
              <div style={{ aspectRatio: '2/3', borderRadius: 6, overflow: 'hidden', background: 'var(--neutral-100)', boxShadow: '2px 2px 8px rgba(0,0,0,0.1)' }}>
                {book.cover_url
                  ? <img src={book.cover_url} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h4)' }}>📚</div>}
              </div>
              {!readOnly && (
              <button
                className="remove-btn"
                onClick={() => removeBook(i)}
                style={{ position: 'absolute', top: -4, right: -4, width: noHover ? 24 : 18, height: noHover ? 24 : 18, borderRadius: 'var(--radius-circle)', background: 'var(--feedback-error)', border: 'none', color: 'var(--brand-white)', fontSize: 'var(--font-size-xxs)', cursor: 'pointer', display: noHover ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center' }}>
                ✕
              </button>
              )}
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-800)', margin: '4px 0 0', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.title}</p>
              {book.book_id && !readOnly && (
                <button onClick={() => setTagPanelFor({ id: book.book_id, title: book.title })}
                  style={{ background: 'none', border: 'none', padding: 0, marginTop: 2, cursor: 'pointer', fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', textDecoration: 'underline' }}>
                  edit tags
                </button>
              )}
            </div>
          ))}
          {books.length < 3 && !readOnly && (
            <button onClick={openSearch}
              style={{ aspectRatio: '2/3', borderRadius: 6, border: '1.5px dashed var(--brand-sand)', background: 'transparent', cursor: 'pointer', fontSize: 'var(--font-size-xxl)', color: 'var(--neutral-300)' }}>
              +
            </button>
          )}
        </div>
      ) : readOnly ? <VisitorEmpty /> : (
        <button onClick={openSearch}
          style={{ width: '100%', padding: 'var(--space-xl)', background: 'var(--brand-paper)', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>
          + Add your defining books
        </button>
      )}

      {tagPanelFor && (
        <BookTagPanel bookId={tagPanelFor.id} title={tagPanelFor.title} onClose={() => setTagPanelFor(null)} />
      )}

      {/* ── Search Modal ── */}
      {showSearch && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}
          onClick={() => { setShowSearch(false); setSelectedBook(null); setSearchQuery(''); setSearchResults([]); }}
        >
          <div
            style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 520, width: '100%', padding: 'var(--space-xxl)', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            {!selectedBook ? (
              /* ── Step 1: Search ── */
              <>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: '0 0 6px' }}>💛 Add to To Know Me</h3>
                <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 14px' }}>Books that define you as a reader</p>

                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by title or author..."
                  autoFocus
                  style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-xs)' }}
                />
                <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '0 0 10px' }}>Searches Reanlo + Open Library</p>

                {searching && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '12px 0' }}>
                    <div style={{ width: 14, height: 14, border: '2px solid var(--brand-sand)', borderTopColor: 'var(--feedback-warning)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.8s linear infinite' }} />
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Searching...</p>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', maxHeight: 320, overflowY: 'auto', scrollbarWidth: 'none' }}>
                  {searchResults.map((book, idx) => (
                    <button key={book.id || idx} onClick={() => selectBook(book)}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '10px 12px', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', cursor: 'pointer', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <div style={{ width: 32, height: 46, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0 }}>
                        {book.cover_image_url
                          ? <img src={book.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)' }}>📚</div>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.title}</p>
                        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {book.author_name}{publicationYear(book) ? ` · ${publicationYear(book)}` : ''}</p>
                      </div>
                      <SourceBadge source={book.source || 'openlibrary'} />
                    </button>
                  ))}
                  {!searching && searchQuery.trim() && searchResults.length === 0 && (
                    <p style={{ fontSize: 'var(--font-size-sm)', color: outsideFailed ? '#b85c38' : 'var(--neutral-400)', textAlign: 'center', padding: '16px 0' }}>
                      {outsideFailed ? SEARCH_FAILED_MESSAGE : 'No results found'}
                    </p>
                  )}
                </div>
              </>
            ) : (
              /* ── Step 2: Tropes + confirm ── */
              <>
                <button onClick={() => setSelectedBook(null)}
                  style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', cursor: 'pointer', marginBottom: 'var(--space-md)' }}>
                  ← Back
                </button>

                {/* Book preview */}
                <div style={{ display: 'flex', gap: 'var(--space-md)', padding: '12px 14px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--space-lg)', border: '1px solid var(--brand-sand)' }}>
                  <div style={{ width: 44, height: 62, borderRadius: '3px 7px 7px 3px', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0 }}>
                    {selectedBook.cover_image_url
                      ? <img src={selectedBook.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xl)' }}>📚</div>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px', fontFamily: 'var(--font-display)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedBook.title}</p>
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {selectedBook.author_name}</p>
                    {selectedBook.source !== 'reanlo' && (
                      <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning)', margin: '4px 0 0', fontWeight: 600 }}>Will be added to Reanlo catalog</p>
                    )}
                  </div>
                </div>

                {/* Tropes */}
                <div style={{ marginBottom: 'var(--space-14)' }}>
                  <TagPicker
                    tags={tags} setTags={setTags} tropes={tropes} setTropes={setTropes}
                    selectedGenreIds={selectedGenreIds} setSelectedGenreIds={setSelectedGenreIds}
                    selectedSubgenreIds={selectedSubgenreIds} setSelectedSubgenreIds={setSelectedSubgenreIds}
                    genres={tkmGenres} subgenresById={tkmSubgenresById} canonSubgenresForGenre={tkmCanonSubgenresForGenre}
                    genreLoading={tkmGenreTaxonomyLoading} genreError={tkmGenreTaxonomyError} genreNeeded={genreNeeded}
                  />
                </div>
                <div style={TAG_MODAL_FOOTER}>
                  <button onClick={() => setSelectedBook(null)}
                    style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>
                    Cancel
                  </button>
                  <button onClick={confirmAdd} disabled={saving || combinedCount < 3 || (genreNeeded && selectedGenreIds.length === 0)}
                    style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 700, background: !saving && combinedCount >= 3 && (!genreNeeded || selectedGenreIds.length > 0) ? 'var(--neutral-800)' : 'var(--brand-sand)', color: !saving && combinedCount >= 3 && (!genreNeeded || selectedGenreIds.length > 0) ? 'var(--brand-white)' : 'var(--neutral-400)' }}>
                    {saving ? 'Adding...' : combinedCount < 3 ? `Add ${3-combinedCount} more trope${3-combinedCount!==1?'s':''} or tag${3-combinedCount!==1?'s':''} ↑` : (genreNeeded && selectedGenreIds.length === 0) ? 'Pick a genre ↑' : 'Confirm & Add'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 🏷️ MY TOP TROPES
// ═══════════════════════════════════════════════════════════════
export function TopTropesBlock({ profile, onUpdate, readOnly = false }) {
  const [showModal,        setShowModal]        = useState(false);
  const [allTropes,        setAllTropes]        = useState([]);
  const [selected,         setSelected]         = useState(profile?.top_tropes || []);
  const [search,           setSearch]           = useState('');
  const [showSharePrompt,  setShowSharePrompt]  = useState(false);
  const [submittingCustom, setSubmittingCustom] = useState(false);
  const [tagWarning,       setTagWarning]       = useState('');
  const [saving,           setSaving]           = useState(false);
  const [saveError,        setSaveError]        = useState('');
  const currentTropes = profile?.top_tropes || [];
const previousTropesRef = useRef([]);

  useEffect(() => {
    async function prefillFromLibrary() {
      // Not on someone else's profile: like To Know Me's prefill, this reads
      // the signed-in reader's own library.
      //
      // Once only: save() stamps top_tropes_updated_at, so a block the reader
      // has ever saved, including one they saved empty, is theirs and is not
      // filled from the library again (audit 4.1).
      if (readOnly || currentTropes.length > 0 || profile?.top_tropes_updated_at) return;
      try {
        const user = await getCurrentUser();
        if (!user) return;

        const { data: csvStats } = await supabase.from('user_csv_stats').select('top_tropes').eq('user_id', user.id).maybeSingle();
        if (csvStats?.top_tropes?.length > 0) { await onUpdate({ top_tropes: csvStats.top_tropes.slice(0, 5) }); return; }

        const { data: libData } = await supabase.from('user_library').select('books ( book_tags ( tag_type, tropes ( canonical_name ) ) )').eq('user_id', user.id).in('shelf', ['read', 'loved']).not('book_id', 'is', null);
        if (!libData?.length) return;

        const tropeCounts = {};
        libData.forEach(item => {
          (item.books?.book_tags || []).forEach(bt => {
            if (bt.tag_type !== 'trope') return;
            const name = bt.tropes?.canonical_name;
            if (name) tropeCounts[name] = (tropeCounts[name] || 0) + 1;
          });
        });
        const topTropes = Object.entries(tropeCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name]) => name);
        if (topTropes.length > 0) await onUpdate({ top_tropes: topTropes });
      } catch (err) {
        console.warn('TopTropes prefill error:', err.message);
      }
    }
    prefillFromLibrary();
  }, []);

  useEffect(() => {
    if (showModal) {
     supabase.from('tropes').select('canonical_name').eq('is_active', true).order('canonical_name')
        .then(({ data }) => setAllTropes((data || []).map(t => t.canonical_name)))
        .catch(err => console.error('Trope list fetch failed:', err.message));
      setSelected([...currentTropes]);
      setSaveError('');
    }
  }, [showModal]);

async function save() {
  if (saving) return;
  previousTropesRef.current = currentTropes;   // snapshot BEFORE this edit takes effect
  setSaving(true);
  setSaveError('');
  const ok = await saved(onUpdate({ top_tropes: selected, top_tropes_updated_at: new Date().toISOString() }));
  setSaving(false);
  if (!ok) { setSaveError(SAVE_FAILED); return; }   // the picks stay in the modal
  setShowModal(false);
  setShowSharePrompt(true);
}

async function shareToCommmunity() {
  const user = await getCurrentUser();
  const newlyAdded = selected.filter(t => !previousTropesRef.current.includes(t));
  if (user && newlyAdded.length > 0) {
    await supabase.from('shelf_activity_events').insert(
      newlyAdded.map(t => ({ user_id: user.id, event_type: 'top_trope', trope_name: t }))
    );
  }
  setShowSharePrompt(false);
}
  async function handleAddCustomTrope() {
    const name = search.trim();
    if (!name || selected.length >= 5) return;
    // Refuse rather than truncate, and leave their text alone so they can edit
    // it down. See tagNameError in tropeUtils for why this is not a maxLength.
    const problem = tagNameError(name);
    if (problem) { setTagWarning(problem); return; }
    setTagWarning('');
    setSubmittingCustom(true);
    // Add to selected immediately
    setSelected(prev => [...prev, name]);
    setSearch('');
    // Route it through the matcher rather than creating the row here. The
    // reader keeps their trope on their profile either way — `selected` was
    // already updated above — so a matcher outage costs the catalogue
    // submission, not the reader's choice.
    try {
      await submitTropeName(name, 'trope');
    } catch (err) {
      console.warn('Custom trope submit error:', err.message);
    } finally {
      setSubmittingCustom(false);
    }
  }

  const filtered = allTropes.filter(t => !selected.includes(t) && t.toLowerCase().includes(search.toLowerCase()));
  const isCustomInput = search.trim().length > 0
    && !allTropes.some(t => t.toLowerCase() === search.trim().toLowerCase())
    && !selected.includes(search.trim());

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onClick={() => setShowModal(true)} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>🏷️</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>My Top Tropes</span>
      </div>
      {currentTropes.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
          {currentTropes.map((t, i) => (
            <span key={i} style={{ padding: '5px 12px', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', fontWeight: 500, border: '1px solid var(--feedback-warning-border)' }}>{t}</span>
          ))}
        </div>
      ) : readOnly ? <VisitorEmpty /> : (
        <button onClick={() => setShowModal(true)} style={{ width: '100%', padding: 'var(--space-lg)', background: 'var(--brand-paper)', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>
          + Select your top tropes
        </button>
      )}

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }} onClick={() => setShowModal(false)}>
          <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 500, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--neutral-100)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-md)' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: 'var(--space-none)' }}>🏷️ My Top Tropes</h3>
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: selected.length >= 5 ? 'var(--feedback-error)' : 'var(--neutral-400)', background: selected.length >= 5 ? 'var(--feedback-error-bg)' : 'var(--neutral-100)', padding: '3px 10px', borderRadius: 'var(--radius-10)' }}>
                  {selected.length}/5
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)', marginBottom: 'var(--space-10)', minHeight: 36 }}>
                {selected.map((t, i) => (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '4px 10px', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', border: '1px solid var(--feedback-warning-border)' }}>
                    {t}<span onClick={() => setSelected(selected.filter((_, j) => j !== i))} style={{ cursor: 'pointer', fontSize: 'var(--font-size-xxs)' }}>✕</span>
                  </span>
                ))}
                {selected.length === 0 && <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', fontStyle: 'italic' }}>No tropes selected yet</span>}
              </div>
              <input value={search} onChange={e => { setSearch(e.target.value); if (tagWarning) setTagWarning(''); }} placeholder="Search tropes..."
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-lg)', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ overflowY: 'auto', padding: '10px 20px', flex: 1, scrollbarWidth: 'none' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
                {filtered.map((t, i) => (
                  <button key={i}
                    onClick={() => { if (selected.length < 5) setSelected([...selected, t]); setSearch(''); }}
                    disabled={selected.length >= 5}
                    style={{ padding: '5px 12px', background: 'var(--brand-paper)', color: selected.length >= 5 ? 'var(--neutral-300)' : 'var(--neutral-800)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', border: '1.5px dashed var(--brand-sand)', cursor: selected.length >= 5 ? 'not-allowed' : 'pointer', transition: 'all 0.1s', opacity: selected.length >= 5 ? 0.5 : 1 }}
                    onMouseEnter={e => { if (selected.length < 5) { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.borderColor = '#fde68a'; } }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#faf8f5'; e.currentTarget.style.borderColor = '#e8e4dc'; }}>
                    + {t}
                  </button>
                ))}
              </div>

              {/* Custom trope — shown when search has no match in DB */}
              {isCustomInput && (
                <div style={{ marginTop: 'var(--space-md)', padding: '10px 12px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)' }}>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '0 0 8px' }}>
                    "{search.trim()}" isn't in our list yet
                  </p>
                  <button
                    onClick={handleAddCustomTrope}
                    disabled={submittingCustom || selected.length >= 5}
                    style={{ width: '100%', padding: '8px 12px', background: submittingCustom || selected.length >= 5 ? 'var(--brand-sand)' : 'var(--neutral-800)', color: submittingCustom || selected.length >= 5 ? 'var(--neutral-400)' : 'var(--brand-paper)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: submittingCustom || selected.length >= 5 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-6)' }}>
                    <span>{submittingCustom ? 'Adding...' : `+ Add "${search.trim()}"`}</span>
                    {!submittingCustom && selected.length < 5 && <span style={{ fontSize: 'var(--font-size-xxs)', opacity: 0.55, fontWeight: 400 }}>· sent for review</span>}
                  </button>
                  {tagWarning && (
                    <p role="status" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning-ink)', margin: '8px 0 0', lineHeight: 1.4 }}>
                      {tagWarning}
                    </p>
                  )}
                </div>
              )}
            </div>
            {saveError && (
              <div style={{ padding: '0 20px' }}>
                <SaveError message={saveError} style={{ margin: '0 0 8px', fontSize: 'var(--font-size-sm)' }} />
              </div>
            )}
            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--neutral-100)', display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ flex: 1, padding: '11px', background: 'var(--neutral-800)', border: 'none', borderRadius: 'var(--radius-10)', cursor: saving ? 'default' : 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--brand-white)', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save Tropes'}</button>
            </div>
          </div>
        </div>
      )}

      {showSharePrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}>
          <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 360, width: '100%', padding: 'var(--space-xxl)', textAlign: 'center' }}>
            <p style={{ fontSize: 22, margin: '0 0 8px' }}>🏷️</p>
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Share your tropes?</p>
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 20px' }}>Let your followers know what you're into</p>
            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={() => setShowSharePrompt(false)} style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>Skip</button>
              <button onClick={shareToCommmunity} style={{ flex: 1, padding: '11px', background: 'var(--neutral-800)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--brand-white)' }}>Share to Community</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════
// 🔥 HOT TAKE
// ═══════════════════════════════════════════════════════════════
// Up to MAX_HOT_TAKES takes, one on screen at a time, with arrows between
// them and a finger swipe (useSwipe). Read and saved only through
// src/lib/hotTakes.js, which knows the old single hot_take column and the
// hot_takes list (20261008100000).
//
// The block keeps one size while a reader moves between takes: every take is
// rendered in the same grid cell and only the current one is visible, so the
// cell is always as tall as the longest take. Without that, each arrow press
// would resize the block and shift everything under it on the profile.
//
// The community feed has one card per reader and it shows slide 1
// (firstHotTake). "Post to Community" makes that card public; it does not
// post the take being edited on its own. Decided 8 Oct.

const HOT_TAKE_UNDO_MS = 6000;
// The profile's remove-block button sits over every block's top-right corner
// (EDGE_CLEAR_OF_REMOVE). The block's own 18px padding plus this keeps + Add
// 36px from the edge, the same clearance EDIT_BTN and the CSV button use
// (right: 36).
const HOT_TAKE_CLEAR_OF_REMOVE = EDGE_CLEAR_OF_REMOVE - 18;

const hotTakeArrow = disabled => ({
  width: 30, height: 30, borderRadius: 'var(--radius-circle)', flexShrink: 0,
  border: '1.5px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)',
  color: disabled ? 'var(--neutral-700)' : 'var(--brand-paper)',
  cursor: disabled ? 'default' : 'pointer', fontSize: 15, lineHeight: 1, padding: 0,
});

export function HotTakeBlock({ profile, onUpdate, readOnly = false }) {
  // takes is what is on screen. It follows the profile, except while a save
  // is in flight, when it holds the reader's new list until the save answers.
  const [takes, setTakes] = useState(() => hotTakesOf(profile));
  const [index, setIndex] = useState(0);
  // null, or { at, text }: at is the slide being edited, or 'new'.
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // Which button started the save in flight, so only that one says so.
  const [via, setVia] = useState(null);
  const [saveError, setSaveError] = useState('');
  // The last delete, for Undo: { takes, index } from before it, and after,
  // the list it left. Cleared on a timer, and by anything that can change the
  // list after it (dropUndo).
  const [undo, setUndo] = useState(null);
  const undoTimer = useRef(null);

  useEffect(() => {
    if (saving) return;
    const next = hotTakesOf(profile);
    setTakes(next);
    setIndex(i => Math.min(i, Math.max(next.length - 1, 0)));
  }, [profile, saving]);

  useEffect(() => () => clearTimeout(undoTimer.current), []);

  const count = takes.length;
  const canAdd = !readOnly && count < MAX_HOT_TAKES;
  // A finger swipe does what the arrows do. Off while a take is being edited.
  const swipe = useSwipe({
    onNext: () => setIndex(i => Math.min(count - 1, i + 1)),
    onPrev: () => setIndex(i => Math.max(0, i - 1)),
    enabled: count > 1 && !editing,
  });

  // Saves the whole list. Answers true or false; on false the screen is put
  // back to what was there and the reason is shown.
  async function saveList(next, { nextIndex = 0, rollbackTo = takes, rollbackIndex = index } = {}) {
    const problem = hotTakesProblem(next);
    if (problem) { setSaveError(problem); return false; }
    const clean = cleanHotTakes(next);
    setSaving(true);
    setSaveError('');
    setTakes(clean);
    // Never below 0: deleting the last take asks for slide -1.
    setIndex(Math.max(0, Math.min(nextIndex, clean.length - 1)));
    const ok = await saved(onUpdate(hotTakesPatch(clean)));
    if (!ok) {
      setTakes(rollbackTo);
      setIndex(rollbackIndex);
      setSaveError(SAVE_FAILED);
    }
    setSaving(false);
    return ok;
  }

  // The feed card is one row per reader, shown live from slide 1, so posting
  // only has to make sure the row exists and is public.
  async function postToCommunity() {
    const user = await getCurrentUser();
    if (!user) return;
    const { error } = await supabase.from('community_thought_posts').upsert(
      { user_id: user.id, post_type: 'hot_take', is_public: true, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,post_type' }
    );
    if (error) {
      console.error('Hot take post error:', error.message, error.hint, error.code);
      setSaveError(`Saved to your profile, but not posted to the community: ${error.message}`);
    }
  }

  // Undo is only for the delete just made. Once a reader starts a new take or
  // opens one to edit, the offer goes, or Undo would save the list from
  // before the delete over what they have saved since.
  function dropUndo() {
    clearTimeout(undoTimer.current);
    setUndo(null);
  }

  function startNew() {
    dropUndo();
    setSaveError('');
    setEditing({ at: 'new', text: '' });
  }

  function startEdit(i) {
    if (readOnly) return;
    dropUndo();
    setSaveError('');
    setEditing({ at: i, text: takes[i] || '' });
  }

  async function submit(alsoPost) {
    if (!editing || saving) return;
    const text = editing.text;
    const next = editing.at === 'new' ? [...takes, text] : takes.map((t, i) => (i === editing.at ? text : t));
    const nextIndex = editing.at === 'new' ? takes.length : editing.at;
    setVia(alsoPost ? 'post' : 'profile');
    // The feed shows slide 1, so only a change to slide 1 can leave the feed
    // out of step with "Profile only".
    const changesSlideOne = editing.at === 0 || (editing.at === 'new' && takes.length === 0);
    const ok = await saveList(next, { nextIndex });
    if (!ok) { setVia(null); return; }
    const offFeed = alsoPost || !changesSlideOne ? true : await takeOffFeed('hot_take');
    setVia(null);
    dropUndo();
    setEditing(null);
    if (!offFeed) setSaveError(STILL_IN_FEED);
    if (alsoPost) await postToCommunity();
  }

  async function removeAt(i) {
    if (saving) return;
    const before = takes;
    const beforeIndex = index;
    const next = takes.filter((_, j) => j !== i);
    if (!(await saveList(next, { nextIndex: Math.min(i, next.length - 1) }))) return;
    setEditing(null);
    clearTimeout(undoTimer.current);
    setUndo({ takes: before, index: beforeIndex, after: cleanHotTakes(next) });
    undoTimer.current = setTimeout(() => setUndo(null), HOT_TAKE_UNDO_MS);
  }

  async function undoRemove() {
    if (!undo || saving) return;
    const back = undo;
    dropUndo();
    // A second guard for what dropUndo cannot see, such as the profile
    // reloading with a change from elsewhere: only put the old list back over
    // the exact list the delete left.
    if (JSON.stringify(takes) !== JSON.stringify(back.after)) {
      setSaveError('Couldn\'t undo: your hot takes have changed since that delete.');
      return;
    }
    if (!(await saveList(back.takes, { nextIndex: back.index }))) setUndo(back);
  }

  const stop = e => e.stopPropagation();
  const editingExisting = editing && editing.at !== 'new';
  const editText = editing?.text || '';
  const emptyEdit = !editText.trim();

  return (
    // A click anywhere on the block edits the take on screen (or starts the
    // first one), as the single take always did. Arrows, dots, X and + Add
    // stop the click so they do only their own thing.
    <div onClick={() => { if (readOnly || editing || saving) return; if (count === 0) startNew(); else startEdit(index); }}
      style={{ background: 'var(--neutral-900)', borderRadius: 14, padding: '18px', cursor: readOnly || editing ? 'default' : 'pointer', minHeight: 100, position: 'relative', border: '1px solid var(--neutral-800)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-10)' }}>
        <span style={{ fontSize: 'var(--font-size-lg)', flexShrink: 0 }}>🔥</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--feedback-warning)', letterSpacing: 1, textTransform: 'uppercase' }}>
          {count > 1 ? 'Hot Takes' : 'Hot Take'}
        </span>
        {!editing && canAdd && count > 0 && (
          <button onClick={e => { stop(e); startNew(); }} disabled={saving}
            style={{ marginLeft: 'auto', marginRight: HOT_TAKE_CLEAR_OF_REMOVE, background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 'var(--radius-xxl)', padding: '3px 10px', fontSize: 'var(--font-size-tiny)', fontWeight: 600, color: 'var(--neutral-300)', cursor: 'pointer' }}>
            + Add
          </button>
        )}
        {!editing && !readOnly && count >= MAX_HOT_TAKES && (
          <span style={{ marginLeft: 'auto', marginRight: HOT_TAKE_CLEAR_OF_REMOVE, fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-500)' }}>{MAX_HOT_TAKES} max</span>
        )}
      </div>

      {editing ? (
        <>
          <textarea value={editText} onChange={e => setEditing({ ...editing, text: e.target.value })} onClick={stop}
            placeholder="Your hill to die on..."
            rows={3} autoFocus maxLength={HOT_TAKE_MAX_LENGTH}
            style={{ width: '100%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 'var(--radius-md)', padding: '8px 10px', fontSize: 'var(--font-size-md)', color: 'var(--brand-paper)', outline: 'none', resize: 'none', fontFamily: 'var(--font-display)', boxSizing: 'border-box' }} />
          <div style={{ fontSize: 'var(--font-size-tiny)', color: editText.length > HOT_TAKE_MAX_LENGTH ? '#fca5a5' : 'var(--neutral-500)', textAlign: 'right', marginTop: 2 }}>
            {editText.length}/{HOT_TAKE_MAX_LENGTH}
          </div>
          {editingExisting && emptyEdit ? (
            // Clearing the text of a saved take is how a reader deletes it
            // from here, the same as the X. It used to be impossible: both
            // save buttons stayed off with nothing typed.
            <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)' }}>
              <button onClick={e => { stop(e); removeAt(editing.at); }} disabled={saving}
                style={{ flex: 2, padding: '10px 8px', background: 'transparent', border: '1px solid #fca5a5', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-tiny)', fontWeight: 700, cursor: 'pointer', color: '#fca5a5', textAlign: 'center' }}>
                {saving ? 'Deleting...' : 'Delete this hot take'}
              </button>
              <button onClick={e => { stop(e); setEditing(null); setSaveError(''); }}
                style={{ flex: 1, padding: '10px 8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-tiny)', cursor: 'pointer', color: 'var(--neutral-500)', textAlign: 'center' }}>
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)' }}>
              <button onClick={e => { stop(e); submit(true); }} disabled={saving || emptyEdit}
                style={{ flex: 1, padding: '10px 5px', background: 'var(--brand-paper)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-tiny)', fontWeight: 700, cursor: 'pointer', color: 'var(--neutral-800)', textAlign: 'center', lineHeight: 1.4 }}>
                {saving && via === 'post' ? 'Posting...' : 'Post to Community'}
              </button>
              <button onClick={e => { stop(e); submit(false); }} disabled={saving || emptyEdit}
                style={{ flex: 1, padding: '10px 8px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-tiny)', fontWeight: 600, cursor: 'pointer', color: 'var(--neutral-300)', textAlign: 'center' }}>
                {saving && via === 'profile' ? 'Saving...' : 'Profile only'}
              </button>
              <button onClick={e => { stop(e); setEditing(null); setSaveError(''); }}
                style={{ flex: 1, padding: '10px 8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-tiny)', cursor: 'pointer', color: 'var(--neutral-500)', textAlign: 'center' }}>
                Cancel
              </button>
            </div>
          )}
          {(editing.at === 'new' ? count > 0 : editing.at > 0) && (
            <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-500)', margin: '8px 0 0', lineHeight: 1.4 }}>
              The community feed shows your first hot take.
            </p>
          )}
          <SaveError message={saveError} onDark />
        </>
      ) : count === 0 ? (
        <>
          {readOnly ? <VisitorEmpty onDark />
            : <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-base)', color: 'var(--neutral-600)', fontStyle: 'italic', margin: 'var(--space-none)' }}>Your permanent opinion. Click to write your hot take...</p>}
          {undo && (
            <p onClick={stop} style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '10px 0 0' }}>
              Hot take deleted. <button onClick={e => { stop(e); undoRemove(); }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--feedback-warning)', fontWeight: 700, cursor: 'pointer', fontSize: 'inherit' }}>Undo</button>
            </p>
          )}
          <SaveError message={saveError} onDark />
        </>
      ) : (
        <>
          <div {...swipe}>
            <div style={{ display: 'grid' }}>
              {takes.map((t, i) => (
                <div key={i} aria-hidden={i !== index}
                  style={{ gridArea: '1 / 1', visibility: i === index ? 'visible' : 'hidden', position: 'relative', paddingRight: readOnly ? 0 : 28 }}>
                  <p
                    style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--brand-paper)', lineHeight: 1.5, margin: 'var(--space-none)', cursor: readOnly ? 'default' : 'pointer', overflowWrap: 'anywhere' }}>
                    {t}
                  </p>
                  {!readOnly && (
                    <button onClick={e => { stop(e); removeAt(i); }} disabled={saving || i !== index}
                      aria-label={`Delete hot take ${i + 1}`} title="Delete this hot take"
                      style={{ position: 'absolute', top: -2, right: 0, width: 24, height: 24, borderRadius: 'var(--radius-circle)', border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)', color: 'var(--neutral-300)', fontSize: 13, lineHeight: 1, padding: 0, cursor: 'pointer' }}>
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            {count > 1 && (
              <div onClick={stop} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-10)', marginTop: 'var(--space-md)' }}>
                <button onClick={() => setIndex(i => Math.max(0, i - 1))} disabled={index === 0}
                  aria-label="Previous hot take" style={hotTakeArrow(index === 0)}>‹</button>
                <div style={{ display: 'flex', gap: 6 }} aria-label={`Hot take ${index + 1} of ${count}`}>
                  {takes.map((_, i) => (
                    <button key={i} onClick={() => setIndex(i)} aria-label={`Show hot take ${i + 1}`}
                      style={{ width: 7, height: 7, borderRadius: 'var(--radius-circle)', border: 'none', padding: 0, cursor: 'pointer', background: i === index ? 'var(--feedback-warning)' : 'rgba(255,255,255,0.25)' }} />
                  ))}
                </div>
                <button onClick={() => setIndex(i => Math.min(count - 1, i + 1))} disabled={index === count - 1}
                  aria-label="Next hot take" style={hotTakeArrow(index === count - 1)}>›</button>
              </div>
            )}
          </div>

          {undo && (
            <p onClick={stop} style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '10px 0 0', textAlign: 'center' }}>
              Hot take deleted. <button onClick={e => { stop(e); undoRemove(); }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--feedback-warning)', fontWeight: 700, cursor: 'pointer', fontSize: 'inherit' }}>Undo</button>
            </p>
          )}
          <SaveError message={saveError} onDark />
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 📓 A BOOK THAT... (JOURNAL)
// ════════════════════════════════════════════════════════════════
// Ticket 6a. Backend is 20260901143000_reader_journal_reflections.sql —
// prompts + reflections tables, RLS, shared_reflections_public view.
// Prompts are read live from the `prompts` table, never hardcoded here:
// the seeded rows are the only source of truth for label/emoji/color.
//
// Round 4: rebuilt to match the "JournalBlockOptions.jsx" (Round 3) design
// artifact pixel-for-pixel -- the quote-card TileDeck, the prompt-first
// EntryFlow modal, the per-book "history" modal, the empty-state invitation.
// What's real instead of mock: prompts come from `prompts`, book search is
// `searchBooks()`, genre is asked only when the picked book has no
// `book_genres` row yet (2.4 -- since PR5, docs/genre-subgenre-build-spec.md,
// the same store the author portal writes; book_tags stopped being a genre
// store in that PR, and the unused `books.genres` column was never it),
// comp titles are real search results saved to `reflections.comp_titles`,
// tropes route through `insertReaderTropes()` -> `resolveTagWithBucket()`,
// and the genre/subgenre pick routes through `writeReaderGenrePicks()`
// straight into book_genres/book_subgenres by id -- its bucket was never a
// question (the reader picked it from the genre/subgenre picker), so unlike
// a bare typed trope this never needed a name-resolution step at all.
//
// What's deliberately NOT copied from the artifact: its microphone button
// and waveform are a 1400ms setTimeout and a fixed decorative pattern --
// there is no real recorder wired to this block. Shipping that button would
// be a control that looks like it works and doesn't, which is worse than no
// button at all. So every tile renders the artifact's own honest fallback
// copy ("no voice note yet · open & add to record one") unconditionally,
// and the write step has no mic. The artifact's "OTHER_SECTIONS" accordions
// (Mood & Tone, Content Warnings, ...) are dropped for the same reason --
// their own copy says "the full options... live here", and nothing does.
//
// Public visibility is per-entry (visibility: 'private'|'shared'), but
// even a shared entry never exposes reflection_text on someone else's
// profile — the public view (shared_reflections_public) doesn't carry
// that column at all, so JournalBlockPublic's render can't leak it even
// by a future bug in this file. See JournalBlockPublic below, wired into
// PublicProfile.jsx.
//
// No embedding here — ticket 17 owns that. Rows are left at
// embedding_status = 'pending', which is the column's own default.
const MAX_REFLECTIONS_SHOWN = 200;

// JOURNAL_GENRE_OPTIONS was a flat 22-name mock list (a mix of genres and
// subgenres, e.g. "Mystery" beside "Contemporary Romance") predating the
// real genres/subgenres tables -- deleted here (PR3,
// docs/genre-subgenre-build-spec.md, Control A surface #5). See
// JournalGenreSubgenrePicker below for its replacement.
function journalFormatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toLowerCase();
}

// A book cover with the artifact's spine shadow and tilt. Falls back to a
// brand gradient (never a per-book invented colour, since real books don't
// carry one) with the title/author printed on it, exactly like the
// artifact's own no-cover state.
//
// The image fills the frame (objectFit 'cover'), as every other book cover
// in the app does. It was 'contain', the only cover drawn that way, and an
// image that isn't book-shaped (a square product photo on white, which some
// books carry) shrank to the frame's width and left grey bands above and
// below it. 'cover' trims a few pixels off a real cover instead.
//
// Every picture keeps the frame, whatever its shape. A version that took
// the frame away from a square picture, and showed it whole and small, was
// tried and turned down: the cover should always fill its box.
function JournalCover({ book, w = 84, h = 122, tilt = -2 }) {
  return (
    <div style={{ width: w, height: h, borderRadius: '3px 8px 8px 3px', flexShrink: 0,
      background: book?.cover_image_url ? 'var(--neutral-100)' : 'linear-gradient(155deg, var(--brand-forest) 0%, #20211f 130%)',
      transform: `rotate(${tilt}deg)`, overflow: 'hidden',
      boxShadow: 'inset 8px 0 10px -6px rgba(0,0,0,.6), 5px 6px 14px rgba(41,37,36,.2)',
      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      {book?.cover_image_url ? (
        <img src={book.cover_image_url} alt="" onError={onCoverError}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (w >= 50 && (
        <div style={{ padding: 8 }}>
          <div style={{ fontFamily: 'var(--font-display)', color: '#fff', fontWeight: 700, fontSize: 10, lineHeight: 1.2 }}>{book?.title}</div>
          <div style={{ color: 'var(--brand-gold-light)', fontSize: 7.5, marginTop: 3 }}>{book?.author_name}</div>
        </div>
      ))}
    </div>
  );
}

// What stands where the cover goes on an entry that has no book: the reader wrote
// about a book Reanlo doesn't have (ticket 94).
function NoBookCover({ w = 140, h = 200 }) {
  // The write step shows it at 48 x 70: only the "?", the words don't fit.
  const small = w < 80;
  return (
    <div aria-hidden="true" style={{ width: w, height: h, flexShrink: 0, borderRadius: 4, boxSizing: 'border-box', padding: small ? 4 : 10,
      border: '2px dashed rgba(184,92,56,.55)', background: 'repeating-linear-gradient(135deg, #fbf4de 0 10px, #f3e7cb 10px 20px)',
      display: 'grid', placeItems: 'center', textAlign: 'center' }}>
      <span>
        <span style={{ display: 'block', fontFamily: 'var(--font-display)', fontSize: small ? 24 : 46, fontWeight: 800, color: 'var(--brand-rust)', lineHeight: 1 }}>?</span>
        {!small && <span style={{ display: 'block', fontSize: 9.5, fontWeight: 700, letterSpacing: '.14em', color: '#9E4B2C', marginTop: 8 }}>no book yet</span>}
      </span>
    </div>
  );
}

// Under a bookless entry's words, seen only by its owner. Ask the Shelf comes with
// Reader's Corner, so until then it says so and has no buttons.
function ShelfSticker({ narrow }) {
  return (
    <div onClick={(ev) => ev.stopPropagation()} style={{ marginTop: narrow ? 11 : 14 }}>
      <div style={{ background: '#fff', border: `${narrow ? 1.5 : 2}px dashed rgba(184,92,56,.45)`, borderRadius: narrow ? 10 : 12,
        padding: narrow ? '8px 11px 9px' : '9px 14px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: narrow ? 13.5 : 15, fontWeight: 700, color: 'var(--neutral-800)', lineHeight: 1.25 }}>the shelf might know this one.</div>
        <div style={{ fontSize: narrow ? 11 : 11.5, color: 'var(--neutral-500)', marginTop: 2, lineHeight: 1.35 }}>ask the shelf is coming soon.</div>
      </div>
    </div>
  );
}

// The tappable "private" word inside the share-step footnote.
function JournalPrivateWord() {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', padding: 0,
        color: 'var(--brand-forest)', fontWeight: 700, fontSize: 'inherit', cursor: 'pointer', fontFamily: 'inherit',
        borderBottom: '1.5px dotted var(--brand-forest)' }}>private</button>
      {open && (
        <span style={{ position: 'absolute', bottom: 22, left: '50%', transform: 'translateX(-50%)', width: 270,
          zIndex: 80, background: '#fff', border: '1.5px solid var(--brand-sand)', borderRadius: 12,
          padding: '11px 13px', boxShadow: '5px 6px 0 rgba(41,37,36,.08)', display: 'block', textAlign: 'left' }}>
          <button onClick={() => setOpen(false)} style={{ position: 'absolute', top: 6, right: 8,
            background: 'none', border: 'none', color: 'var(--neutral-400)', fontSize: 11, cursor: 'pointer' }}>✕</button>
          <span style={{ display: 'block', fontSize: 11.5, color: 'var(--neutral-800)', lineHeight: 1.55, paddingRight: 10 }}>
            your reflection stays off your public profile and the community. it helps power book matching
            behind the scenes, always unattributed, never shown with your name.
          </span>
        </span>
      )}
    </span>
  );
}

// The "(i)" tooltip beside the reflection box. Copy speaks only to
// matching and makes no claim about retention -- that is deliberate, see
// the build spec's section 8.4, and it must stay that way.
function JournalPrivTip() {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button aria-label="privacy and matching" onClick={() => setOpen(o => !o)} style={{ width: 26, height: 26,
        borderRadius: '50%', border: '1.5px solid var(--brand-sand)', background: 'transparent', color: 'var(--neutral-400)',
        fontSize: 11, fontStyle: 'italic', fontFamily: 'var(--font-display)', cursor: 'pointer' }}>i</button>
      {open && (
        <div style={{ position: 'absolute', bottom: 34, right: -8, width: 280, zIndex: 70, background: '#fff',
          border: '1.5px solid var(--brand-sand)', borderRadius: 14, padding: '13px 15px',
          boxShadow: '6px 8px 0 rgba(41,37,36,.08)' }}>
          <button onClick={() => setOpen(false)} style={{ position: 'absolute', top: 7, right: 9, background: 'none',
            border: 'none', color: 'var(--neutral-400)', fontSize: 12, cursor: 'pointer' }}>✕</button>
          <div style={{ fontSize: 12, color: 'var(--neutral-800)', lineHeight: 1.55, paddingRight: 12 }}>
            Nothing writes or rephrases what you wrote. A private reflection is used only
            as anonymous matching signal.
          </div>
          <a href="/how-matching-works" style={{ display: 'inline-block', marginTop: 7, fontSize: 11.5,
            fontWeight: 700, color: 'var(--brand-rust)', textDecoration: 'none' }}>how matching works →</a>
        </div>
      )}
    </span>
  );
}

// Journal's genre pick, ported to the real genres/subgenres tables (PR3,
// Control A surface #5 -- write-in allowed, same as every other Control A
// caller). GenreSubgenrePicker itself is multi-select by design (every
// other Control A surface can tag a book with more than one genre), but
// the journal's own contract only ever had room for exactly one:
// The journal's own write (writeReaderGenrePicks, PR5) only ever sends one
// genre id for this block, canSubmit blocks on "no genre picked" (never
// "fewer than N"), and no doc anywhere describes a journal entry carrying
// two genres. Rather than fork the shared component for
// this one caller, this wrapper reuses it unmodified and corrects back
// down to one genre the instant a second one lands -- via useLayoutEffect,
// so the correction happens before paint and there is no visible flash of
// two chips selected. Subgenre stays exactly as multi-select and optional
// as it is everywhere else: nothing in the spec caps the journal at one
// subgenre, only that a subgenre must never be required (build spec Part
// 5's "subgenre is always optional" rule, unrelated to this genre-count
// constraint).
function JournalGenreSubgenrePicker(props) {
  const { selectedGenreIds, setSelectedGenreIds, selectedSubgenreIds, setSelectedSubgenreIds,
    subgenreNamesById, canonSubgenresForGenre } = props;

  useLayoutEffect(() => {
    if (selectedGenreIds.length <= 1) return;
    // A second genre chip landed (the reader tapped a new one without
    // unchecking the first) -- keep only the most recently added id, and
    // drop any subgenre picks that belonged to the genre being dropped.
    // Mirrors GenreSubgenrePicker's own toggleGenre "uncheck" cleanup,
    // which never runs here since neither chip was explicitly unchecked.
    const keepId = selectedGenreIds[selectedGenreIds.length - 1];
    setSelectedGenreIds([keepId]);
    const keepSubgenreIds = new Set(canonSubgenresForGenre(keepId).map(s => s.id));
    for (const [sid, info] of Object.entries(subgenreNamesById)) {
      if (info.genreId === keepId) keepSubgenreIds.add(Number(sid));
    }
    setSelectedSubgenreIds(prev => prev.filter(sid => keepSubgenreIds.has(sid)));
  }, [selectedGenreIds, canonSubgenresForGenre, subgenreNamesById, setSelectedGenreIds, setSelectedSubgenreIds]);

  return <GenreSubgenrePicker Chip={TagPickerChip} {...props} />;
}

// Reader-typed tropes, added by name only -- no fake preset chip list,
// since the artifact's TROPES array is mock data with nothing real behind
// it. Names are routed through insertReaderTropes()/resolveTagWithBucket()
// at save time; this component only collects them.
function JournalTropeBox({ tropes, setTropes }) {
  const [v, setV] = useState('');
  function add() {
    const t = v.trim();
    if (!t || tropes.includes(t)) { setV(''); return; }
    setTropes(x => [...x, t]);
    setV('');
  }
  return (
    <div style={{ background: '#fff', border: '1px solid var(--brand-sand)', borderRadius: 12, padding: '11px 14px' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--neutral-800)', marginBottom: 8 }}>Tropes</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
        {tropes.map(t => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1.5px solid var(--brand-forest)',
            background: 'var(--brand-forest)', color: '#fff', borderRadius: 999, padding: '5px 6px 5px 12px', fontSize: 11.5 }}>
            {t}
            <button onClick={() => setTropes(x => x.filter(y => y !== t))} style={{ background: 'none', border: 'none',
              color: 'rgba(255,255,255,.8)', cursor: 'pointer', fontSize: 11, padding: 0 }}>✕</button>
          </span>
        ))}
        <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          <input value={v} onChange={e => setV(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()}
            placeholder="search or add a trope…" style={{ width: 150, padding: '5px 11px', borderRadius: 999,
              border: '1.5px dashed var(--neutral-400)', fontSize: 11.5, outline: 'none', background: 'transparent' }} />
          {v.trim() && <button onClick={add} style={{ border: 'none', background: 'var(--brand-forest)', color: '#fff',
            borderRadius: 999, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>add</button>}
        </span>
      </div>
    </div>
  );
}

// Reusable comp-titles picker: up to 3 books with an optional "how they
// connect" line each. Stored on `reflections.comp_titles` (jsonb, already on
// main) from day one -- dormant until V1.5 wires it into matching/embedding,
// per the ticket doc. Used by both new entries and edits, since "editable
// from day one" applies to comp titles too, not just the reflection text.
function CompTitlesPicker({ comps, setComps }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [outsideFailed, setOutsideFailed] = useState(false);
  const debouncedQuery = useDebounce(query, 400);
  const searchIdRef = useRef(0);

  useEffect(() => {
    if (comps.length >= 3 || !debouncedQuery.trim()) { setResults([]); return; }
    const myId = ++searchIdRef.current;
    searchBooks(
      debouncedQuery,
      (r) => { if (searchIdRef.current === myId) setResults(r); },
      (v) => { if (searchIdRef.current === myId) setSearching(v); },
      (f) => { if (searchIdRef.current === myId) setOutsideFailed(f); }
    );
  }, [debouncedQuery, comps.length]);

  function addBook(book) {
    setComps(c => [...c, { book, bridge: '' }]);
    setQuery('');
    setResults([]);
  }
  function setBridge(i, v) {
    setComps(c => c.map((x, xi) => xi === i ? { ...x, bridge: v } : x));
  }
  function removeAt(i) {
    setComps(c => c.filter((_, xi) => xi !== i));
  }

  return (
    <div style={{ background: '#fff', border: '1px solid var(--brand-sand)', borderRadius: 12, padding: '11px 14px' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--neutral-800)' }}>Comp Titles</div>
      <div style={{ fontSize: 11, color: 'var(--neutral-400)', marginTop: 3 }}>
        "For readers who liked...": add 1-3 books this compares to.
      </div>
      {comps.map((c, i) => (
        <div key={i} style={{ marginTop: 9, background: 'var(--brand-paper)', borderRadius: 10, padding: '8px 11px' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <JournalCover book={c.book} w={28} h={40} tilt={0} />
            <span style={{ flex: 1, fontSize: 12, color: 'var(--neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><b>{c.book.title}</b></span>
            <button onClick={() => removeAt(i)} style={{ background: 'none', border: 'none', color: 'var(--neutral-400)', fontSize: 12, cursor: 'pointer' }}>✕</button>
          </div>
          <input value={c.bridge} onChange={e => setBridge(i, e.target.value)}
            placeholder="how they connect (optional)"
            style={{ width: '100%', marginTop: 6, border: 'none', outline: 'none', background: 'transparent',
              fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 12.5, color: 'var(--neutral-800)', boxSizing: 'border-box' }} />
        </div>
      ))}
      {comps.length < 3 && (
        <>
          <input value={query} onChange={e => setQuery(e.target.value)}
            placeholder={comps.length === 0 ? 'search a book it compares to…' : 'add another (up to 3)…'}
            style={{ width: '100%', marginTop: 9, padding: '8px 12px', borderRadius: 999,
              border: '1.5px dashed var(--neutral-400)', fontSize: 11.5, outline: 'none', background: 'transparent', boxSizing: 'border-box' }} />
          {searching && <p style={{ fontSize: 11, color: 'var(--neutral-400)', margin: '6px 0 0' }}>Searching...</p>}
          {results.map((b, i) => (
            <button key={i} onClick={() => addBook(b)} style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 7,
              background: 'var(--brand-paper)', border: 'none', borderRadius: 10, padding: '7px 10px', cursor: 'pointer',
              width: '100%', textAlign: 'left', boxSizing: 'border-box' }}>
              <JournalCover book={b} w={26} h={38} tilt={0} />
              <span style={{ fontSize: 12, color: 'var(--neutral-800)' }}><b>{b.title}</b> <span style={{ color: 'var(--neutral-500)' }}>· {b.author_name}</span></span>
            </button>
          ))}
          {!searching && query.trim() && results.length === 0 && (
            <p style={{ fontSize: 11, color: outsideFailed ? 'var(--brand-rust)' : 'var(--neutral-400)', margin: '6px 0 0' }}>
              {outsideFailed ? SEARCH_FAILED_MESSAGE : 'No results found'}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ── the entry flow: prompt → book → write → share, exactly the artifact's
// four steps, wired to the live prompts table and the real search path.
// ── Voice note recording, upload & transcription for the journal's write
// step (ticket 6b). One-time consent gate per reader (9.9), a 3-minute
// keepAudio recording (9.1/9.4), upload-then-transcribe-then-correct
// (9.3), and "no orphans" cleanup for anything uploaded but never saved.
//
// voiceNote.origin distinguishes two kinds of "there's already audio
// here", which get cleaned up very differently:
//   - 'session': recorded in THIS write-step visit, not yet attached to
//     any row. Superseding it (re-recording, removing, or closing the
//     sheet without saving) deletes it immediately, best-effort -- it
//     will never be referenced by anything once that happens.
//   - 'existing': the note already saved on an entry being edited. Never
//     deleted speculatively here -- only handleDone deletes it, and only
//     after its replacement (or its removal) is safely committed to the
//     row, so a cancelled edit never loses a note that was never actually
//     replaced.
// What the reader is told when the recording couldn't be stored, keyed by
// transcribeRecordingInline's status. None of them is just "please try
// again": a retry only helps if the connection dropped, the reader can't
// tell whether it did, and what they need to know is where their words went.
const UPLOAD_FAILED_NOTICE = {
  ok: "your recording couldn't be saved, so it isn't attached, but what you said is in the box above.",
  partial: "your recording couldn't be saved. some of what you said is in the box above, but part of it couldn't be turned into text.",
  empty: "your recording couldn't be saved, and we didn't catch any words in it. you can type instead, or record again.",
  failed: "your recording couldn't be saved or turned into text. you can type what you said, or record again.",
};

function VoiceNoteRecorder({ voiceNote, setVoiceNote, reflectionText, setReflectionText, flowOpenRef }) {
  const recorder = useVoiceRecorder({ maxDurationMs: 180_000, keepAudio: true });
  // 6b piece 7 ("the fallback stays"): declining the storage consent must
  // not take voice input away entirely -- it only means this recording is
  // never uploaded or kept. This second hook instance is ticket 2's
  // original shape (keepAudio: false, the 55s sync-recognize cap from
  // useVoiceRecorder's own DEFAULT_MAX_DURATION_MS) -- the same
  // transcribe-then-discard path AddBook already uses, reused here rather
  // than rebuilt, since it already is "the old way" the ticket asks for.
  // A second fixed-mode instance, not the same `recorder` reconfigured:
  // useVoiceRecorder.js's own ref-mirroring comment explains why flipping
  // one hook's keepAudio between clicks risks a stale closure mid-flip --
  // two instances that never change mode have no such window.
  const declinedRecorder = useVoiceRecorder({ keepAudio: false });
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [checkingConsent, setCheckingConsent] = useState(false);
  const [grantingConsent, setGrantingConsent] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // This component unmounts every time the reader leaves the write step --
  // on to share, or back to prompt or book -- while the entry is still
  // open. So it must NOT clean up after itself on unmount: it used to, and
  // that deleted every recording the moment the reader moved on to the
  // share step, before the entry that points at it was saved. Abandoning a
  // recording is decided by JournalEntryFlow, which outlives every step
  // (see its own unmount cleanup). For the same reason, the async work
  // below checks flowOpenRef -- is the entry still open? -- rather than
  // whether this component is still on screen: an upload that finishes
  // while the reader is on another step still belongs to the entry.

  // A reader is asked for voice consent once, ever (src/lib/voiceConsent.js,
  // top of file). So a failed check must NOT fall through to the modal: that
  // would ask a reader who has already agreed, just because a read failed.
  // It says so and stops; pressing the mic again retries the check.
  async function handleMicClick() {
    setCheckingConsent(true);
    let granted;
    try {
      granted = await hasGrantedVoiceConsent();
    } catch (err) {
      console.error('Voice consent check failed:', err.message);
      setCheckingConsent(false);
      setUploadError("Couldn't start recording. Please try again.");
      return;
    }
    setCheckingConsent(false);
    if (!granted) {
      setShowConsent(true);
      return;
    }
    setUploadError(null);
    recorder.start();
  }

  async function handleAgreeConsent() {
    setGrantingConsent(true);
    try {
      await grantVoiceConsent();
      setShowConsent(false);
      setConsentChecked(false);
      setUploadError(null);
      recorder.start();
    } catch (err) {
      console.error('Granting voice consent failed:', err.message);
      alert('Could not save your consent. Please try again.');
    } finally {
      setGrantingConsent(false);
    }
  }

  // Consent declined -- distinct from "cancel" (which just closes the
  // modal below and does nothing). This starts recording anyway, through
  // the keepAudio:false instance, so nothing on this path ever calls
  // uploadVoiceNote or touches voiceNote/objectPath -- there is no audio
  // in the bucket to orphan-clean because none is ever written there.
  function handleDeclineAndRecord() {
    setShowConsent(false);
    setConsentChecked(false);
    setUploadError(null);
    declinedRecorder.start();
  }

  // declinedRecorder.stop() already resolves to the transcript string (or
  // '' on nothing captured / a transcription failure, which
  // declinedRecorder.error reports on its own) -- the same contract
  // AddBook's caller relies on. Appending to reflectionText is the only
  // piece of handleStop this path needs; the rest of that function
  // (upload, transcribeVoiceNote, setVoiceNote) is entirely about the
  // stored path and does not apply here.
  async function handleDeclinedStop() {
    const transcript = await declinedRecorder.stop();
    if (!transcript) return;
    setReflectionText(t => (t.trim() ? `${t}\n\n${transcript}` : transcript));
  }

  async function handleStop() {
    const priorOrigin = voiceNote.origin;
    const result = await recorder.stop();
    if (!result?.blob) return; // nothing was actually captured

    if (voiceNote.origin === 'session' && voiceNote.objectPath) {
      deleteOrphanedUpload(voiceNote.objectPath);
    }

    setUploadError(null);
    setVoiceNote({ objectPath: null, durationSeconds: result.durationSeconds, transcript: null, status: 'uploading', origin: 'session' });

    let objectPath;
    try {
      objectPath = await uploadVoiceNote(result.blob, result.durationSeconds);
    } catch {
      // uploadVoiceNote has already logged the failure and sent it to Sentry
      // with the step that broke. The recording can't be kept, but it is
      // still in memory, so the reader's words can be: transcribe it without
      // storing it. Until this, a failed upload threw the words away and
      // said "please try again", which could never work while the bucket
      // refused every upload.
      if (!flowOpenRef.current) return;
      const idle = {
        objectPath: null, durationSeconds: null, transcript: null, status: 'idle',
        // A reader re-recording on an entry whose old note they removed is
        // still in that state: the old note stays removed when they save.
        origin: priorOrigin === 'removed_existing' ? 'removed_existing' : null,
      };
      setVoiceNote({ ...idle, status: 'transcribing' });
      const inline = await transcribeRecordingInline(result.blob, { where: 'upload_failed' });
      if (!flowOpenRef.current) return;
      if (inline.transcript) {
        setReflectionText(t => (t.trim() ? `${t}\n\n${inline.transcript}` : inline.transcript));
      }
      setUploadError(UPLOAD_FAILED_NOTICE[inline.status]);
      setVoiceNote(idle);
      return;
    }

    if (!flowOpenRef.current) {
      // The entry closed while this upload was in flight -- nothing in any
      // state will ever reference this object now.
      deleteOrphanedUpload(objectPath);
      return;
    }

    setVoiceNote({ objectPath, durationSeconds: result.durationSeconds, transcript: null, status: 'transcribing', origin: 'session' });

    let outcome;
    try {
      outcome = await transcribeVoiceNote(objectPath);
    } catch (err) {
      // 9.8: transcription failing must not lose the recording. The
      // upload already succeeded -- keep objectPath and durationSeconds,
      // just without a transcript, same as a plain timeout below.
      console.error('Voice note transcription failed:', err.message);
      Sentry.captureException(new Error(`voice note long-running transcription: ${err.message}`), {
        tags: { voice_step: 'long_running_transcribe' },
      });
      outcome = { status: 'failed' };
    }

    if (!flowOpenRef.current) {
      deleteOrphanedUpload(objectPath);
      return;
    }

    // The stored copy didn't come back as text, but the recording is still
    // in memory: try the in-memory path before telling the reader there is
    // no text. This is what happens if Google's speech service can't read
    // the bucket (voice-to-text/index.ts, startLongRunningOperation).
    let partial = false;
    if (outcome.status === 'failed' || outcome.status === 'timeout') {
      if (outcome.status === 'timeout') {
        Sentry.captureMessage('voice note long-running transcription timed out', {
          level: 'warning', tags: { voice_step: 'long_running_transcribe' },
        });
      }
      const inline = await transcribeRecordingInline(result.blob, { where: 'long_running_failed' });
      if (!flowOpenRef.current) {
        deleteOrphanedUpload(objectPath);
        return;
      }
      if (inline.transcript) {
        outcome = { status: 'ok', transcript: inline.transcript };
        partial = inline.status === 'partial';
      } else if (inline.status === 'empty') {
        outcome = { status: 'empty' };
      }
    }

    if (outcome.status === 'ok') {
      setVoiceNote({ objectPath, durationSeconds: result.durationSeconds, transcript: outcome.transcript, status: 'ready', origin: 'session' });
      setReflectionText(t => (t.trim() ? `${t}\n\n${outcome.transcript}` : outcome.transcript));
      if (partial) setUploadError("part of what you said couldn't be turned into text. the rest is in the box above, and the recording is saved.");
    } else if (outcome.status === 'empty') {
      setVoiceNote({ objectPath, durationSeconds: result.durationSeconds, transcript: null, status: 'empty', origin: 'session' });
    } else {
      setVoiceNote({ objectPath, durationSeconds: result.durationSeconds, transcript: null, status: 'transcribe_failed', origin: 'session' });
    }
  }

  async function handleRemove() {
    if (voiceNote.origin === 'session' && voiceNote.objectPath) {
      await deleteOrphanedUpload(voiceNote.objectPath);
    }
    // An 'existing' note is left alone here -- deleting its object happens
    // in handleDone, only once the reader actually saves with no voice
    // note attached, not speculatively the moment this button is clicked.
    setVoiceNote({ objectPath: null, durationSeconds: null, transcript: null, status: 'idle', origin: voiceNote.origin === 'existing' ? 'removed_existing' : null });
  }

  // Whichever instance is actually recording drives this block -- at most
  // one ever is: handleMicClick's granted branch, handleAgreeConsent, and
  // handleDeclineAndRecord are three mutually exclusive ways out of the
  // same click, and none of them can run while the other recorder is
  // already active (the idle mic button that starts either one is not
  // even rendered once either is recording -- see below).
  const activeRecorder = recorder.state === 'recording' ? recorder
    : declinedRecorder.state === 'recording' ? declinedRecorder
    : null;
  if (activeRecorder) {
    const isDeclined = activeRecorder === declinedRecorder;
    const remainingS = Math.ceil(Math.max(0, activeRecorder.maxDurationMs - activeRecorder.elapsedMs) / 1000);
    return (
      <div style={{ marginTop: 10, background: 'rgba(184,92,56,.06)', border: '1.5px solid var(--brand-rust)', borderRadius: 13, padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 22, flex: 1 }}>
            {activeRecorder.waveform.map((level, i) => (
              <div key={i} style={{ width: 3, borderRadius: 1.5, height: Math.max(2, level * 22), background: 'var(--brand-rust)' }} />
            ))}
          </div>
          <span style={{ fontSize: 11, color: 'var(--brand-rust)', fontWeight: 700, flexShrink: 0 }}>
            {Math.floor(remainingS / 60)}:{(remainingS % 60).toString().padStart(2, '0')} left
          </span>
        </div>
        {isDeclined && (
          <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--neutral-500)' }}>not being saved, text only</div>
        )}
        <button onClick={isDeclined ? handleDeclinedStop : handleStop} style={{ marginTop: 8, width: '100%', border: 'none', background: 'var(--brand-rust)',
          color: '#fff', borderRadius: 10, padding: 9, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>⏹ stop recording</button>
      </div>
    );
  }

  if (voiceNote.status === 'uploading' || voiceNote.status === 'transcribing' || declinedRecorder.state === 'transcribing') {
    return (
      <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--neutral-500)' }}>
        {voiceNote.status === 'uploading' ? 'uploading your recording…' : 'transcribing…'}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      {voiceNote.status === 'ready' && voiceNote.objectPath && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'var(--brand-forest)', fontWeight: 700 }}>
          <span>🎙️ voice note attached · {Math.round(voiceNote.durationSeconds || 0)}s</span>
          <button onClick={handleRemove} style={{ background: 'none', border: 'none', color: 'var(--neutral-400)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>remove</button>
        </div>
      )}
      {voiceNote.status === 'empty' && (
        <div style={{ fontSize: 11, color: 'var(--neutral-500)' }}>
          didn't catch that, the recording is kept, but you can type it instead, or{' '}
          <button onClick={handleMicClick} style={{ background: 'none', border: 'none', color: 'var(--brand-rust)', fontSize: 11, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>try again</button>
        </div>
      )}
      {voiceNote.status === 'transcribe_failed' && (
        <div style={{ fontSize: 11, color: 'var(--brand-rust)' }}>
          we couldn't turn that into text. your recording is saved, you can type what you said, or{' '}
          <button onClick={handleMicClick} style={{ background: 'none', border: 'none', color: 'var(--brand-rust)', fontSize: 11, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>try again</button>
        </div>
      )}
      {/* Rust means something was lost. When the recording didn't save but
          every word reached the box, nothing the reader said was lost, so
          that one is shown calm, like "didn't catch that" above. The rest
          (some or all of the words lost, or recording wouldn't start) stay
          rust. Lowercase like the other notes here. */}
      {uploadError && <div style={{ fontSize: 11, color: uploadError === UPLOAD_FAILED_NOTICE.ok ? 'var(--neutral-500)' : 'var(--brand-rust)', marginBottom: 6 }}>{uploadError}</div>}
      {(voiceNote.status === 'idle' || voiceNote.status === 'removed_existing') && (
        <button onClick={handleMicClick} disabled={checkingConsent || declinedRecorder.state === 'transcribing'} style={{ border: '1.5px dashed var(--brand-rust)', background: 'transparent',
          color: 'var(--brand-rust)', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: (checkingConsent || declinedRecorder.state === 'transcribing') ? 'default' : 'pointer' }}>
          🎙️ {checkingConsent ? 'checking…' : voiceNote.origin === 'removed_existing' ? 're-record voice note' : 'record instead of typing'}
        </button>
      )}
      {recorder.error && <div style={{ fontSize: 11, color: 'var(--brand-rust)', marginTop: 6 }}>{recorder.error}</div>}
      {declinedRecorder.error && <div style={{ fontSize: 11, color: 'var(--brand-rust)', marginTop: 6 }}>{declinedRecorder.error}</div>}

      {showConsent && (
        <div onClick={() => setShowConsent(false)} style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(41,37,36,.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440,
            padding: '22px 24px', boxShadow: '10px 12px 0 rgba(41,37,36,.16)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17, color: 'var(--neutral-800)' }}>before you record</div>
            {/* CONSENT_TEXT is the lawyer-approved wording, verbatim -- shown
                once, as the checkbox's own label, not duplicated as a
                separate paragraph above it. That is deliberate: the earlier
                placeholder was displayed as a description ABOVE a short,
                separately-worded "I understand and agree" label, but the
                lawyer's actual text is itself the agreement statement (it
                already reads "I agree that...", "I've read..."), so
                repeating it a second time in different words would mean two
                different descriptions of the same consent existing on
                screen at once -- exactly the kind of drift this file
                elsewhere goes out of its way to avoid. */}
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, fontSize: 12, color: 'var(--neutral-700)', lineHeight: 1.6, cursor: 'pointer' }}>
              <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
              {/* Required by the lawyer's wording: the checkbox itself has to
                  link straight to the Voice & Biometric Data section of the
                  privacy policy, not just live near it. #166 gave that
                  section a stable, lowercase #voice-biometric-data fragment
                  independent of its heading text -- see PrivacyPolicy.jsx's
                  own comment on why the id can't just be derived from the
                  heading. onClick stopPropagation isn't strictly needed
                  (browsers already exempt an interactive descendant like an
                  <a> from a wrapping <label>'s click-to-toggle behavior),
                  but it's kept explicit so a future style/library change to
                  this label can't quietly turn "open the policy" into "open
                  the policy AND flip the checkbox". */}
              <span>
                {CONSENT_TEXT_BEFORE_LINK}
                <a href="https://reanloforreaders.com/privacy#voice-biometric-data" target="_blank" rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()} style={{ color: 'var(--brand-rust)', fontWeight: 700 }}>
                  {CONSENT_LINK_PHRASE}
                </a>
                {CONSENT_TEXT_AFTER_LINK}
              </span>
            </label>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              {/* This IS "decline" (ticket 6b piece 7) -- the modal only has
                  two real outcomes, agree to storage or don't, so declining
                  is what this button does rather than a third separate
                  action. It still records; it just never uploads or keeps
                  the audio. A reader who wants out entirely (no recording
                  at all) still has the backdrop click above, which stays a
                  true no-op close. */}
              <button onClick={handleDeclineAndRecord} style={{ flex: 1, border: '1.5px solid var(--brand-sand)', background: '#fff',
                color: 'var(--neutral-600)', borderRadius: 11, padding: 10, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>don't save, just record</button>
              <button onClick={handleAgreeConsent} disabled={!consentChecked || grantingConsent} style={{ flex: 1, border: 'none',
                background: consentChecked ? 'var(--brand-rust)' : 'var(--brand-sand)', color: consentChecked ? '#fff' : 'var(--neutral-400)',
                borderRadius: 11, padding: 10, fontSize: 12.5, fontWeight: 800, cursor: consentChecked && !grantingConsent ? 'pointer' : 'not-allowed' }}>
                {grantingConsent ? 'saving…' : 'agree & record'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Playback for a saved entry's tile. Waveform is derived from the
// actual decoded audio at play time (spec 5.4/9.4's "either is fine" --
// we didn't add a peaks-storage column in the 6b migration, so this is
// the one of the two options that fits what's already built), not from a
// fixed decorative pattern.
const WAVEFORM_BAR_COUNT = 24;

// A twentieth of a second of silence, as a data: URL, built once. Played
// inside the tap to unlock the <audio> element for Safari; see togglePlay.
let silentWavUrl = null;
function getSilentWavUrl() {
  if (!silentWavUrl) silentWavUrl = `data:audio/wav;base64,${bytesToBase64(encodeWav16(new Float32Array(400), 8000))}`;
  return silentWavUrl;
}

function VoiceNotePlayback({ reflectionId, durationSeconds }) {
  const [state, setState] = useState('idle'); // idle | loading | playing | error
  const [peaks, setPeaks] = useState(null);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);

  useEffect(() => () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // The silent unlock clip (togglePlay) fires these too, while the real
    // recording is still loading; it must not reset the button to idle.
    const isUnlockClip = () => audio.src === getSilentWavUrl();
    const onTimeUpdate = () => { if (!isUnlockClip()) setProgress(audio.duration ? audio.currentTime / audio.duration : 0); };
    const onEnded = () => { if (!isUnlockClip()) { setState('idle'); setProgress(0); } };
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  async function togglePlay(ev) {
    ev.stopPropagation();
    const audio = audioRef.current;
    if (state === 'playing') {
      audio?.pause();
      setState('idle');
      return;
    }
    setState('loading');

    // Safari (iPhone and Mac) only lets audio start from inside the tap
    // itself, and everything below waits on the network first: minting the
    // playback link, then downloading the file. By the time play() runs the
    // tap is over and Safari refuses it (NotAllowedError, "couldn't play").
    // Playing this element once, silently, before the first await unlocks
    // it: WebKit lifts its user-gesture rule for an element once play() has
    // been called on it during a gesture. Chrome and Firefox already allow
    // play() after any earlier tap on the page, so this changes nothing
    // there. Not checked on a real iPhone.
    if (audio && !audio.src) {
      audio.src = getSilentWavUrl();
      const unlock = audio.play();
      if (unlock?.then) {
        unlock.then(() => { if (audio.src === getSilentWavUrl()) audio.pause(); }).catch(() => {});
      }
    }

    try {
      const url = await getVoiceNoteUrl(reflectionId);
      if (!url) { setState('error'); return; }
      const res = await fetch(url);
      // fetch() resolves on a 403 or 404 too. Without this, Google's XML
      // error page was decoded as audio and the failure surfaced as a
      // confusing decode error, or not at all.
      if (!res.ok) throw new Error(`downloading the recording failed (${res.status})`);
      const arrayBuffer = await res.arrayBuffer();

      if (!peaks) {
        try {
          const AudioCtx = window.AudioContext || window.webkitAudioContext;
          const ctx = new AudioCtx();
          const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
          const channelData = audioBuffer.getChannelData(0);
          const samplesPerBucket = Math.max(1, Math.floor(channelData.length / WAVEFORM_BAR_COUNT));
          const computed = [];
          for (let i = 0; i < WAVEFORM_BAR_COUNT; i++) {
            let max = 0;
            const start = i * samplesPerBucket;
            const end = Math.min(start + samplesPerBucket, channelData.length);
            for (let j = start; j < end; j++) { const abs = Math.abs(channelData[j]); if (abs > max) max = abs; }
            computed.push(max);
          }
          setPeaks(computed);
          ctx.close().catch(() => {});
        } catch (err) {
          // Not fatal -- playback still works without a real waveform.
          console.error('Voice note waveform decode failed:', err.message);
          setPeaks(new Array(WAVEFORM_BAR_COUNT).fill(0.3));
        }
      }

      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const blobUrl = URL.createObjectURL(new Blob([arrayBuffer], { type: 'audio/wav' }));
      blobUrlRef.current = blobUrl;
      if (!audio) return;
      audio.src = blobUrl;
      await audio.play();
      setState('playing');
    } catch (err) {
      console.error('Voice note playback failed:', err.message);
      Sentry.captureException(new Error(`voice note playback: ${err.message}`), {
        tags: { voice_step: 'playback' },
      });
      setState('error');
    }
  }

  const mm = Math.floor((durationSeconds || 0) / 60);
  const ss = Math.round((durationSeconds || 0) % 60).toString().padStart(2, '0');
  const bars = peaks || new Array(WAVEFORM_BAR_COUNT).fill(0.15);

  return (
    <div onClick={ev => ev.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
      {/* Drawn, not typed: an iPhone shows the characters ▶ and ⏸ as its
          own blue emoji buttons, which sat inside this rust circle. */}
      <button onClick={togglePlay} disabled={state === 'loading'}
        aria-label={state === 'playing' ? 'Pause the voice note' : 'Play the voice note'}
        style={{ width: 24, height: 24, borderRadius: '50%',
        border: 'none', background: 'var(--brand-rust)', color: '#fff', fontSize: 10, cursor: state === 'loading' ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }}>
        {state === 'loading' ? '…' : state === 'playing' ? (
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <rect x="1.5" y="1" width="2.4" height="8" rx="0.6" fill="currentColor" />
            <rect x="6.1" y="1" width="2.4" height="8" rx="0.6" fill="currentColor" />
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" style={{ marginLeft: 1 }}>
            <path d="M2 1.2v7.6L8.6 5z" fill="currentColor" />
          </svg>
        )}
      </button>
      {/* The waveform may shrink (and clip) but never push the time out:
          beside the full-size cover on a 320px phone the column is about
          68px, and the time used to sit past the tile's edge. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 16, flex: '0 1 auto', minWidth: 0, overflow: 'hidden' }}>
        {bars.map((p, i) => (
          <div key={i} style={{ width: 2, flexShrink: 0, borderRadius: 1, height: Math.max(2, p * 16),
            background: state === 'playing' && i / WAVEFORM_BAR_COUNT <= progress ? 'var(--brand-rust)' : 'var(--brand-sand)' }} />
        ))}
      </div>
      <span style={{ fontSize: 10.5, color: 'var(--neutral-400)', flexShrink: 0 }}>{mm}:{ss}</span>
      {state === 'error' && <span style={{ fontSize: 10, color: 'var(--brand-rust)' }}>couldn't play</span>}
      <audio ref={audioRef} style={{ display: 'none' }} />
    </div>
  );
}

// Exported (ticket 27): the craving dead-end offer on ReaderHomepage reuses
// this exact composer -- initialBook pre-fills the reader's closest match,
// initialPrompt pre-fills the generic talk prompt. "Add another entry"
// (JournalTileDeck's onAddToBook below) also hands in a book, with the prompt
// used for the first entry on it, or none if that prompt is gone (ticket 91).
// Nothing about the composer itself changes for the craving caller; see
// resolveJournalEntryFacts/insertNewJournalReflection just below JournalBlock's
// handleDone for the save-side half of this reuse.
// describeBook (ticket 94): the entry is about a book Reanlo doesn't have, in the reader's
// own words. It opens on the describe step instead of the book search, saves with the
// reader's title and no book, is always private (so no share step), and has no genre
// ask and no trope box, since both are filed against a book. Editing such an entry
// (editEntry.bookless) works the same way, with its title fixed.
export function JournalEntryFlow({ initialPrompt, initialBook, initialFreeformLabel, describeBook = false, editEntry, prompts, onDone, onClose }) {
  const editing = !!editEntry;
  const describing = !!describeBook || (editing && !!editEntry.bookless);
  const presetPrompts = prompts.filter(p => p.kind === 'preset');
  const freeformPrompt = prompts.find(p => p.kind === 'freeform');
  const talkPrompt = prompts.find(p => p.kind === 'talk');

  const [step, setStep] = useState(editing ? 'write' : describeBook ? (initialPrompt ? 'describe' : 'prompt') : (initialBook ? (initialPrompt ? 'write' : 'prompt') : (initialPrompt ? 'book' : 'prompt')));
  const [selectedPrompt, setSelectedPrompt] = useState(editing ? editEntry.prompt : (initialPrompt || null));
  const [freeformLabel, setFreeformLabel] = useState(editing && editEntry.prompt?.kind === 'freeform' ? editEntry.prompt.label : (initialFreeformLabel || ''));
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [outsideFailed, setOutsideFailed] = useState(false);
  const [selectedBook, setSelectedBook] = useState(editing ? (editEntry.bookless ? null : editEntry.book) : (initialBook || null));
  const [entryTitle, setEntryTitle] = useState(editing ? (editEntry.entryTitle || '') : '');
  const titleOk = !entryTitleProblem(entryTitle);
  const [bookGenres, setBookGenres] = useState(null); // null = still resolving
  const [reflectionText, setReflectionText] = useState(editing ? editEntry.reflection : '');
  // Real genres/subgenres tables (PR3) in place of the old flat genre
  // string -- see JournalGenreSubgenrePicker's own comment for why genre
  // stays single-select here while subgenre stays multi-select/optional.
  const { genres: journalGenres, subgenresById: journalSubgenresById,
    canonSubgenresForGenre: journalCanonSubgenresForGenre, matchIndex: journalMatchIndex,
    upsertPendingSubgenre: journalUpsertPendingSubgenre,
    loading: journalGenreTaxonomyLoading, error: journalGenreTaxonomyError } = useGenreTaxonomy();
  const [selectedGenreIds, setSelectedGenreIds] = useState([]);
  const [selectedSubgenreIds, setSelectedSubgenreIds] = useState([]);
  const [subgenreNamesById, setSubgenreNamesById] = useState({});
  const [tropes, setTropes] = useState([]);
  const [moreOpen, setMoreOpen] = useState(editing && (editEntry.comps || []).length > 0);
  const [comps, setComps] = useState(editing ? (editEntry.comps || []) : []);
  const [saving, setSaving] = useState(false);
  const [voiceNote, setVoiceNote] = useState(() => (editing && editEntry.audioUrl) ? {
    objectPath: editEntry.audioUrl, durationSeconds: editEntry.durationSeconds, transcript: editEntry.transcript,
    status: 'ready', origin: 'existing',
  } : { objectPath: null, durationSeconds: null, transcript: null, status: 'idle', origin: null });
  // The one place a fresh ('session') recording is thrown away for being
  // abandoned: when the entry closes without saving. Not when a step
  // changes -- VoiceNoteRecorder comes and goes with the write step, and
  // owned this cleanup until it turned out to delete every recording on
  // the way to the share step. savedRef is set by finish() around onDone:
  // a successful save closes this flow from inside onDone, so the flag has
  // to be true before that happens and is cleared again if onDone reports
  // it did not save (the entry stays open, still holding the recording,
  // for the reader to retry).
  const voiceNoteRef = useRef(voiceNote);
  useEffect(() => { voiceNoteRef.current = voiceNote; }, [voiceNote]);
  const flowOpenRef = useRef(true);
  const savedRef = useRef(false);
  useEffect(() => {
    flowOpenRef.current = true;
    return () => {
      flowOpenRef.current = false;
      const current = voiceNoteRef.current;
      if (!savedRef.current && current.origin === 'session' && current.objectPath) {
        deleteOrphanedUpload(current.objectPath);
      }
    };
  }, []);

  const debouncedQuery = useDebounce(searchQuery, 400);
  const searchIdRef = useRef(0);

  useEffect(() => {
    if (step !== 'book') return;
    const myId = ++searchIdRef.current;
    searchBooks(
      debouncedQuery,
      (r) => { if (searchIdRef.current === myId) setSearchResults(r); },
      (v) => { if (searchIdRef.current === myId) setSearching(v); },
      (f) => { if (searchIdRef.current === myId) setOutsideFailed(f); }
    );
  }, [debouncedQuery, step]);

  // Resolve whether the chosen book already has a genre, so the picker only
  // shows up when it actually needs an answer (2.4).
  //
  // Reads book_genres, not books.genres and (since PR5, docs/genre-
  // subgenre-build-spec.md) not book_tags either. Originally this read
  // book_tags (tag_type: 'genre'), which is what the author portal's genre
  // picker wrote at the time; PR5 stopped that write, so a well-tagged
  // author book would look genre-less here again -- the exact bug this
  // comment used to describe about books.genres, recurring one table later
  // -- unless this switches to book_genres, which is what actually still
  // gets written. bookGenres now holds book_genres rows; only its .length
  // is read below.
  useEffect(() => {
    let cancelled = false;
    // Reset to null the moment the book changes, synchronously, before the
    // query below even starts. Without this, switching books left the PREVIOUS
    // book's resolved bookGenres in place until the new query returned --
    // so a book that needs a genre could inherit "not needed" from whatever
    // was selected before it, for the whole gap.
    setBookGenres(null);
    async function resolve() {
      if (!selectedBook) return;
      if (!selectedBook.id) { setBookGenres([]); return; }
      const { data, error } = await supabase.from('book_genres').select('id').eq('book_id', selectedBook.id);
      if (cancelled) return;
      if (error) { console.error('Journal genre lookup failed:', error.message); setBookGenres([]); return; }
      setBookGenres(data || []);
    }
    resolve();
    return () => { cancelled = true; };
  }, [selectedBook]);

  // Two books are the same book when they share a Reanlo id; an outside
  // result has id: null (searchBooks above), so those fall back to title +
  // author, which is the pair findOrCreateBook matches on as well.
  function sameBook(a, b) {
    if (!a || !b) return false;
    if (a.id && b.id) return String(a.id) === String(b.id);
    return (a.title || '').toLowerCase() === (b.title || '').toLowerCase()
        && (a.author_name || '').toLowerCase() === (b.author_name || '').toLowerCase();
  }

  function chooseBook(b) {
    // Picking a DIFFERENT book drops what was said about the previous one.
    // genre and tropes are statements about a book, and finish() sends both
    // against whatever selectedBook ends up being -- so carrying them across
    // would file the old book's answers on the new one. genre is the one that
    // actually lands wrong: finish() discards it when the new book already
    // has a genre (genreNeeded is false), but when the new book needs one it
    // would submit the previous book's answer unchallenged, and the picker
    // would not even be shown as empty for the reader to notice.
    //
    // The reflection and any voice note are the reader's own words and stay.
    // Realising you tapped the wrong book should not cost you what you have
    // already written -- that is the entire point of being able to go back.
    if (!sameBook(selectedBook, b)) {
      setSelectedGenreIds([]);
      setSelectedSubgenreIds([]);
      setTropes([]);
      setComps([]);
      setMoreOpen(false);
    }
    setSelectedBook(b);
    setStep('write');
  }

  // Reused by every prompt-step control. When a book is already chosen
  // (arriving via "add another entry" for a book already journaled), picking
  // a prompt goes straight back to writing instead of asking to search again.
  function choosePrompt(p) {
    setSelectedPrompt(p);
    setStep(stepAfterPrompt());
  }
  // Where choosing a prompt leads: to the book (or, with one already chosen, to writing);
  // with no book to find (ticket 94), to the describe box, or to writing once it has a title.
  function stepAfterPrompt() {
    if (describing) return titleOk ? 'write' : 'describe';
    return selectedBook ? 'write' : 'book';
  }

  // Which step the back arrow returns to, or null for "no arrow". One rule in
  // one place, rather than a link per step saying the same thing differently.
  //
  //   write -> book    so a mis-tapped book can be changed...
  //   write -> prompt  ...EXCEPT when the book was handed in from outside.
  //                    initialBook with a prompt starts the flow AT 'write', and
  //                    with none at 'prompt' (see the useState above), so 'book'
  //                    is a step this reader never visited,
  //                    and the book is not theirs to swap -- they got here from
  //                    "add another entry" on a book they already journaled
  //                    (JournalTileDeck's onAddToBook). What they CAN
  //                    change is the prompt, which is what the old
  //                    `!editing && initialBook` "change prompt" link did, and
  //                    choosePrompt sends them straight back to 'write' with
  //                    the book intact. The arrow replaces that link exactly.
  //   book  -> prompt  ONLY when the prompt was not handed in from outside:
  //                    arriving with initialPrompt means the caller chose it
  //                    (the prompt tile on the journal block), and the old
  //                    "change prompt" link was hidden in exactly that case.
  //                    The arrow inherits that rule rather than quietly
  //                    widening it.
  //   prompt -> null   first step, nothing behind it
  //
  // The two initial* props are NOT symmetric and that is why the two lines read
  // differently: initialPrompt HIDES the arrow on its step, initialBook REDIRECTS
  // it. That matches what each one means -- a prompt handed in was a choice the
  // reader just made on the tile they tapped, a book handed in is the subject of
  // the entry.
  //
  // Editing is one step by design -- an existing entry belongs to its book and
  // its prompt -- so it never gets an arrow.
  const backTo = editing ? null
    : step === 'write' ? (describing ? 'describe' : initialBook ? 'prompt' : 'book')
    : step === 'describe' ? 'prompt'
    : step === 'book' && !initialPrompt ? 'prompt'
    : null;

  const promptLabel = selectedPrompt?.kind === 'freeform'
    ? (freeformLabel || 'your own prompt')
    : (selectedPrompt?.label || '');
  // null means "don't know yet, still resolving" -- NOT "not needed". Treating
  // it as not-needed is how a reader could save without a genre at all: a fast
  // click landing before the book_tags query above returns made genreNeeded
  // false (bookGenres === null fails the check) and let canSubmit through with
  // no genre picker ever shown. genreResolving below closes that window --
  // submit stays blocked, fail-closed, until we actually know the answer.
  const genreResolving = !!selectedBook && bookGenres === null;
  const genreNeeded = bookGenres !== null && bookGenres.length === 0;
  const canSubmit = (describing ? titleOk : !!selectedBook) && !!selectedPrompt
    && (selectedPrompt.kind !== 'freeform' || freeformLabel.trim())
    && !genreResolving
    && (!genreNeeded || selectedGenreIds.length > 0)
    && !!reflectionText.trim()
    && voiceNote.status !== 'uploading' && voiceNote.status !== 'transcribing';

  async function finish(sharedChoice) {
    if (!canSubmit || saving) return;
    setSaving(true);
    // journalGenreName/journalSubgenreNames below are bare names -- kept on
    // the payload for onDone's own display/history use, even though the
    // actual book_genres/book_subgenres write (PR5, genreId/subgenreIds
    // further down) goes by id and never re-resolves these back. tropes
    // still write by name (insertReaderTropes), resolving the id itself.
    // subgenreNamesById covers write-ins, whose id isn't in
    // journalSubgenresById until a curator promotes them.
    const journalGenreName = selectedGenreIds.length
      ? (journalGenres.find(g => g.id === selectedGenreIds[0])?.name || '')
      : '';
    const journalSubgenreNames = selectedSubgenreIds
      .map(id => journalSubgenresById.get(id)?.name || subgenreNamesById[id]?.name)
      .filter(Boolean);
    // onDone returns true only once the entry is saved. Set savedRef first:
    // a successful save closes this flow from inside onDone, and the
    // unmount cleanup above must already know not to delete the recording
    // the new row points at.
    savedRef.current = true;
    let saved = false;
    try {
      saved = (await onDone({
      book: describing ? null : selectedBook,
      // The reader's own title, for an entry with no book. null for every other entry.
      entryTitle: describing ? cleanEntryTitle(entryTitle) : null,
      prompt: selectedPrompt,
      promptLabel,
      reflection: reflectionText.trim(),
      genre: genreNeeded ? journalGenreName.trim() : null,
      // Always optional, even while genre is required -- never blocks canSubmit.
      subgenres: genreNeeded ? journalSubgenreNames : [],
      // Raw ids, alongside the names above -- handleDone's book_genres/
      // book_subgenres write (PR3 Finding 3) uses these directly instead
      // of re-resolving the names it already has back to ids.
      genreId: genreNeeded && selectedGenreIds.length ? selectedGenreIds[0] : null,
      subgenreIds: genreNeeded ? selectedSubgenreIds : [],
      tropes,
      comps,
      shared: sharedChoice,
      voiceNote,
    }, editing, editEntry)) === true;
    } finally {
      if (!saved) savedRef.current = false;
    }
    setSaving(false);
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(41,37,36,.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--brand-cream)', borderRadius: 22, width: '100%',
        maxWidth: 640, maxHeight: '92vh', overflowY: 'auto', padding: '26px 28px', position: 'relative',
        boxShadow: '10px 12px 0 rgba(41,37,36,.16)', boxSizing: 'border-box' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 16, width: 32, height: 32,
          borderRadius: '50%', border: '1.5px solid rgba(139,115,85,.3)', background: '#fff', color: 'var(--neutral-500)',
          fontSize: 14, cursor: 'pointer' }}>✕</button>

        {/* Back, mirroring the ✕ exactly: same 32px circle, same border and
            background, opposite corner. A 32px target is tappable on a phone;
            a text link at this size is not, which is the whole reason this is
            a button and not a word.
            Only rendered when there IS a previous step (backTo), so the first
            step of a flow and an edit -- which has one step -- show nothing
            and the corner stays empty. */}
        {backTo && (
          <button onClick={() => setStep(backTo)} aria-label="Back"
            style={{ position: 'absolute', top: 14, left: 16, width: 32, height: 32,
              borderRadius: '50%', border: '1.5px solid rgba(139,115,85,.3)', background: '#fff',
              color: 'var(--neutral-500)', fontSize: 15, lineHeight: 1, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
        )}

        {step === 'prompt' && <>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--neutral-800)' }}>a book that I…</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
            {presetPrompts.map(p => (
              <button key={p.id} onClick={() => choosePrompt(p)} style={{
                border: `1.5px solid ${p.color}44`, background: '#fff', color: 'var(--neutral-800)', borderRadius: 999,
                padding: '7px 13px', fontSize: 12, cursor: 'pointer' }}>{p.emoji} {p.label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={freeformLabel} onChange={e => { setFreeformLabel(e.target.value); setSelectedPrompt(freeformPrompt); }}
              placeholder="type your own…" style={{ flex: 1, minWidth: 180, padding: '10px 14px',
                borderRadius: 999, border: '1.5px dashed var(--brand-sand)', fontSize: 12.5, outline: 'none', boxSizing: 'border-box' }} />
            {talkPrompt && <button onClick={() => choosePrompt(talkPrompt)} style={{ border: 'none',
              background: 'var(--brand-forest)', color: '#fff', borderRadius: 999, padding: '10px 16px', fontSize: 12,
              fontWeight: 700, cursor: 'pointer' }}>I just want to talk about a book</button>}
            {selectedPrompt?.id === freeformPrompt?.id && freeformLabel.trim() && (
              <button onClick={() => setStep(stepAfterPrompt())} style={{ border: 'none', background: 'var(--brand-rust)', color: '#fff',
                borderRadius: 999, padding: '10px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>use mine →</button>
            )}
          </div>
        </>}

        {step === 'describe' && <>
          {/* Ticket 94: the step that stands in for "Which book?" when the reader describes
              the book in their own words. Same padding rules as that step: paddingTop clears
              the back arrow, paddingRight clears the X. The arrow goes back to the prompts,
              so the prompt this step opened on can still be changed. */}
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--neutral-800)',
            paddingRight: 40, paddingTop: backTo ? 24 : 0 }}>Describe the book</div>
          <div style={{ fontSize: 12, color: 'var(--brand-rust)', fontWeight: 700, marginTop: 4 }}>
            {selectedPrompt?.emoji} {promptLabel}
          </div>
          {/* A one-line box, so a line break can't get into the title. The limit is counted in
              characters, not the input's maxLength (UTF-16 units), so an emoji is one. */}
          <input autoFocus value={entryTitle} aria-label="Describe the book"
            onChange={e => setEntryTitle(limitEntryTitle(e.target.value.replace(/[\r\n]+/g, ' ')))}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (titleOk) setStep('write'); } }}
            placeholder="e.g. alien book with mommy issues and a sad ending"
            style={{ width: '100%', marginTop: 12, padding: '12px 15px', borderRadius: 12,
              border: '1.5px solid var(--brand-sand)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 6, fontSize: 11.5, color: 'var(--neutral-500)' }}>
            <span>{"in your own words. it becomes this entry's title, and only you see it."}</span>
            <span style={{ flexShrink: 0 }}>{entryTitleLength(entryTitle)}/{ENTRY_TITLE_MAX}</span>
          </div>
          <button disabled={!titleOk} onClick={() => setStep('write')} style={{
            width: '100%', marginTop: 20, background: titleOk ? 'var(--brand-rust)' : 'var(--brand-sand)',
            color: titleOk ? '#fff' : 'var(--neutral-400)', border: 'none', borderRadius: 13, padding: 13, fontSize: 14,
            fontWeight: 800, cursor: titleOk ? 'pointer' : 'not-allowed' }}>
            continue
          </button>
        </>}

        {step === 'book' && <>
          {/* paddingTop clears the back arrow, same as the write step, and
              only when there is one -- arriving here with a preselected
              prompt gives no arrow and no gap. paddingRight clears the ✕,
              which this heading already ran under at narrow widths. */}
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--neutral-800)',
            paddingRight: 40, paddingTop: backTo ? 24 : 0 }}>Which book?</div>
          {/* The "change prompt" link that sat here is gone for the same
              reason as the ones on the write step: 11px underlined text is not
              a tap target. The arrow covers it, and covers the case this step
              never had -- arriving from the write step and wanting to keep the
              book after all. */}
          <div style={{ fontSize: 12, color: 'var(--brand-rust)', fontWeight: 700, marginTop: 4 }}>
            {selectedPrompt?.emoji} {promptLabel}
          </div>
          <input autoFocus value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="search by title or author"
            style={{ width: '100%', marginTop: 12, padding: '12px 15px', borderRadius: 12,
              border: '1.5px solid var(--brand-sand)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          {!searchQuery.trim() && (
            <div style={{ marginTop: 16, fontSize: 12.5, color: 'var(--neutral-400)', textAlign: 'center', padding: '18px 0' }}>
              start typing. your Reanlo shelves come first, then the wider catalog.
            </div>
          )}
          {searching && (
            <div style={{ fontSize: 12, color: 'var(--neutral-400)', textAlign: 'center', padding: '10px 0' }}>Searching…</div>
          )}
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {searchResults.map((b, i) => (
              <button key={i} onClick={() => chooseBook(b)} style={{ display: 'flex', gap: 12,
                alignItems: 'center', background: '#fff', border: '1.5px solid var(--brand-sand)', borderRadius: 14,
                padding: '10px 12px', cursor: 'pointer', textAlign: 'left' }}>
                <JournalCover book={b} w={38} h={56} tilt={0} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14.5, color: 'var(--neutral-800)' }}>{b.title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--neutral-500)' }}>{b.author_name}{publicationYear(b) ? ` · ${publicationYear(b)}` : ''}</div>
                </div>
                <SourceBadge source={b.source || 'openlibrary'} />
              </button>
            ))}
          </div>
          {!searching && searchQuery.trim() && searchResults.length === 0 && (
            <p style={{ fontSize: 12.5, color: outsideFailed ? 'var(--brand-rust)' : 'var(--neutral-400)', textAlign: 'center', padding: '16px 0' }}>
              {outsideFailed ? SEARCH_FAILED_MESSAGE : 'No results found'}
            </p>
          )}
        </>}

        {step === 'write' && <>
          {/* Both corner buttons are absolutely positioned over this row:
              ✕ at top 14 / right 16, and the back arrow at top 14 / left 16,
              each 32px, so together they occupy y 14-46 while the modal's own
              padding starts the content at y 26.

              paddingRight clears the ✕ -- pre-existing, invisible on desktop
              where titles are short, but at 375px a long title wrapped
              underneath it.

              paddingTop clears the arrow, and only when there is one: the
              cover starts at the left edge of this row and would otherwise
              sit under it. Nothing is added on a step with no arrow, so the
              flow does not grow a gap it does not need. */}
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', paddingRight: 40, paddingTop: backTo ? 24 : 0 }}>
            {describing ? <NoBookCover w={48} h={70} /> : <JournalCover book={selectedBook} w={48} h={70} tilt={-2} />}
            <div style={{ minWidth: 0, flex: describing && editing ? 1 : undefined }}>
              {/* Reopening a described entry: the title is the same one-line box as the describe
                  step, with its count, its approved line under it and the same rules (ticket 94,
                  Reader's Corner spec 5.11). The line stays grey; a blank title turns the border
                  rust, and "save changes" and the hint under it say why it is off. Enter does
                  nothing here: there is no next step to go to. */}
              {describing && editing ? <>
                <input value={entryTitle} aria-label="Describe the book"
                  onChange={e => setEntryTitle(limitEntryTitle(e.target.value.replace(/[\r\n]+/g, ' ')))}
                  onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                  placeholder="e.g. alien book with mommy issues and a sad ending"
                  style={{ width: '100%', padding: '8px 11px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                    border: `1.5px solid ${titleOk ? 'var(--brand-sand)' : 'var(--brand-rust)'}`, outline: 'none', boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 4, fontSize: 11, color: 'var(--neutral-500)' }}>
                  <span>{"in your own words. it becomes this entry's title, and only you see it."}</span>
                  <span style={{ flexShrink: 0 }}>{entryTitleLength(entryTitle)}/{ENTRY_TITLE_MAX}</span>
                </div>
              </> : (
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--neutral-800)' }}>{describing ? cleanEntryTitle(entryTitle) : selectedBook.title}</div>
              )}
              {/* No "change book" / "change prompt" links here any more. Both
                  were 10.5px underlined text -- too small to tap reliably on a
                  phone, and the reader who needs them is the one who just
                  mis-tapped something. The back arrow does the same job at a
                  size a thumb can hit. */}
              <div style={{ fontSize: 12, color: 'var(--brand-rust)', fontWeight: 700, marginTop: 1 }}>
                {selectedPrompt?.emoji} {promptLabel}
              </div>
              {editing && <div style={{ fontSize: 10.5, color: 'var(--neutral-400)' }}>editing this entry</div>}
            </div>
          </div>

          <div style={{ marginTop: 16, background: '#fff', border: '1.5px solid var(--brand-sand)', borderRadius: 16, padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 10, letterSpacing: '.16em', color: 'var(--neutral-500)', fontWeight: 700 }}>
                YOUR REFLECTION (private by default)
              </span>
              <JournalPrivTip />
            </div>
            <textarea value={reflectionText} onChange={e => setReflectionText(e.target.value)} rows={4}
              placeholder="say it or type it. this stays off your public profile." maxLength={2000}
              style={{ width: '100%', marginTop: 8, border: 'none', outline: 'none', resize: 'none',
                background: 'transparent', fontFamily: 'var(--font-display)', fontSize: 14.5, lineHeight: 1.6,
                color: 'var(--neutral-800)', boxSizing: 'border-box' }} />
            <VoiceNoteRecorder voiceNote={voiceNote} setVoiceNote={setVoiceNote} reflectionText={reflectionText} setReflectionText={setReflectionText} flowOpenRef={flowOpenRef} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--neutral-400)', marginTop: 8, lineHeight: 1.55, fontStyle: 'italic' }}>
            what did it make you feel? what does this remind you of? who would you hand it to? was the character what you expected? did the ending satisfy you?
          </div>

          {/* Ticket 27 bullet 3: "a reader pointing at a catalogued book is
              usually asked for nothing, and the screen should say so rather
              than leave them braced for a form." This line is the "say so" --
              shown as soon as we actually know the answer (genreResolving
              gates it the same way it gates canSubmit, for the same reason:
              a fast render before book_genres resolves must not claim
              "nothing needed" for a book that turns out to need one). */}
          {!describing && !genreResolving && !genreNeeded && (
            <div style={{ marginTop: 14, fontSize: 11.5, color: 'var(--neutral-500)', fontStyle: 'italic' }}>
              already catalogued with a genre, nothing else needed here, just your reflection.
            </div>
          )}

          {genreNeeded && (
            <div style={{ marginTop: 16 }}>
              {/* Copy per ticket 27 bullet 3, verbatim: "help us find it next
                  time" -- the book has no genre on file, and this pick is
                  what lets the NEXT reader's craving find it. */}
              <span style={{ fontSize: 10, letterSpacing: '.16em', color: 'var(--brand-rust)', fontWeight: 700 }}>HELP US FIND IT NEXT TIME</span>
              <div style={{ fontSize: 11, color: 'var(--neutral-500)', marginTop: 2 }}>this book has no genre on file yet, pick the closest one (required).</div>
              <div style={{ marginTop: 8 }}>
                <JournalGenreSubgenrePicker
                  genres={journalGenres}
                  canonSubgenresForGenre={journalCanonSubgenresForGenre}
                  matchIndex={journalMatchIndex}
                  upsertPendingSubgenre={journalUpsertPendingSubgenre}
                  selectedGenreIds={selectedGenreIds}
                  setSelectedGenreIds={setSelectedGenreIds}
                  selectedSubgenreIds={selectedSubgenreIds}
                  setSelectedSubgenreIds={setSelectedSubgenreIds}
                  subgenreNamesById={subgenreNamesById}
                  setSubgenreNamesById={setSubgenreNamesById}
                  loading={journalGenreTaxonomyLoading}
                  error={journalGenreTaxonomyError}
                />
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button onClick={() => setMoreOpen(o => !o)} style={{ background: 'none', border: 'none',
              color: 'var(--brand-rust)', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
              {moreOpen ? 'optional detail ▲' : 'add more detail (optional) ▼'}
            </button>
            {moreOpen && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {/* Tropes are filed on the entry's book, and a described entry has none (ticket 94). */}
                {!describing && <JournalTropeBox tropes={tropes} setTropes={setTropes} />}
                <CompTitlesPicker comps={comps} setComps={setComps} />
              </div>
            )}
          </div>

          {/* A described entry is always private, so there is nothing to choose: it saves from here, on a new entry and on an edit. */}
          <button disabled={!canSubmit || saving} onClick={() => (describing ? finish(false) : setStep('share'))} style={{
            width: '100%', marginTop: 20, background: canSubmit ? 'var(--brand-rust)' : 'var(--brand-sand)',
            color: canSubmit ? '#fff' : 'var(--neutral-400)', border: 'none', borderRadius: 13, padding: 13, fontSize: 14,
            fontWeight: 800, cursor: canSubmit && !saving ? 'pointer' : 'not-allowed' }}>
            {saving ? 'saving…' : describing ? (editing ? 'save changes' : 'save the entry') : editing ? 'continue' : 'save the entry'}
          </button>
          {!canSubmit && <div style={{ fontSize: 10.5, color: 'var(--neutral-400)', marginTop: 6, textAlign: 'center' }}>
            {genreResolving ? 'checking this book\u2019s genre\u2026' : `${describing ? 'a title and a reflection are' : genreNeeded ? 'a book, a genre, and a reflection are' : 'a book and a reflection are'} all that's required`}
          </div>}
        </>}

        {step === 'share' && <>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--neutral-800)' }}>Share this one to the community?</div>
          <p style={{ fontSize: 12.5, color: 'var(--neutral-500)', marginTop: 6 }}>
            {editing
              ? <>Currently <strong>{editEntry.shared ? 'shared' : 'private'}</strong>. Pick again below, or leave it as is, this is exactly what would appear, attributed to you:</>
              : 'Totally optional. This is exactly what would appear, attributed to you:'}
          </p>
          <div style={{ marginTop: 12, background: '#fff', border: '1.5px solid var(--brand-sand)', borderRadius: 16,
            padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center' }}>
            <JournalCover book={selectedBook} w={44} h={64} tilt={-2} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--brand-rust)', fontWeight: 700 }}>{selectedPrompt?.emoji} {promptLabel}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--neutral-800)' }}>{selectedBook.title}</div>
              <div style={{ fontSize: 11, color: 'var(--neutral-500)' }}>{selectedBook.author_name}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={() => finish(false)} disabled={saving} style={{ flex: 1.2, background: 'var(--brand-forest)', color: '#fff',
              border: editing && !editEntry.shared ? '2px solid var(--brand-gold)' : 'none', borderRadius: 13, padding: 12, fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
              keep it private
            </button>
            <button onClick={() => finish(true)} disabled={saving} style={{ flex: 1, background: '#fff', color: 'var(--brand-rust)',
              border: editing && editEntry.shared ? '2px solid var(--brand-gold)' : '1.5px solid var(--brand-rust)', borderRadius: 13, padding: 12, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              share it
            </button>
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--neutral-400)', marginTop: 10, textAlign: 'left', lineHeight: 1.6 }}>
            <div><strong style={{ color: 'var(--brand-forest)' }}>private</strong> — stays off Reader's Corner entirely. helps match books behind the scenes, always unattributed, never shown with your name.</div>
            <div style={{ marginTop: 6 }}><strong style={{ color: 'var(--brand-rust)' }}>shared</strong>: your book & the prompt show on your profile. your written words stay private.</div>
            <div style={{ marginTop: 6 }}>you can switch an entry back to private any time by opening it.</div>
          </div>
        </>}
      </div>
    </div>
  );
}

// ── the tile deck: quote-card tiles, browsed with the arrows under the
// tile or a finger swipe, one book's sittings reachable through the
// "N entries for this book" pill and its own "history" modal. A tile with a
// voice note plays it (VoiceNotePlayback).
// The journal's fold arrow: a chevron pointing down while it is open, turned
// to point right once it is folded, the usual reading of the two states.
function JournalCollapseToggle({ collapsed, onToggle }) {
  return (
    <button onClick={onToggle} aria-expanded={!collapsed}
      aria-label={collapsed ? 'Show the journal' : 'Fold the journal away'}
      title={collapsed ? 'Show' : 'Fold away'}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24,
        padding: 0, border: 'none', borderRadius: 'var(--radius-circle)', background: 'transparent',
        color: 'var(--neutral-500)', cursor: 'pointer' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.15s' }}>
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );
}

// Below this deck width the tile's padding, gap and quote size come down.
// A phone's deck is about 326px; the narrowest desktop deck, in a 768px
// window, is about 392.
const JOURNAL_TILE_NARROW_PX = 480;

function JournalTileDeck({ entries, onEdit, onAdd, onDelete, onAddToBook, canAddEntry, handle, collapseToggle = null }) {
  const [idx, setIdx] = useState(0);
  const [filter, setFilter] = useState(null);
  const [history, setHistory] = useState(false);
  // If the active filter's last remaining entry just got deleted, fall back
  // to showing everything rather than rendering an empty deck -- entries is
  // never empty here (the parent swaps to the empty state itself), so this
  // guarantees visible never is either.
  const promptLabels = [...new Set(entries.map(x => x.prompt.label))];
  const filtered = filter ? entries.filter(x => x.prompt.label === filter) : entries;
  const visible = filtered.length ? filtered : entries;
  useEffect(() => {
    if (filter && !entries.some(x => x.prompt.label === filter)) setFilter(null);
  }, [entries, filter]);
  // at, not idx, is the entry on screen: idx can point past the end for a
  // moment after a delete, and "entry 4 of 3" is what reading idx printed.
  const at = Math.min(idx, visible.length - 1);
  const e = visible[at];
  const go = (d) => { setHistory(false); setIdx(i => Math.min(visible.length - 1, Math.max(0, Math.min(i, visible.length - 1) + d))); };
  // An entry with no book stands on its own: matching on book_id would make every one of them a single book.
  const bookEntries = visible.map((x, i) => [x, i]).filter(([x]) => sameBookEntry(x, e));
  // A finger swipe on the tile does what the arrows do, as on Hot Take and
  // Currently Reading. Off while the history list is open over it.
  const swipe = useSwipe({ onNext: () => go(1), onPrev: () => go(-1), enabled: visible.length > 1 && !history });

  // The tile's width decides its padding, gap and quote size. On a phone
  // the deck is about 326px wide. Measured before paint, so the wide
  // spacing never flashes first.
  const deckRef = useRef(null);
  const [deckW, setDeckW] = useState(0);
  useLayoutEffect(() => {
    const el = deckRef.current;
    if (!el) return undefined;
    const measure = () => setDeckW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const narrow = deckW > 0 && deckW < JOURNAL_TILE_NARROW_PX;
  // The cover stays 140 x 200 at every width: shrinking it on a phone was
  // tried and turned down. On a narrow deck only the padding, the gap and
  // the quote's size come down.
  const coverW = 140;
  const coverH = 200;

  // onDelete resolves to true only once the row is actually gone (it may
  // show its own confirm and can be cancelled) -- only then is it safe to
  // close the history modal a delete was triggered from.
  async function handleDeleteClick(entry, fromHistory) {
    const ok = await onDelete(entry);
    if (ok && fromHistory) setHistory(false);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ fontSize: 'var(--font-size-md)' }}>📓</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase', marginRight: 6 }}>A Book That...</span>
        {collapseToggle}
        {/* Only when there is something to filter: with every entry under
            one prompt, "all" and that prompt show the same tiles. */}
        {promptLabels.length > 1 && <>
          <span style={{ fontSize: 9.5, letterSpacing: '.16em', fontWeight: 800, color: 'var(--neutral-400)', marginLeft: 10 }}>FILTER</span>
          <button onClick={() => { setFilter(null); setIdx(0); }} style={{ border: `1.5px solid ${!filter ? 'var(--brand-forest)' : 'var(--brand-sand)'}`,
            background: !filter ? 'var(--brand-forest)' : '#fff', color: !filter ? '#fff' : 'var(--neutral-800)', borderRadius: 999,
            padding: '4px 12px', fontSize: 11, cursor: 'pointer' }}>all · {entries.length}</button>
          {promptLabels.slice(0, 6).map(pl => (
            <button key={pl} onClick={() => { setFilter(pl); setIdx(0); }} style={{
              border: `1.5px solid ${filter === pl ? 'var(--brand-forest)' : 'var(--brand-sand)'}`, background: filter === pl ? 'var(--brand-forest)' : '#fff',
              color: filter === pl ? '#fff' : 'var(--neutral-800)', borderRadius: 999, padding: '4px 12px', fontSize: 11,
              cursor: 'pointer' }}>{pl.replace('a book that ', '')}</button>
          ))}
        </>}
        <button onClick={onAdd} style={{ marginLeft: 'auto', background: 'var(--brand-rust)', color: '#fff', border: 'none',
          borderRadius: 999, padding: '8px 18px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>+ new entry</button>
      </div>
      <div ref={deckRef} style={{ width: '100%', position: 'relative' }}>
        <div {...swipe} style={{ position: 'relative', width: '100%' }}>
          {bookEntries.length > 1 && <>
            <div style={{ position: 'absolute', inset: 0, background: '#fff', borderRadius: 8,
              transform: 'rotate(-1.6deg) translate(-5px,4px)', boxShadow: '6px 8px 0 rgba(41,37,36,.08)' }} />
            <div style={{ position: 'absolute', inset: 0, background: '#FDFAF3', borderRadius: 8,
              transform: 'rotate(1.2deg) translate(4px,2px)', boxShadow: '6px 8px 0 rgba(41,37,36,.08)' }} />
          </>}
          {/* Nothing sits on top of the tile any more. The delete button
              used to be pinned to its top-right corner, over the prompt, and
              the arrows over the cover and the title. Delete is in the
              tile's bottom row now, beside the date, and the arrows are
              under the tile. */}
          <div onClick={() => onEdit(e)} title="open & add to it" style={{ position: 'relative',
            background: 'var(--brand-cream)', borderRadius: 8, cursor: 'pointer',
            overflow: 'hidden', boxShadow: '10px 12px 0 rgba(41,37,36,.12)',
            backgroundImage: 'radial-gradient(rgba(139,115,85,.07) 1px, transparent 1px)', backgroundSize: '7px 7px' }}>
            <div style={{ padding: narrow ? '16px 16px 10px' : 'clamp(16px,5%,32px) clamp(18px,5%,36px) 14px', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', flexDirection: 'row', gap: narrow ? 14 : 'clamp(16px,4%,28px)', alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0 }}>
                  {e.bookless ? <NoBookCover w={coverW} h={coverH} /> : <JournalCover book={e.book} w={coverW} h={coverH} tilt={0} />}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: e.prompt.color }}>
                    {e.prompt.kind === 'talk' ? e.date : `${e.prompt.emoji} ${e.prompt.label}`}
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 700,
                    fontSize: narrow || (e.reflection && e.reflection.length > 90) ? 16 : 19, lineHeight: 1.4, color: 'var(--neutral-800)',
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    <span style={{ color: 'var(--brand-gold)', fontSize: 24, lineHeight: 0, verticalAlign: '-7px', marginRight: 4 }}>&ldquo;</span>
                    {e.pull || e.reflection || 'no reflection was written for this entry.'}
                    <span style={{ color: 'var(--brand-gold)', fontSize: 24, lineHeight: 0, verticalAlign: '-9px', marginLeft: 4 }}>&rdquo;</span>
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--neutral-800)',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{e.book?.title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--neutral-500)' }}>{e.bookless ? 'no book yet \u00b7 in your words' : e.book?.author_name}</div>
                  {e.audio_url
                    ? <VoiceNotePlayback reflectionId={e.id} durationSeconds={e.duration_seconds} />
                    : <div style={{ fontSize: 11, color: 'var(--neutral-400)' }}>no voice note yet</div>}
                  {bookEntries.length > 1 &&
                    <button onClick={(ev) => { ev.stopPropagation(); setHistory(true); }}
                      style={{ marginTop: 3, alignSelf: 'flex-start', border: '1.5px solid var(--brand-gold)', background: 'rgba(212,168,75,.14)',
                        color: '#8a7136', borderRadius: 999, padding: '4px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                      📚 {bookEntries.length} entries for this book ›
                    </button>}
                </div>
              </div>
              {e.bookless && <ShelfSticker narrow={narrow} />}
              {/* The tile's bottom row: who, when, private, and delete. It
                  runs the tile's full width, so on a phone the line fits
                  instead of breaking over two lines beside the cover. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: narrow ? 12 : 16, paddingTop: 8,
                borderTop: '1px dashed rgba(139,115,85,.25)' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: 10.5, color: 'var(--neutral-400)' }}>
                  {/* A talk entry's date is already its heading (above), so
                      it isn't said twice; the history list does the same.
                      Each part stays whole, so on the narrowest phones the
                      line breaks between parts, never inside "🔒 private". */}
                  {[handle && `@${handle}`, e.prompt.kind !== 'talk' && e.date, !e.shared && '🔒 private'].filter(Boolean).map((part, i) => (
                    <React.Fragment key={i}>{i > 0 && ' · '}<span style={{ whiteSpace: 'nowrap' }}>{part}</span></React.Fragment>
                  ))}
                </div>
                <button onClick={(ev) => { ev.stopPropagation(); handleDeleteClick(e, false); }}
                  title="delete this entry" aria-label="Delete this entry" style={{
                  flexShrink: 0, width: 28, height: 28, borderRadius: 'var(--radius-circle)', border: 'none', padding: 0,
                  background: 'transparent', color: 'var(--neutral-400)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6M14 11v6" />
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* The arrows, under the tile and the same as Currently Reading's.
          Only when there is somewhere to go: one entry has no pager. */}
      {visible.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-10)', marginTop: 20 }}>
          <button onClick={() => go(-1)} disabled={at === 0} aria-label="Previous entry"
            style={currentlyReadingArrow(at === 0)}>‹</button>
          <span style={{ fontSize: 11, color: 'var(--neutral-400)', minWidth: 76, textAlign: 'center' }}>entry {at + 1} of {visible.length}</span>
          <button onClick={() => go(1)} disabled={at === visible.length - 1} aria-label="Next entry"
            style={currentlyReadingArrow(at === visible.length - 1)}>›</button>
        </div>
      )}

      {history && (
        <div onClick={() => setHistory(false)} style={{ position: 'fixed', inset: 0, zIndex: 1001, background: 'rgba(41,37,36,.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
          <div onClick={ev => ev.stopPropagation()} style={{ background: 'var(--brand-cream)', borderRadius: 20,
            width: '100%', maxWidth: 480, maxHeight: '86vh', overflowY: 'auto', padding: '22px 24px',
            position: 'relative', boxShadow: '10px 12px 0 rgba(41,37,36,.16)', boxSizing: 'border-box' }}>
            <button onClick={() => setHistory(false)} style={{ position: 'absolute', top: 12, right: 14, width: 30, height: 30,
              borderRadius: '50%', border: '1.5px solid rgba(139,115,85,.3)', background: '#fff', color: 'var(--neutral-500)',
              fontSize: 13, cursor: 'pointer' }}>✕</button>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 19, color: 'var(--neutral-800)' }}>{e.book?.title}</div>
            <div style={{ fontSize: 11.5, color: 'var(--neutral-500)' }}>{e.book?.author_name} · your {bookEntries.length} sittings, newest first</div>
            {canAddEntry && <button onClick={() => { setHistory(false); onAddToBook(e.book); }} style={{ marginTop: 10, border: 'none',
              background: 'var(--brand-rust)', color: '#fff', borderRadius: 999, padding: '7px 14px', fontSize: 11.5,
              fontWeight: 700, cursor: 'pointer' }}>+ add another entry</button>}
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {bookEntries.map(([x, i]) => (
                <div key={x.id} style={{ background: i === at ? 'rgba(212,168,75,.12)' : '#fff',
                  border: `1.5px solid ${i === at ? 'var(--brand-gold)' : 'var(--brand-sand)'}`, borderRadius: 13, padding: '10px 13px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: x.prompt.color }}>
                      {x.prompt.kind === 'talk' ? x.date : `${x.prompt.emoji} ${x.prompt.label}`}
                    </span>
                    <span style={{ fontSize: 10.5, color: 'var(--neutral-400)' }}>{x.prompt.kind === 'talk' ? '' : x.date}{!x.shared && ' · 🔒'}</span>
                  </div>
                  {x.reflection && <div style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 13,
                    color: 'var(--neutral-700)', lineHeight: 1.5, marginTop: 5 }}>
                    "{x.reflection.length > 110 ? x.reflection.slice(0, 110) + '…' : x.reflection}"
                  </div>}
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
                    <button onClick={() => handleDeleteClick(x, true)}
                      style={{ background: 'none', border: 'none', color: 'var(--neutral-400)',
                        fontSize: 11, cursor: 'pointer' }}>delete</button>
                    <button onClick={() => { setIdx(i); setHistory(false); }}
                      style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--brand-rust)',
                        fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>view its tile ›</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--neutral-400)', marginTop: 10 }}>
              every sitting is its own tile. add a new one anytime, this list grows.
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: visible.length > 1 ? 12 : 22 }}>
        <JournalPrivTip />
        <span style={{ fontSize: 11, color: 'var(--neutral-400)' }}>tap the tile to open & add to it</span>
      </div>
    </div>
  );
}


export function JournalBlock({ profile, onUpdate }) {
  const [prompts,     setPrompts]     = useState([]);
  const [reflections, setReflections] = useState([]);
  const [loadedAll,   setLoadedAll]   = useState(false);
  const [books,       setBooks]       = useState({});
  const [loading,     setLoading]     = useState(true);
  const [loadError,   setLoadError]   = useState(false);
  const [flow,        setFlow]        = useState(null); // {p: prompt|null, book?} | {edit: id, editEntry}
  const [toast,       setToast]       = useState(null);
  // Collapsed for THIS visit only, on purpose: plain component state, not
  // storage, so a refresh or coming back to the page opens it again. The
  // journal is the first thing on every profile by decision; a reader can
  // fold it out of the way while they work on the rest, not hide it.
  const [collapsed,   setCollapsed]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [{ data: promptRows, error: promptError }, { data: reflectionRows, error: reflectionError }] = await Promise.all([
        supabase.from('prompts').select('*').eq('is_active', true).order('sort_order'),
        profile?.user_id
          ? supabase.from('reflections').select('*').eq('reader_id', profile.user_id).order('created_at', { ascending: false }).limit(MAX_REFLECTIONS_SHOWN)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (cancelled) return;
      if (promptError || reflectionError) {
        console.error('Journal load failed:', (promptError || reflectionError).message);
        setLoadError(true);
        setLoading(false);
        return;
      }
      setPrompts(promptRows || []);
      const rowList = reflectionRows || [];
      setReflections(rowList);
      setLoadedAll(rowList.length < MAX_REFLECTIONS_SHOWN);
      const bookIds = bookIdsToLookUp(rowList);
      if (bookIds.length) {
        const { data: bookRows, error: bookError } = await supabase.from('books').select('id, title, author_name, cover_image_url').in('id', bookIds);
        if (bookError) console.error('Journal book lookup failed:', bookError.message);
        if (!cancelled) setBooks(Object.fromEntries((bookRows || []).map(b => [b.id, b])));
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [profile?.user_id]);

  function say(t) { setToast(t); setTimeout(() => setToast(null), 1900); }

  // Starts the new entry on the prompt used for the first entry on this book (ticket 91).
  async function addAnotherEntry(book) {
    let first = null;
    if (loadedAll) {
      first = reflections
        .filter(r => r.book_id === book.id)
        .reduce((oldest, r) => (!oldest || new Date(r.created_at) < new Date(oldest.created_at) ? r : oldest), null);
    } else {
      const { data, error } = await supabase.from('reflections').select('*')
        .eq('reader_id', profile.user_id).eq('book_id', book.id)
        .order('created_at', { ascending: true }).limit(1);
      if (error) console.error('Journal first entry lookup failed:', error.message);
      first = data?.[0] || null;
    }
    let p = null;
    let freeformLabel = '';
    if (first?.prompt_kind === 'talk') {
      p = prompts.find(x => x.kind === 'talk') || null;
    } else if (first?.prompt_kind === 'freeform') {
      p = prompts.find(x => x.kind === 'freeform') || null;
      if (p) freeformLabel = first.prompt_label || '';
    } else if (first) {
      p = prompts.find(x => x.id === first.prompt_id) || null;
    }
    setFlow({ p, book, freeformLabel });
  }

  function dismissPrivacyNotice() { onUpdate({ seen_journal_privacy_notice: true }); }

  function openEditor(entry) {
    const book = entry.book;
    setFlow({
      edit: entry.id,
      editEntry: {
        book,
        prompt: { id: entry.raw.prompt_id, kind: entry.raw.prompt_kind, label: entry.raw.prompt_label, emoji: entry.raw.prompt_emoji, color: entry.raw.prompt_color },
        // An entry with no book: the editor works on its title and has no book to ask a genre of (ticket 94).
        bookless: !!entry.bookless,
        entryTitle: entry.raw.entry_title || '',
        reflection: entry.raw.reflection_text || '',
        comps: (entry.raw.comp_titles || []).map(c => ({ book: { id: c.book_id, title: c.title, author_name: c.author_name, cover_image_url: c.cover_image_url }, bridge: c.bridge || '' })),
        shared: entry.raw.visibility === 'shared',
        audioUrl: entry.raw.audio_url || null,
        durationSeconds: entry.raw.duration_seconds || null,
        transcript: entry.raw.transcript || null,
      },
    });
  }

  async function handleDone(payload, isEdit, editEntry) {
    const user = await getCurrentUser();
    if (!user?.id) {
      alert('You need to be signed in to save a reflection. Please refresh and try again.');
      return;
    }

    const facts = await resolveJournalEntryFacts(payload);
    if (!facts) {
      alert('Could not save that book. Please try again.');
      return;
    }
    const { resolvedId, comp_titles, failedWrites } = facts;

    // All three writes resolveJournalEntryFacts attempts are secondary to
    // the entry itself, so none of them blocks the save -- but silently
    // claiming a forced genre, a chosen trope, or a genre-table row "saved"
    // when it didn't is the same shape of bug this project keeps hitting,
    // so whichever failed gets named back to the reader.
    const failureNote = failedWrites.length
      ? ` (but the ${failedWrites.join(' and ')} didn't stick, try setting ${failedWrites.length > 1 ? 'them' : 'it'} again)`
      : '';

    if (isEdit) {
      // 'removed_existing' means the reader explicitly removed the note
      // that was on this entry without recording a replacement -- null out
      // all three columns. Any other origin ('existing', untouched, or
      // 'session', a fresh recording) just writes payload.voiceNote as is.
      const removedExisting = payload.voiceNote?.origin === 'removed_existing';
      const newAudioUrl  = removedExisting ? null : (payload.voiceNote?.objectPath || null);
      const newTranscript = removedExisting ? null : (payload.voiceNote?.transcript || null);
      const newDuration  = removedExisting ? null : (payload.voiceNote?.durationSeconds != null ? Math.round(payload.voiceNote.durationSeconds) : null);

      // embed-reflections only reads pending entries, so changed words go back in its queue.
      const wordsChanged = (editEntry.reflection || '').trim() !== (payload.reflection || '').trim();

      // A described entry (no book) carries its title on the row and the reader can change it
      // here. An entry with a book never gets one: the database refuses a title beside a book
      // (reflections_book_or_title). book_id is never written by an edit.
      const retitle = editEntry.bookless && payload.entryTitle != null
        ? { entry_title: cleanEntryTitle(payload.entryTitle) }
        : {};

      const { error } = await supabase.from('reflections')
        .update({
          reflection_text: payload.reflection || null,
          visibility: payload.shared ? 'shared' : 'private',
          comp_titles,
          audio_url: newAudioUrl,
          transcript: newTranscript,
          duration_seconds: newDuration,
          ...retitle,
          ...(wordsChanged ? { embedding_status: 'pending' } : {}),
        })
        .eq('id', editEntry.id ?? flow.edit);
      if (error) {
        console.error('Journal entry update failed:', error.message);
        // A freshly recorded replacement is NOT deleted here. The entry
        // stays open, still holding it, so the reader can press save again;
        // deleting it here made that retry save a row pointing at a deleted
        // recording. If they give up and close instead, JournalEntryFlow's
        // unmount cleanup deletes it then. The ORIGINAL note (if any) is
        // untouched either way, since the update that would have replaced
        // it never landed.
        alert('Could not save your changes. Please try again.');
        return;
      }

      // The update landed -- only now is it safe to clean up whatever the
      // row USED to point at, if it changed. delete_orphan (via
      // deleteOrphanedUpload), not deleteVoiceNote -- the row already
      // points at the new value (or at nothing), so cleaning up the OLD
      // object must not read the row's audio_url back or touch it again.
      const previousAudioUrl = editEntry.audioUrl;
      if (previousAudioUrl && previousAudioUrl !== newAudioUrl) {
        deleteOrphanedUpload(previousAudioUrl);
      }

      const editId = flow?.edit;
      setReflections(rs => rs.map(r => r.id === editId
        ? { ...r, reflection_text: payload.reflection || null, visibility: payload.shared ? 'shared' : 'private', comp_titles, audio_url: newAudioUrl, transcript: newTranscript, duration_seconds: newDuration, ...retitle, ...(wordsChanged ? { embedding_status: 'pending' } : {}) }
        : r));
      setFlow(null);
      say(`entry updated${failureNote}`);
      return true;
    }

    const { inserted, error } = await insertNewJournalReflection(user.id, payload, resolvedId, comp_titles);
    if (error) {
      alert('Could not save your reflection. Please try again.');
      return;
    }

    setReflections(rs => [inserted, ...rs]);
    if (resolvedId != null) setBooks(prev => ({ ...prev, [resolvedId]: prev[resolvedId] || { id: resolvedId, title: payload.book.title, author_name: payload.book.author_name, cover_image_url: payload.book.cover_image_url } }));
    if (!profile.seen_journal_privacy_notice) dismissPrivacyNotice();
    setFlow(null);
    say((payload.shared ? 'entry saved & shared to the community' : 'entry saved. reflection stays private.') + failureNote);
    return true;
  }

  // Hard requirement from #162 carried forward: a misfiled or regretted
  // entry needs a real way out, not just an edit that can't change the book.
  async function deleteEntry(entry) {
    if (!window.confirm("Delete this journal entry? This can't be undone.")) return false;

    // Object before row (ticket 6b, spec 9.10 event 1) -- so a failure
    // between the two never leaves a reflections row gone while its
    // recording lingers in the bucket with nothing left to ever clean it
    // up. Only called when this entry actually has a stored note; most
    // entries don't, and deleteVoiceNote's own edge-function call is
    // idempotent either way.
    if (entry.audio_url) {
      try {
        await deleteVoiceNote(entry.id);
      } catch (err) {
        console.error('Voice note delete failed for entry', entry.id, err.message);
        alert("Could not delete this entry's recording. Please try again.");
        return false;
      }
    }

    const { error } = await supabase.from('reflections').delete().eq('id', entry.id);
    if (error) {
      console.error('Journal entry delete failed:', error.message);
      alert('Could not delete this entry. Please try again.');
      return false;
    }
    setReflections(rs => rs.filter(r => r.id !== entry.id));
    say('entry deleted');
    return true;
  }

  if (loading) {
    return (
      <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: 'var(--space-lg)', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-sm)' }}>
        Loading your journal...
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: 'var(--space-lg)', textAlign: 'center' }}>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--brand-rust)', margin: 0 }}>
          Could not load your journal right now. Your entries are safe -- try refreshing the page.
        </p>
      </div>
    );
  }

  const deckEntries = reflections.map(r => ({
    id: r.id,
    book_id: r.book_id,
    bookless: isBookless(r),
    // An entry with no book is shown by the reader's own title. A book that can't be found still reads "a book".
    book: isBookless(r)
      ? { id: null, title: r.entry_title || '', author_name: '', cover_image_url: null }
      : (books[r.book_id] || { id: r.book_id, title: 'a book', author_name: '', cover_image_url: null }),
    prompt: { label: r.prompt_label, emoji: r.prompt_emoji, color: r.prompt_color, kind: r.prompt_kind },
    reflection: r.reflection_text,
    pull: r.pull,
    audio_url: r.audio_url,
    transcript: r.transcript,
    duration_seconds: r.duration_seconds,
    shared: r.visibility === 'shared',
    date: journalFormatDate(r.created_at),
    raw: r,
  }));

  const presetPrompts = prompts.filter(p => p.kind === 'preset');
  const talkPrompt = prompts.find(p => p.kind === 'talk');
  const collapseToggle = <JournalCollapseToggle collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} />;

  if (collapsed) {
    return (
      <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)' }}>
          <span style={{ fontSize: 'var(--font-size-md)' }}>📓</span>
          <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>A Book That...</span>
          {collapseToggle}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px', position: 'relative' }}>
      {deckEntries.length === 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)' }}>
          <span style={{ fontSize: 'var(--font-size-md)' }}>📓</span>
          <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>A Book That...</span>
          {collapseToggle}
        </div>
      )}

      {!profile?.seen_journal_privacy_notice && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)', padding: '10px 12px', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-md)' }}>
          <div style={{ flex: 1, fontSize: 'var(--font-size-xs)', color: 'var(--neutral-700)', lineHeight: 1.5 }}>
            <p style={{ margin: '0 0 6px' }}>Reflections are private by default. A private one never appears anywhere on Reanlo — it only helps match books behind the scenes, unattributed, never with your name on it.</p>
            <p style={{ margin: 0 }}>Shared ones show the book &amp; the prompt on your profile. When Reader's Corner opens, shared entries will also carry what you wrote &amp; any voice note, credited to you. So share the ones you would be happy reading aloud.</p>
          </div>
          <button onClick={dismissPrivacyNotice}
            style={{ flexShrink: 0, background: 'none', border: 'none', color: 'var(--feedback-warning-ink)', fontWeight: 700, fontSize: 'var(--font-size-xs)', cursor: 'pointer' }}>
            Got it
          </button>
        </div>
      )}

      {toast && (
        <div style={{ marginBottom: 12 }}>
          <span style={{ background: 'var(--brand-forest)', color: 'var(--brand-cream)', fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: '6px 14px' }}>{toast}</span>
        </div>
      )}

      {deckEntries.length === 0 ? (
        <div style={{ background: 'var(--brand-paper)', border: '1.5px solid var(--brand-sand)', borderRadius: 16, padding: '20px 22px' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--neutral-800)' }}>the reading journal</div>
          <div style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 13, color: 'var(--brand-rust)' }}>a book that I…</div>
          <p style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 6 }}>pick one and tell it about a book. any book, read anytime.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {presetPrompts.slice(0, 8).map(p => (
              <button key={p.id} onClick={() => setFlow({ p })} style={{ border: `1.5px solid ${p.color}44`,
                background: '#fff', color: 'var(--neutral-800)', borderRadius: 999, padding: '7px 13px', fontSize: 12, cursor: 'pointer' }}>
                {p.emoji} {p.label}
              </button>
            ))}
            <button onClick={() => setFlow({ p: null })} style={{ border: '1.5px dashed var(--brand-sand)',
              background: 'transparent', color: 'var(--neutral-400)', borderRadius: 999, padding: '7px 13px', fontSize: 12, cursor: 'pointer' }}>
              ✏️ type your own
            </button>
            {talkPrompt && <button onClick={() => setFlow({ p: talkPrompt })} style={{ border: 'none', background: 'var(--brand-forest)',
              color: '#fff', borderRadius: 999, padding: '7px 15px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              I just want to talk about a book
            </button>}
          </div>
        </div>
      ) : (
        <JournalTileDeck collapseToggle={collapseToggle} entries={deckEntries} onAdd={() => setFlow({ p: null })} onEdit={openEditor}
          onDelete={deleteEntry} onAddToBook={addAnotherEntry} canAddEntry={!!talkPrompt} handle={profile?.username} />
      )}

      {flow && (
        <JournalEntryFlow
          initialPrompt={flow.edit ? null : flow.p}
          initialBook={flow.edit ? null : (flow.book || null)}
          initialFreeformLabel={flow.edit ? '' : (flow.freeformLabel || '')}
          editEntry={flow.edit ? flow.editEntry : null}
          prompts={prompts}
          onClose={() => setFlow(null)}
          onDone={handleDone}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 📓 A BOOK THAT... (JOURNAL — PUBLIC, READ-ONLY)
// ═══════════════════════════════════════════════════════════════
// Used on another reader's profile. Reads shared_reflections_public, which
// doesn't carry reflection_text at all -- book, prompt and date only (5.7).
// Grouped by book so the same title doesn't repeat down the page.
export function JournalBlockPublic({ profile }) {
  const [entries, setEntries] = useState([]);
  const [books,   setBooks]   = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!profile?.user_id) { setLoading(false); return; }
      const { data: rows, error: rowsError } = await supabase
        .from('shared_reflections_public')
        .select('*')
        .eq('reader_id', profile.user_id)
        .order('created_at', { ascending: false })
        .limit(MAX_REFLECTIONS_SHOWN);
      if (cancelled) return;
      if (rowsError) {
        console.error('Public journal load failed:', rowsError.message);
        setLoading(false);
        return;
      }
      const rowList = rows || [];
      setEntries(rowList);
      const bookIds = bookIdsToLookUp(rowList);
      if (bookIds.length) {
        const { data: bookRows, error: bookError } = await supabase.from('books').select('id, title, author_name, cover_image_url').in('id', bookIds);
        if (bookError) console.error('Public journal book lookup failed:', bookError.message);
        if (!cancelled) setBooks(Object.fromEntries((bookRows || []).map(b => [b.id, b])));
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [profile?.user_id]);

  if (loading) return null;
  // One card per book. An entry with no book is never shared, so none should be here; if one were, it would not get a card.
  const shownEntries = entries.filter(r => r.book_id != null);
  if (shownEntries.length === 0) return null;

  const byBook = [];
  const seen = new Set();
  for (const r of shownEntries) {
    if (seen.has(r.book_id)) continue;
    seen.add(r.book_id);
    const count = shownEntries.filter(x => x.book_id === r.book_id).length;
    byBook.push({ latest: r, count });
  }

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)' }}>
        <span style={{ fontSize: 'var(--font-size-md)' }}>📓</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>A Book That...</span>
      </div>
      {/* Per review on fde8078: a visitor should see the same species of
          card the owner does, not a stretched-out list -- section 1's public
          tile is book, prompt, cover, handle, date (never the reflection
          text itself; shared_reflections_public has never carried that).
          A wrapping grid of small tiles rather than the owner's single
          browsable deck: a visitor scans someone else's journal, they don't
          edit it, so there's no reason to make them click through one at a
          time. Grouping by book (byBook, above) is unchanged from before --
          this review didn't ask for that to change, only the card shape and
          the handle's placement. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-10)' }}>
        {byBook.map(({ latest: r, count }) => {
          const book = books[r.book_id];
          return (
            <div key={r.book_id} style={{ width: 148, flexShrink: 0, position: 'relative', background: 'var(--brand-cream)',
              borderRadius: 10, border: '1px solid var(--brand-sand)', padding: '14px 10px 12px', boxSizing: 'border-box',
              backgroundImage: 'radial-gradient(rgba(139,115,85,.07) 1px, transparent 1px)', backgroundSize: '7px 7px' }}>
              {count > 1 && (
                <span title={`${count} entries for this book`} style={{ position: 'absolute', top: 7, right: 7, fontSize: 9, fontWeight: 700, color: 'var(--neutral-500)',
                  background: '#fff', border: '1px solid var(--brand-sand)', borderRadius: 999, padding: '1px 7px' }}>{count}</span>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 9 }}>
                <JournalCover book={book} w={56} h={80} tilt={-2} />
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12, color: 'var(--neutral-800)', textAlign: 'center',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3 }}>{book?.title || 'a book'}</div>
              <div style={{ marginTop: 8, fontSize: 10, fontWeight: 700, color: r.prompt_color || 'var(--brand-rust)', textAlign: 'center' }}>
                {r.prompt_kind === 'talk' ? '💬 talked about it' : `${r.prompt_emoji} ${r.prompt_label}`}
              </div>
              <div style={{ fontSize: 9.5, color: 'var(--neutral-400)', textAlign: 'center', marginTop: 4 }}>
                {profile?.username && `@${profile.username} · `}{journalFormatDate(r.created_at)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// 📊 READING STATS
// ═══════════════════════════════════════════════════════════════
export function ReadingStatsBlock({ profile, onOpenCsvModal, refreshKey, readOnly = false }) {
  const [csvStats,     setCsvStats]     = useState(null);
  const [libraryStats, setLibraryStats] = useState(null);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
     let cancelled = false;
  async function fetchStats() {
    setLoading(true);
    setCsvStats(null);
    setLibraryStats(null);
    const ownerId = profile?.user_id || (await getCurrentUser())?.id;
    if (!ownerId || cancelled) { setLoading(false); return; }
    const { data: csv } = await supabase
      .from('user_csv_stats').select('*').eq('user_id', ownerId).maybeSingle();
    if (cancelled) return;
    if (csv) { setCsvStats(csv); setLoading(false); return; }

      const { data: libData } = await supabase
        .from('user_library')
        .select(`shelf, rating, date_added, books ( author_name, page_count, book_tags ( tag_type, tropes ( canonical_name ) ) )`)
        .eq('user_id', ownerId)
        .in('shelf', ['read', 'loved']);
      if (!libData?.length) { setLoading(false); return; }

      const readBooks  = libData;
      const ratedBooks = readBooks.filter(b => b.rating);
      // null, not 0 — a shelf with nothing rated has no average. This happened
      // to render correctly already, because 0 is falsy and the display below
      // tests truthiness, but that is luck rather than intent: the moment
      // anything reads this with `?? '—'` or formats it, a 0 becomes "0.0".
      const avgRating  = ratedBooks.length
        ? (ratedBooks.reduce((s, b) => s + b.rating, 0) / ratedBooks.length).toFixed(1)
        : null;
      const totalPages    = readBooks.reduce((s, b) => s + (b.books?.page_count || 250), 0);
      const uniqueAuthors = new Set(readBooks.map(b => b.books?.author_name).filter(Boolean)).size;

      const tropeCounts = {};
      readBooks.forEach(item => {
        (item.books?.book_tags || []).forEach(bt => {
          if (bt.tag_type !== 'trope') return;
          const name = bt.tropes?.canonical_name;
          if (name) tropeCounts[name] = (tropeCounts[name] || 0) + 1;
        });
      });
      const topTropes = Object.entries(tropeCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([t]) => t);

      const ratingBreakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      ratedBooks.forEach(b => {
        const r = Math.round(b.rating);
        if (r >= 1 && r <= 5) ratingBreakdown[r]++;
      });

      const authorCounts = {};
      readBooks.forEach(b => {
        const a = b.books?.author_name;
        if (a) authorCounts[a] = (authorCounts[a] || 0) + 1;
      });
      const topAuthors = Object.entries(authorCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([author, count]) => ({ author, count, initial: author[0].toUpperCase() }));

      const monthlyData = Array(12).fill(0).map((_, i) => {
        const d = new Date();
        d.setMonth(d.getMonth() - (11 - i));
        return { month: d.toLocaleString('default', { month: 'short' }), count: 0 };
      });
      readBooks.forEach(b => {
        if (!b.date_added) return;
        const monthsAgo = Math.floor(
          (Date.now() - new Date(b.date_added).getTime()) / (1000 * 60 * 60 * 24 * 30)
        );
        if (monthsAgo >= 0 && monthsAgo < 12) monthlyData[11 - monthsAgo].count++;
      });

      setLibraryStats({
        books_read: readBooks.length, avg_rating: avgRating,
        total_pages: totalPages, unique_authors: uniqueAuthors,
        top_tropes: topTropes, rating_breakdown: ratingBreakdown,
        top_authors: topAuthors, monthly_reads: monthlyData,
        source: 'library',
      });
      setLoading(false);
    }
    fetchStats();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const stats = csvStats || libraryStats;

  return (
    <div style={{ background: 'var(--neutral-900)', borderRadius: 14, padding: 'var(--space-lg)', position: 'relative', border: '1px solid var(--neutral-800)' }}>

      {/* ── Upload CSV button — right: 36 to avoid ✕ overlap ── */}
      {!readOnly && (
      <div style={{ position: 'absolute', top: 8, right: 36 }}>
        <button
          onClick={onOpenCsvModal}
          style={{ width: 28, height: 28, borderRadius: 'var(--radius-circle)', background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--feedback-warning)' }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
            e.currentTarget.nextSibling.style.opacity = '1';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
            e.currentTarget.nextSibling.style.opacity = '0';
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </button>
        <div style={{ position: 'absolute', bottom: '110%', right: 0, background: 'var(--neutral-800)', color: 'var(--brand-white)', fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '5px 10px', borderRadius: 'var(--radius-md)', whiteSpace: 'nowrap', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.15s', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
          Import Goodreads CSV
          <div style={{ position: 'absolute', bottom: -4, right: 10, width: 8, height: 8, background: 'var(--neutral-800)', transform: 'rotate(45deg)' }} />
        </div>
      </div>
      )}

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-xs)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>📊</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--feedback-warning)', letterSpacing: 1, textTransform: 'uppercase' }}>Reading Stats</span>
      </div>
      <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-600)', margin: '0 0 12px', fontStyle: 'italic' }}>
        {csvStats?.updated_at
          ? `Last imported ${new Date(csvStats.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
          : stats?.source === 'library' ? 'From your library' : ''}
      </p>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)' }}>Loading...</p>
        </div>

      ) : stats ? (
        <>
          {/* ── Key stats grid ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
            {[
              { label: 'Books Read', value: stats.books_read || 0 },
              { label: 'Avg Rating', value: stats.avg_rating ? Number(stats.avg_rating).toFixed(1) : '—' },
              { label: 'Pages',      value: stats.total_pages ? (stats.total_pages >= 1000 ? `${Math.round(stats.total_pages / 1000)}k` : stats.total_pages) : '—' },
              { label: 'Authors',    value: stats.unique_authors || '—' },
            ].map(stat => (
              <div key={stat.label} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 'var(--radius-md)', padding: 'var(--space-10)', textAlign: 'center' }}>
                <p style={{ fontSize: 'var(--font-size-xxl)', fontWeight: 800, color: 'var(--brand-white)', margin: 'var(--space-none)', lineHeight: 1 }}>{stat.value}</p>
                <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: '3px 0 0' }}>{stat.label}</p>
              </div>
            ))}
          </div>

          {/* ── Rating breakdown ── */}
          {stats.rating_breakdown && (
            <div style={{ marginBottom: 'var(--space-md)' }}>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-600)', margin: '0 0 8px', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>Ratings</p>
              {[5, 4, 3, 2, 1].map(star => {
                const count = stats.rating_breakdown[star] || 0;
                const max   = Math.max(...Object.values(stats.rating_breakdown), 1);
                return (
                  <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-xs)' }}>
                    <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-600)', minWidth: 14 }}>{star}★</span>
                    <div style={{ flex: 1, height: 5, background: 'var(--neutral-800)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: star === 5 ? 'var(--feedback-warning)' : 'var(--neutral-700)', width: `${(count / max) * 100}%`, borderRadius: 3, transition: 'width 0.6s ease' }} />
                    </div>
                    <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-600)', minWidth: 20, textAlign: 'right' }}>{count}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Top tropes ── */}
          {stats.top_tropes?.length > 0 && (
            <div style={{ marginBottom: 'var(--space-md)' }}>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-600)', margin: '0 0 6px', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>Top Tropes</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
                {stats.top_tropes.slice(0, 4).map((t, i) => (
                  <span key={i} style={{ padding: '3px 8px', background: 'rgba(217,119,6,0.15)', color: 'var(--feedback-warning)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-xs)' }}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* ── Top authors ── */}
          {stats.top_authors?.length > 0 && (
            <div>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-600)', margin: '0 0 8px', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>Top Authors</p>
              {stats.top_authors.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-6)' }}>
                  <div style={{ width: 24, height: 24, borderRadius: 'var(--radius-circle)', background: ['#d97706','#9E4B2C','#384D3D','#059669'][i % 4], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--brand-white)', flexShrink: 0 }}>
                    {a.initial}
                  </div>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: '#d1d5db', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.author}</span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-600)' }}>{a.count}</span>
                </div>
              ))}
            </div>
          )}

          {/* ── Monthly reads bar chart ── */}
          {libraryStats?.monthly_reads?.length > 0 && (
            <div style={{ marginTop: 'var(--space-md)' }}>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-600)', margin: '0 0 8px', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>Monthly Reads</p>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 40 }}>
                {libraryStats.monthly_reads.map((m, i) => {
                  const maxCount = Math.max(...libraryStats.monthly_reads.map(x => x.count), 1);
                  const height   = m.count > 0 ? Math.max(4, Math.round((m.count / maxCount) * 36)) : 2;
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-xxs)' }}>
                      <div style={{ width: '100%', background: m.count > 0 ? 'var(--feedback-warning)' : 'var(--neutral-800)', borderRadius: '2px 2px 0 0', height: `${height}px`, transition: 'height 0.4s ease' }} />
                      <span style={{ fontSize: 'var(--font-size-micro)', color: 'var(--neutral-600)' }}>{m.month}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Upgrade nudge for library-only stats ── */}
          {stats.source === 'library' && !readOnly && (
            <button onClick={onOpenCsvModal}
              style={{ width: '100%', marginTop: 'var(--space-10)', padding: 'var(--space-sm)', background: 'rgba(217,119,6,0.12)', border: '1px solid rgba(217,119,6,0.25)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning)', cursor: 'pointer', fontWeight: 600 }}>
              📥 Import Goodreads CSV for richer stats
            </button>
          )}
        </>

      ) : readOnly ? (
        <div style={{ padding: '12px 0 4px' }}><VisitorEmpty onDark /></div>
      ) : (
        /* ── Empty state ── */
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <p style={{ fontSize: 'var(--font-size-h4)', margin: '0 0 8px' }}>📥</p>
          <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Import your Goodreads CSV to see your reading stats
          </p>
          <button onClick={onOpenCsvModal}
            style={{ padding: '10px 20px', background: 'var(--feedback-warning)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer' }}>
            Import CSV
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 🎯 MY READING GOAL
// ═══════════════════════════════════════════════════════════════
export function ReadingGoalBlock({ profile, readOnly = false }) {
  const [showModal,    setShowModal]    = useState(false);
  const [goalInput,    setGoalInput]    = useState('');
  const [challenge,    setChallenge]    = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [incrementing, setIncrementing] = useState(false);
  const [editingCount, setEditingCount] = useState(false);
  const [countInput,   setCountInput]   = useState('');
  const [goalError,    setGoalError]    = useState('');

  const currentYear  = new Date().getFullYear();
  const booksRead    = challenge?.manual_count || 0;
  const goal         = challenge?.goal || 0;
  const progress     = goal > 0 ? Math.min(Math.round((booksRead / goal) * 100), 100) : 0;
  const PRESETS      = [12, 24, 50, 100];

useEffect(() => {
    async function fetchChallenge() {
      const ownerId = profile?.user_id || (await getCurrentUser())?.id;
      if (!ownerId) return;
      const { data } = await supabase
        .from('reading_challenges')
        .select('*')
        .eq('user_id', ownerId)
        .eq('year', currentYear)
        .maybeSingle();
      setChallenge(data);
    }
    fetchChallenge();
  }, [profile?.user_id]);

  // A fresh modal starts without the last attempt's error.
  useEffect(() => { if (showModal) setGoalError(''); }, [showModal]);

  // Returns whether it saved. A failed save used to hand setChallenge the
  // null that came back, so the block turned into "+ Set your reading goal" as
  // though the goal were gone -- and setting it again then wrote a count of 0,
  // because the count on screen had gone with it (audit 14.1). The goal and
  // count on screen now stay as they were, and the reader is told.
  async function saveChallenge(updates) {
    if (readOnly) return false;   // a visitor's tap used to overwrite their own goal (audit 14.2)
    setGoalError('');
    const user = await getCurrentUser();
    if (!user) { setGoalError(SAVE_FAILED); return false; }
    const { data, error } = await supabase
      .from('reading_challenges')
      .upsert(
        { user_id: user.id, year: currentYear, ...updates },
        { onConflict: 'user_id,year' }
      )
      .select()
      .single();
    if (error || !data) {
      reportSaveFailure('reading goal save', error || 'no row returned');
      setGoalError(SAVE_FAILED);
      return false;
    }
    setChallenge(data);
    return true;
  }

  async function saveGoal() {
    const g = parseInt(goalInput);
    if (!g || g < 1) return;
    setSaving(true);
    const ok = await saveChallenge({ goal: g, manual_count: booksRead });
    setSaving(false);
    if (ok) setShowModal(false);
  }

  async function increment() {
    if (incrementing) return;
    setIncrementing(true);
    await saveChallenge({ goal, manual_count: booksRead + 1 });
    setIncrementing(false);
  }

  async function decrement() {
    if (incrementing || booksRead <= 0) return;
    setIncrementing(true);
    await saveChallenge({ goal, manual_count: Math.max(0, booksRead - 1) });
    setIncrementing(false);
  }

  async function saveCountEdit() {
    const val = parseInt(countInput);
    if (!isNaN(val) && val >= 0) {
      setIncrementing(true);
      await saveChallenge({ goal, manual_count: val });
      setIncrementing(false);
    }
    setEditingCount(false);
  }

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: 'var(--space-lg)', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onClick={() => { setGoalInput(goal ? String(goal) : ''); setShowModal(true); }} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>🎯</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>My Reading Goal</span>
      </div>

      {challenge ? (
        <>
          {/* ── Count row ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-sm)' }}>

            {/* Left: editable count + goal */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-6)' }}>
              {editingCount ? (
                <input
                  autoFocus
                  type="number"
                  min="0"
                  value={countInput}
                  onChange={e => setCountInput(e.target.value)}
                  onBlur={saveCountEdit}
                  onKeyDown={e => { if (e.key === 'Enter') saveCountEdit(); if (e.key === 'Escape') setEditingCount(false); }}
                  style={{ width: 72, fontSize: 'var(--font-size-h2)', fontWeight: 800, color: 'var(--neutral-800)', lineHeight: 1, border: 'none', borderBottom: '2px solid var(--feedback-warning)', outline: 'none', background: 'transparent', padding: '0 2px', marginBottom: 'var(--space-xxs)' }}
                />
              ) : (
                <span
                  onClick={readOnly ? undefined : () => { setCountInput(String(booksRead)); setEditingCount(true); }}
                  title={readOnly ? undefined : 'Click to edit'}
                  style={{ fontSize: 'var(--font-size-h2)', fontWeight: 800, color: 'var(--neutral-800)', lineHeight: 1, cursor: readOnly ? 'default' : 'pointer', borderBottom: '2px dashed transparent', transition: 'border-color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderBottomColor = '#d97706'}
                  onMouseLeave={e => e.currentTarget.style.borderBottomColor = 'transparent'}
                >
                  {incrementing ? '…' : booksRead}
                </span>
              )}
              <span style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-400)', marginBottom: 'var(--space-xs)' }}>/ {goal} books</span>
            </div>

            {/* Right: − count + and % */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}>
              {/* Manual − + buttons */}
              {!readOnly && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)' }}>
                <button
                  onClick={decrement}
                  disabled={incrementing || booksRead <= 0}
                  style={{ width: 28, height: 28, borderRadius: 'var(--radius-circle)', border: '1.5px solid var(--brand-sand)', background: booksRead > 0 ? 'var(--brand-white)' : 'var(--neutral-100)', color: booksRead > 0 ? 'var(--neutral-800)' : 'var(--neutral-300)', fontSize: 'var(--font-size-lg)', cursor: booksRead > 0 && !incrementing ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, lineHeight: 1, transition: 'all 0.15s' }}
                  onMouseEnter={e => { if (booksRead > 0) { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.borderColor = '#d97706'; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = booksRead > 0 ? '#fff' : '#f5f5f4'; e.currentTarget.style.borderColor = '#e8e4dc'; }}
                >
                  −
                </button>
                <button
                  onClick={increment}
                  disabled={incrementing}
                  style={{ width: 28, height: 28, borderRadius: 'var(--radius-circle)', border: '1.5px solid var(--brand-sand)', background: 'var(--brand-white)', color: 'var(--neutral-800)', fontSize: 'var(--font-size-lg)', cursor: incrementing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, lineHeight: 1, transition: 'all 0.15s' }}
                  onMouseEnter={e => { if (!incrementing) { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.borderColor = '#d97706'; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e8e4dc'; }}
                >
                  +
                </button>
              </div>
              )}

              {/* % */}
              <span style={{ fontSize: 'var(--font-size-xxl)', fontWeight: 800, color: progress >= 100 ? '#10b981' : 'var(--feedback-warning)', minWidth: 48, textAlign: 'right' }}>
                {progress}%
              </span>
            </div>
          </div>

          {/* ── Progress bar ── */}
          <div style={{ height: 8, background: 'var(--neutral-100)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginBottom: 'var(--space-6)' }}>
            <div style={{ height: '100%', background: progress >= 100 ? '#10b981' : 'linear-gradient(90deg, var(--feedback-warning), #f59e0b)', width: `${progress}%`, borderRadius: 'var(--radius-sm)', transition: 'width 0.4s ease' }} />
          </div>

          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: 'var(--space-none)', textAlign: 'right' }}>
            {progress >= 100
              ? '🎉 Goal reached!'
              : `${goal - booksRead} book${goal - booksRead !== 1 ? 's' : ''} to go`}
          </p>
          {!showModal && <SaveError message={goalError} style={{ textAlign: 'right' }} />}
        </>
      ) : readOnly ? <VisitorEmpty /> : (
        <button onClick={() => { setGoalInput(''); setShowModal(true); }}
          style={{ width: '100%', padding: 'var(--space-lg)', background: 'var(--brand-paper)', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>
          + Set your reading goal
        </button>
      )}

      {/* ── Goal Modal ── */}
      {showModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 380, width: '100%', padding: 'var(--space-xxl)' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: '0 0 6px' }}>🎯 Set Your Reading Goal</h3>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '0 0 16px' }}>How many books do you want to read in {currentYear}?</p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
              {PRESETS.map(p => (
                <button key={p} onClick={() => setGoalInput(String(p))}
                  style={{ padding: 'var(--space-10)', borderRadius: 'var(--radius-10)', border: goalInput === String(p) ? '2px solid var(--feedback-warning)' : '1px solid var(--brand-sand)', background: goalInput === String(p) ? 'var(--feedback-warning-bg)' : 'var(--brand-white)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: 'pointer', color: goalInput === String(p) ? 'var(--feedback-warning-ink)' : 'var(--neutral-800)' }}>
                  {p}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
              <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
              <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', fontWeight: 600, letterSpacing: 1 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
            </div>

            <input
              type="number"
              value={goalInput}
              onChange={e => setGoalInput(e.target.value)}
              placeholder="Custom number..."
              min="1"
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-lg)', textAlign: 'center' }}
            />
            <SaveError message={goalError} style={{ margin: '0 0 12px', fontSize: 'var(--font-size-sm)', textAlign: 'center' }} />

            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={() => setShowModal(false)}
                style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>
                Cancel
              </button>
              <button onClick={saveGoal} disabled={!goalInput || saving}
                style={{ flex: 1, padding: '11px', background: goalInput ? 'var(--neutral-800)' : 'var(--brand-sand)', border: 'none', borderRadius: 'var(--radius-10)', cursor: goalInput ? 'pointer' : 'default', fontSize: 'var(--font-size-base)', fontWeight: 700, color: goalInput ? 'var(--brand-white)' : 'var(--neutral-400)' }}>
                {saving ? 'Saving...' : 'Save Goal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ── ShopUrlInput ─────────────────────────────────────────────
// The box keeps what was typed whether or not it saved, so a failed save used
// to look exactly like a good one until the next visit (audit 6.7). onSave
// answers whether it saved.
function ShopUrlInput({ value, onSave }) {
  const [local, setLocal] = useState(value || '');
  const [error, setError] = useState('');
  useEffect(() => { setLocal(value || ''); }, [value]);
  async function commit() {
    if (local === value) return;
    setError((await onSave(local)) ? '' : SAVE_FAILED);
  }
  return (
    <>
      <input value={local} onChange={e => { setLocal(e.target.value); setError(''); }} onBlur={commit}
        placeholder="https://your-shop.etsy.com"
        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }} />
      <SaveError message={error} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// 🛍️ MY SHOP
// ═══════════════════════════════════════════════════════════════
export function MyShopBlock({ profile, onUpdate, readOnly = false }) {
  const [showModal, setShowModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: '', price: '', emoji: '📦', link: '', image_url: '' });
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [shopError, setShopError] = useState('');
  const shopImgRef = React.useRef();

useEffect(() => {
    async function fetch() {
      if (!profile?.user_id) return;
      const { data } = await supabase.from('shop_products').select('*').eq('user_id', profile.user_id).order('created_at');
      setProducts(data || []);
    }
    fetch();
  }, [profile?.user_id, showModal]);
  useEffect(() => { if (showModal) setShopError(''); }, [showModal]);

  async function addProduct() {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    setShopError('');
    const user = await getCurrentUser();
    if (!user) { setSaving(false); setShopError(SAVE_FAILED); return; }
    const { data, error } = await supabase.from('shop_products').insert({ user_id: user.id, ...form }).select().single();
    if (error || !data) {
      // A failed add used to stop saying "Adding..." and nothing else (audit
      // 6.1). The form keeps what was typed.
      reportSaveFailure('shop product add', error || 'no row returned');
      setShopError("That product didn't save. Check your connection and try again.");
      setSaving(false);
      return;
    }
    setProducts(prev => [...prev, data]);
    const price = form.price ? (form.price.startsWith('$') ? form.price : `$${form.price}`) : '';
    // Post as 'shop_update' with product_data so Community renders a clickable product card
    const { error: postError } = await supabase.from('posts').insert({
      user_id:      user.id,
      post_type:    'shop_update',
      content:      `🛍️ New in my shop: ${form.name}${price ? `, ${price}` : ''}`,
      product_data: {
        name:      form.name.trim(),
        price:     price || null,
        emoji:     form.emoji || '📦',
        link:      form.link  || null,
        image_url: form.image_url || null,
      },
    });
    if (postError) {
      // The product is in the shop; only the announcement failed, so say that.
      reportSaveFailure('shop product post', postError);
      setShopError("Added to your shop, but the community post didn't go out.");
    }
    setForm({ name: '', price: '', emoji: '📦', link: '', image_url: '' });
    setSaving(false);
  }

  // A failed delete used to take the product off the screen anyway (audit
  // 6.2). .select('id') is what tells "removed" from "refused": a delete the
  // row rules turn down comes back with no error and no rows.
  async function removeProduct(id) {
    setShopError('');
    const { data, error } = await supabase.from('shop_products').delete().eq('id', id).select('id');
    if (error || !data?.length) {
      reportSaveFailure('shop product remove', error || 'no row deleted');
      setShopError("That product didn't remove. Check your connection and try again.");
      return;
    }
    setProducts(prev => prev.filter(p => p.id !== id));
  }

  async function handleProductImageUpload(e) {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 3 * 1024 * 1024) { alert('Image must be under 3MB'); return; }
    setUploadingImg(true);
    try {
      const user = await getCurrentUser();
      const ext = file.name.split('.').pop();
      const path = `shop/${user.id}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
      setForm(f => ({ ...f, image_url: publicUrl, emoji: '' }));
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploadingImg(false);
    }
  }

  const EMOJIS = ['📦', '🕯️', '🎨', '📚', '🖊️', '🌿', '💌', '🧴', '🪴', '🎁'];

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onClick={() => setShowModal(true)} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>🛍️</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>My Shop</span>
      </div>
      {products.length > 0 ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-sm)' }}>
            {products.slice(0, 4).map(p => (
              <a key={p.id} href={ensureUrl(p.link)} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', padding: 'var(--space-10)', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', border: '1px solid #f0f0ef', textDecoration: 'none', transition: 'all 0.15s', overflow: 'hidden' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                onMouseLeave={e => e.currentTarget.style.background = '#faf8f5'}>
                {p.image_url
                  ? <div style={{ width: '100%', height: 72, borderRadius: 7, overflow: 'hidden', marginBottom: 'var(--space-6)' }}><img src={p.image_url} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>
                  : <span style={{ fontSize: 'var(--font-size-h4)', display: 'block', marginBottom: 'var(--space-xs)' }}>{p.emoji || '📦'}</span>}
                <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                {p.price && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning)', margin: 'var(--space-none)', fontWeight: 700 }}>{p.price.startsWith('$') ? p.price : `$${p.price}`}</p>}
              </a>
            ))}
          </div>
          {profile?.shop_url && (
            <a href={ensureUrl(profile.shop_url)} target="_blank" rel="noopener noreferrer"
              style={{ display: 'block', marginTop: 'var(--space-10)', padding: 'var(--space-sm)', background: 'var(--neutral-800)', color: 'var(--brand-white)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 700, textAlign: 'center', textDecoration: 'none' }}>
              Visit Full Shop ↗
            </a>
          )}
        </>
      ) : readOnly ? <VisitorEmpty /> : (
        <button onClick={() => setShowModal(true)} style={{ width: '100%', padding: 'var(--space-lg)', background: 'var(--brand-paper)', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>
          + Add your products
        </button>
      )}

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }} onClick={() => setShowModal(false)}>
          <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 480, width: '100%', padding: 'var(--space-xxl)', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: '0 0 16px' }}>🛍️ My Shop</h3>
            {products.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '10px 12px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', marginBottom: 'var(--space-sm)' }}>
                {p.image_url ? <img src={p.image_url} alt={p.name} style={{ width: 36, height: 36, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }} /> : <span style={{ fontSize: 'var(--font-size-xxl)' }}>{p.emoji || '📦'}</span>}
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>{p.name}</p>
                  {p.price && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning)', margin: 'var(--space-none)' }}>{p.price.startsWith('$') ? p.price : `$${p.price}`}</p>}
                </div>
                <button onClick={() => removeProduct(p.id)} style={{ background: 'none', border: 'none', color: 'var(--feedback-error)', cursor: 'pointer', fontSize: 'var(--font-size-lg)' }}>✕</button>
              </div>
            ))}

            <div style={{ marginTop: 'var(--space-lg)', padding: 'var(--space-lg)', background: 'var(--brand-paper)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--brand-sand)' }}>
              <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 10px' }}>Add Product</p>
              <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-10)', alignItems: 'flex-start' }}>
                <div onClick={() => shopImgRef.current?.click()}
                  style={{ width: 60, height: 60, borderRadius: 'var(--radius-10)', border: form.image_url ? 'none' : '1.5px dashed var(--brand-sand)', background: 'var(--brand-paper)', cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {form.image_url ? <img src={form.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: uploadingImg ? 10 : 20, color: 'var(--neutral-400)' }}>{uploadingImg ? 'Uploading...' : '📷'}</span>}
                </div>
                <input ref={shopImgRef} type="file" accept="image/*" onChange={handleProductImageUpload} style={{ display: 'none' }} />
                <div>
                  <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '0 0 5px', fontWeight: 600 }}>OR PICK EMOJI</p>
                  <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap', maxWidth: 220 }}>
                    {EMOJIS.map(e => (
                      <button key={e} onClick={() => setForm(f => ({ ...f, emoji: e, image_url: '' }))}
                        style={{ width: 30, height: 30, borderRadius: 7, border: form.emoji === e && !form.image_url ? '2px solid var(--feedback-warning)' : '1px solid var(--brand-sand)', background: form.emoji === e && !form.image_url ? 'var(--feedback-warning-bg)' : 'var(--brand-white)', cursor: 'pointer', fontSize: 15 }}>
                        {e}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Product name *"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-sm)' }} />
              <div style={{ position: 'relative', marginBottom: 'var(--space-sm)' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', fontWeight: 700 }}>$</span>
                <input value={form.price.replace(/^\$/, '')} onChange={e => setForm(f => ({ ...f, price: e.target.value.replace(/^\$/, '') }))} placeholder="0.00"
                  style={{ width: '100%', padding: '8px 12px 8px 24px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <input value={form.link} onChange={e => setForm(f => ({ ...f, link: e.target.value }))} placeholder="Link to this specific product (Etsy listing, etc.)"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-sm)' }} />
              <button onClick={addProduct} disabled={!form.name.trim() || saving}
                style={{ width: '100%', padding: 'var(--space-10)', background: form.name.trim() ? 'var(--neutral-800)' : 'var(--brand-sand)', border: 'none', borderRadius: 'var(--radius-md)', cursor: form.name.trim() ? 'pointer' : 'default', fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--brand-white)' }}>
                {saving ? 'Adding...' : 'Add Product'}
              </button>
              <SaveError message={shopError} style={{ margin: '8px 0 0', fontSize: 'var(--font-size-sm)' }} />
            </div>

            <div style={{ marginTop: 'var(--space-md)', padding: 'var(--space-md)', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)' }}>
              <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>Your main shop URL</p>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '0 0 8px' }}>Your overall shop homepage (Etsy, Gumroad, etc.)</p>
              <ShopUrlInput value={profile?.shop_url || ''} onSave={val => saved(onUpdate({ shop_url: val }))} />
            </div>
            <button onClick={() => setShowModal(false)} style={{ width: '100%', marginTop: 'var(--space-14)', padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 💼 ASK ME ABOUT
// ═══════════════════════════════════════════════════════════════



// ── Rate Parser — AskMeAboutBlock only ───────────────────────────────────────
// These functions live here. useAuthorDirectory reads effective_price from stored data
// and falls back to its own parseEffectivePrice for rows not yet backfilled.
// ─────────────────────────────────────────────────────────────────────────────

// ── Currency detection ────────────────────────────────────────────────────────
function detectCurrency(text) {
  if (!text) return '$';
  if (text.includes('£')) return '£';
  if (text.includes('€')) return '€';
  if (text.includes('¥')) return '¥';
  return '$';
}

// ── Number formatting ─────────────────────────────────────────────────────────
function fmt(n, currency = '$') {
  if (n == null || isNaN(n)) return null;
  if (n >= 1_000_000) return `${currency}${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${currency}${(n / 1_000).toFixed(1)}k`;
  if (n < 1 && n > 0) return `${currency}${parseFloat(n.toFixed(4))}`;
  return `${currency}${Number(n).toLocaleString()}`;
}

// ── Rate parser ───────────────────────────────────────────────────────────────
// Returns: { amount, unit, effectivePrice, currency, display, unparseable }
// unit: 'per_word' | 'per_hour' | 'per_month' | 'per_year' | 'flat' | 'contact'
function parseRate(rateText) {
  if (!rateText?.trim()) return { unparseable: true, display: rateText };

  const original  = rateText.trim();
  const t         = original.toLowerCase();
  const currency  = detectCurrency(original);

  // Contact / DM / inquiry → unparseable, display as-is, show under Any only
  if (/contact|inquire|inquiry|dm me|message me|reach out|tbd|negotiable/i.test(t)) {
    return { unit: null, currency, display: original, unparseable: true };
  }

  // "starting from $X", "from $X" → flat
  const fromMatch = t.match(/(?:starting from|starting at|from)\s*[£€¥$]?\s*(\d+(?:\.\d+)?)/);

  

  // Extract amount — strip currency symbols then find number
  const stripped    = t.replace(/[£€¥$,]/g, '');
  const numMatch    = fromMatch ? [null, fromMatch[1]] : stripped.match(/(\d+(?:\.\d+)?)/);
  if (!numMatch) return { unparseable: true, display: original };

  let amount = parseFloat(numMatch[1]);
  if (isNaN(amount)) return { unparseable: true, display: original };

  // Handle cents: "5 cents" → 0.05
  if (/\bcents?\b|¢/.test(t)) {
    amount = amount / 100;
  }

  let unit, effectivePrice;

  // ── per-N-words variants (must check BEFORE plain "per word") ────────────
  // "$15 per 1k words", "$15/1000 words", "$1 per 100 words" etc.
  const perNMatch = t.match(/per\s*([\d,]+(?:k)?)\s*words?/)
                 || t.match(/\/([\d,]+(?:k)?)\s*words?/);
  if (perNMatch) {
    let n = perNMatch[1].replace(/,/g, '');
    if (n.endsWith('k')) n = parseFloat(n) * 1000;
    else n = parseFloat(n);
    const pricePerWord = amount / n;          // e.g. $15/1000 = $0.015/word
    unit           = 'per_1k_words';          // keep original unit — amount stays as 15, not 0.015
    effectivePrice = pricePerWord * 80_000;   // e.g. $0.015 × 80k = $1,200 ✓
  }
  // ── plain per-word ────────────────────────────────────────────────────────
  else if (/\/word\b|per\s*word\b|\/wd\b/.test(t)) {
    unit           = 'per_word';
    effectivePrice = amount * 80_000;
  }
  // ── per-page ──────────────────────────────────────────────────────────────
  else if (/\/page\b|per\s*page\b/.test(t)) {
    unit           = 'per_page';
    effectivePrice = amount * 300;
  }
  // ── per-hour ─────────────────────────────────────────────────────────────
  else if (/\/h(ou)?r\b|per\s*h(ou)?r\b/.test(t)) {
    unit           = 'per_hour';
    effectivePrice = amount;
  }
  // ── per-month ────────────────────────────────────────────────────────────
  else if (/\/mo(nth)?\b|per\s*mo(nth)?\b/.test(t)) {
    unit           = 'per_month';
    effectivePrice = amount;
  }
  // ── per-year ─────────────────────────────────────────────────────────────
  else if (/\/yr\b|\/year\b|per\s*year\b/.test(t)) {
    unit           = 'per_year';
    effectivePrice = amount;
  }
  // ── /book, /ms, /manuscript, /project, /cover, /campaign → flat ─────────
  else if (/\/book|\/ms|\/manuscript|\/project|\/cover|\/campaign|per\s*book|per\s*manuscript|per\s*project/.test(t)) {
    unit           = 'flat';
    effectivePrice = amount;
  }
  // ── flat / everything else ────────────────────────────────────────────────
  else {
    unit           = 'flat';
    effectivePrice = amount;
  }

  return { amount, unit, effectivePrice, currency, display: original, unparseable: false };
}

// ── Display formatter ─────────────────────────────────────────────────────────
// Formats structured rate for human reading
function formatRateDisplay(amount, unit, currency = '$') {
  if (unit === 'contact' || unit === null) return null; // display original text, not "Contact me"
  if (amount == null)     return null;
  const labels = {
    per_word:      'per word',
    per_1k_words:  'per 1,000 words',
    per_page:  'per page',
    per_hour:  'per hour',
    per_month: 'per month',
    per_year:  'per year',
    flat:      'flat rate',
  };
  return `${fmt(amount, currency)} ${labels[unit] || unit}`;
}

// ── "We read this as" confirmation ────────────────────────────────────────────
// Shown in the UI so readers can catch mistakes before saving
function rateConfirmation(amount, unit, effectivePrice, currency = '$') {
  if (unit === 'contact')   return null;
  if (amount == null)       return null;
  const base = formatRateDisplay(amount, unit, currency);
  if (unit === 'per_word' || unit === 'per_1k_words')  return `${base}  ≈ ${fmt(effectivePrice, currency)} for an 80k novel`;
  if (unit === 'per_page')  return `${base}  ≈ ${fmt(effectivePrice, currency)} for a 300-page book`;
  return base;
}


const RATE_UNITS = [
  { value: 'per_word',      label: 'per word' },
  { value: 'per_1k_words',  label: 'per 1,000 words' },
  { value: 'per_page',  label: 'per page' },
  { value: 'per_hour',  label: 'per hour' },
  { value: 'per_month', label: 'per month' },
  { value: 'per_year',  label: 'per year' },
  { value: 'flat',      label: 'flat rate' },
  { value: 'contact',   label: 'contact me' },
];

export function AskMeAboutBlock({ profile, onUpdate, readOnly = false }) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [services,      setServices]      = useState([]);
  const [rateAmounts,   setRateAmounts]   = useState({});
  const [rateUnits,     setRateUnits]     = useState({});
  const [rateCurrencies,setRateCurrencies]= useState({});
  const [email,         setEmail]         = useState('');
  const [customService, setCustomService] = useState('');
  const [addWarning,    setAddWarning]    = useState('');
  const [canonicalServices, setCanonicalServices] = useState(CANONICAL_SERVICES);
  const [saving,        setSaving]        = useState(false);
  const [saveError,     setSaveError]     = useState('');
  // { services: [names], sharing, error } while the "share this?" prompt is up.
  const [sharePrompt,   setSharePrompt]   = useState(null);

  const data           = profile?.ask_me_about;
  const activeServices = data?.services || [];
  const contactEmail   = data?.email || '';

  useEffect(() => {
    fetchCanonicalServices().then(data => {
      setCanonicalServices(data);
    });
  }, []);

  // Adding a service, from the text box and from the Add button alike.
  //
  // Refuses a name already on the list, matched case-insensitively, and says
  // so rather than adding a second entry. "Cosplayer" and "cosplayer" are one
  // service everywhere else — sameServiceName is what the admin queue and the
  // save below both use — so letting the box create two of them here is what
  // starts the divergence. Blocking is deliberate over silently merging: the
  // reader should see why their click did nothing.
  function addCustomService() {
    const name = customService.trim();
    if (!name) return;
    const clash = services.find(s => sameServiceName(s, name));
    if (clash) {
      // Name the spelling already on the list when it differs. Refusing
      // "Cosplayer" while "cosplayer" sits further down the modal reads as a
      // broken button unless the message says where it already is.
      setAddWarning(clash === name
        ? 'You already offer this.'
        : `You already offer this, it's on your list as "${clash}".`);
      return;
    }
    setServices([...services, name]);
    setCustomService('');
    setAddWarning('');
  }

  // Populate edit modal on open
  useEffect(() => {
    if (showEditModal) {
      setAddWarning('');
      setSaveError('');
      setServices(data?.services?.map(s => s.name) || []);
      const amounts = {}, units = {}, currencies = {};
      (data?.services || []).forEach(s => {
        if (s.unit === 'contact') {
          units[s.name]      = 'contact';
          currencies[s.name] = s.currency || '$';
        } else if (s.unit) {
          units[s.name]      = s.unit;
          amounts[s.name]    = s.amount != null ? String(s.amount) : '';
          currencies[s.name] = s.currency || '$';
        } else if (s.rate) {
          // Legacy free-text — parse it on load so the UI shows structured fields
          const parsed = parseRate(s.rate);
          if (!parsed.unparseable) {
            units[s.name]      = parsed.unit;
            amounts[s.name]    = parsed.amount != null ? String(parsed.amount) : '';
            currencies[s.name] = parsed.currency || '$';
          }
        }
      });
      setRateAmounts(amounts);
      setRateUnits(units);
      setRateCurrencies(currencies);
      setEmail(data?.email || '');
      setCustomService('');
    }
  }, [showEditModal]);


// An amount with no unit was saved as no price at all: `amount` below is only
// kept when a unit is set, so the reader's number vanished, their rate showed
// nowhere, and Find Help in the author portal (which filters on
// effective_price) could not find them under any budget but "Any". A unit is
// required once an amount is typed, rather than guessing one for them.
function needsUnit(name) {
  const unit = rateUnits[name] || '';
  return unit !== 'contact' && !unit && String(rateAmounts[name] ?? '').trim() !== '';
}

async function save() {
  if (saving) return;
  const missingUnit = services.filter(needsUnit);
  if (missingUnit.length > 0) {
    setSaveError(`Pick a unit for ${missingUnit.join(', ')}, or clear ${missingUnit.length > 1 ? 'those prices' : 'that price'}. Without a unit the price won't save.`);
    return;
  }
  setSaveError('');
  const servicesData = services.map(name => {
    const unit       = rateUnits[name] || null;
    const currency   = rateCurrencies[name] || '$';
    const isContact  = unit === 'contact';
    const amount     = unit && !isContact && rateAmounts[name]
                         ? parseFloat(rateAmounts[name]) : null;
    // Matched case-insensitively, the same way the admin queue matches and the
    // same way addCustomService blocks duplicates. An exact match here meant a
    // reader who re-typed a service in different casing found no `existing`,
    // so normalized_name was recomputed blind — which is exactly what the
    // comment below exists to prevent, reached by a different route.
    const existing      = (data?.services || []).find(ex => sameServiceName(ex.name, name));
    const rate_original = existing?.rate_original || existing?.rate || null;

    // Preserve an already-resolved normalized_name (whether set by the
    // deterministic alias map originally, or by an admin approving a
    // process-service suggestion) instead of recomputing blind on every
    // save. Without this, hitting Save for ANY reason — even adding an
    // unrelated service — silently wiped out prior admin approvals and
    // re-queued them as fresh suggestions forever.
    const normalized = existing?.normalized_name || normalizeServiceName(name, canonicalServices);

    // "Contact me" shows on profile; structured rate shows as "$50 per hour"; no unit = nothing
    const display    = isContact        ? 'Contact me'
                     : unit && amount != null ? formatRateDisplay(amount, unit, currency)
                     : null;

    // Effective price for budget filtering — null if no amount entered
    // Guard: amount must not be null — 0 would appear as "free" in every budget bucket
    const effectivePrice = isContact || amount == null ? null
                         : unit === 'per_word'     ? amount * 80000
                         : unit === 'per_1k_words' ? (amount / 1000) * 80000
                         : unit === 'per_page'     ? amount * 300
                         : amount;

    // Flag non-USD for admin — effective_price is currency-blind so £50 ≠ $50
    const nonUSD = currency !== '$';

    return {
      name,
      unit:            unit || null,
      amount:          amount ?? null,
      currency,
      rate:            display,          // structured display or null
      rate_original,                     // original text — preserved forever
      effective_price: effectivePrice,
      normalized_name: normalized,
      needs_review:    (normalized === null && !CANONICAL_SERVICES.includes(name)) || nonUSD,
    };
  });
  // Only services that were not on the block before this save are news. Every
  // save used to post the whole list, so fixing a typo in a rate put another
  // "Available for" in the community feed.
  const newServices = services.filter(
    name => !activeServices.some(prev => sameServiceName(prev.name, name))
  );

  setSaving(true);
  let result;
  try {
    result = await onUpdate({ ask_me_about: { services: servicesData, email: email.trim() } });
  } catch (err) {
    result = { error: err?.message || 'save failed' };
  }
  setSaving(false);
  // updateProfile reports a failure by returning { error }, never by throwing.
  // No result at all means nothing was saved either: that is the no-op
  // onUpdate the public profile hands its blocks.
  if (!result || result.error) {
    setSaveError("That didn't save. Check your connection and try again.");
    return;
  }

  const user = await getCurrentUser();

  // NEW — fire-and-forget: ask process-service to check anything the alias
  // map couldn't resolve for a near-match against the canonical list. Not
  // awaited — this is advisory (feeds the admin review queue), so it must
  // never slow down or fail the reader's actual save. Same condition the
  // needs_review flag above already uses, nothing new decided here about
  // what counts as "unresolved." Only after the save has gone through, so a
  // failed save does not queue names that were never stored.
  if (user) {
    const unresolved = servicesData.filter(
      s => s.normalized_name === null && !CANONICAL_SERVICES.includes(s.name)
    );
    unresolved.forEach(s => {
      supabase.functions.invoke('process-service', {
        body: { typed_name: s.name, user_id: user.id, username: profile?.username || null },
      }).catch(err => console.warn('process-service unavailable:', err.message));
    });
  }

  setShowEditModal(false);
  // Asked, not assumed, and only about what is new.
  if (user && newServices.length > 0) {
    setSharePrompt({ services: newServices, sharing: false, error: '' });
  }
}

async function shareNewServices() {
  if (!sharePrompt || sharePrompt.sharing) return;
  setSharePrompt(p => ({ ...p, sharing: true, error: '' }));
  const user = await getCurrentUser();
  const posted = user
    ? await postToCommunity(user.id, `💼 Available for: ${sharePrompt.services.join(', ')}`, null)
    : false;
  if (posted) {
    setSharePrompt(null);
  } else {
    setSharePrompt(p => ({ ...p, sharing: false, error: "That didn't post. Try again, or skip it." }));
  }
}

  // Build mailto subject from services
  const mailtoSubject = activeServices.length === 1
    ? `Inquiry about ${activeServices[0].name} | Reanlo`
    : `Service Inquiry | Reanlo`;
  const mailtoHref = contactEmail
    ? `mailto:${contactEmail}?subject=${encodeURIComponent(mailtoSubject)}`
    : null;

  return (
    <div style={{ background: '#1a3a2e', borderRadius: 14, padding: 'var(--space-lg)', position: 'relative', border: '1px solid #1e4035' }}>
      {!readOnly && <EDIT_BTN onDark onClick={() => setShowEditModal(true)} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>💼</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: '#6ee7b7', letterSpacing: 1, textTransform: 'uppercase' }}>Ask Me About</span>
      </div>

      {activeServices.length > 0 ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', marginBottom: 'var(--space-md)' }}>
            {activeServices.map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--font-size-base)', color: '#d1fae5', fontWeight: 500 }}>{s.name}</span>
                {(s.rate || s.rate_original) && <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-warning)', fontWeight: 700 }}>{s.rate || s.rate_original}</span>}
              </div>
            ))}
          </div>

          {/* mailto link — opens visitor's own email app */}
          {mailtoHref ? (
            <a
              href={mailtoHref}
              style={{ display: 'block', width: '100%', padding: 'var(--space-10)', background: 'rgba(110,231,183,0.1)', border: '1px solid rgba(110,231,183,0.2)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer', color: '#6ee7b7', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' }}>
              ✉️ Inquire / Get in Touch
            </a>
          ) : readOnly ? null : (
            // Owner hasn't added their email yet — show a nudge only to them.
            // It said "only to them" and nothing checked (audit 10.5).
            <button
              onClick={() => setShowEditModal(true)}
              style={{ width: '100%', padding: 'var(--space-10)', background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(110,231,183,0.2)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: '#6ee7b7aa', cursor: 'pointer' }}>
              + Add your email so visitors can reach you
            </button>
          )}
        </>
      ) : readOnly ? <VisitorEmpty onDark /> : (
        <button
          onClick={() => setShowEditModal(true)}
          style={{ width: '100%', padding: 'var(--space-lg)', background: 'rgba(255,255,255,0.05)', border: '1.5px dashed rgba(255,255,255,0.1)', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: '#6ee7b7', opacity: 0.7 }}>
          + Add your services
        </button>
      )}

      {/* ── EDIT MODAL ── */}
      {showEditModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}
          onClick={() => { if (!saving) setShowEditModal(false); }}>
          <div
            style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 480, width: '100%', padding: 'var(--space-xxl)', maxHeight: '85vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>

            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: '0 0 16px' }}>💼 Ask Me About</h3>

            {/* Contact email — new field */}
            <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px', letterSpacing: 0.3 }}>
              Your contact email *
            </p>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-6)' }}
            />
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '0 0 18px', lineHeight: 1.4 }}>
              Visitors click "Inquire" → their email app opens with your address pre-filled. Messages go straight to your inbox.
            </p>

            {/* Service presets */}
            <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px', letterSpacing: 0.3 }}>Services you offer</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)', marginBottom: 'var(--space-lg)' }}>
              {canonicalServices.map(s => {
                // Case-insensitive so a preset reads as already-on when the
                // reader typed it themselves in another casing. With ===, the
                // chip showed as off and clicking it added a second entry for
                // the same service.
                const active = services.some(x => sameServiceName(x, s));
                return (
                  <button key={s}
                    onClick={() => { setAddWarning(''); setServices(active ? services.filter(x => !sameServiceName(x, s)) : [...services, s]); }}
                    style={{ padding: '6px 14px', borderRadius: 'var(--radius-xxl)', border: active ? '2px solid var(--neutral-800)' : '1.5px dashed var(--neutral-300)', background: active ? 'var(--neutral-800)' : 'var(--brand-white)', color: active ? 'var(--brand-white)' : 'var(--neutral-500)', fontSize: 'var(--font-size-sm)', fontWeight: active ? 700 : 400, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                    {active && '✓ '}{s}
                  </button>
                );
              })}
            </div>

            {/* Custom service input — both paths go through addCustomService
                so the Enter key and the Add button cannot drift apart. */}
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <input
                  value={customService}
                  onChange={e => { setCustomService(e.target.value); if (addWarning) setAddWarning(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') addCustomService(); }}
                  placeholder="Add your own service..."
                  aria-invalid={addWarning ? 'true' : undefined}
                  aria-describedby={addWarning ? 'service-add-warning' : undefined}
                  style={{ flex: 1, padding: '8px 12px', border: addWarning ? '1px solid var(--feedback-warning-border)' : '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none' }}
                />
                <button
                  onClick={addCustomService}
                  style={{ padding: '8px 14px', background: 'var(--feedback-warning-bg)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', cursor: 'pointer', color: 'var(--feedback-warning-ink)' }}>
                  Add
                </button>
              </div>
              {addWarning && (
                <p id="service-add-warning" role="status" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning-ink)', margin: '6px 0 0', lineHeight: 1.4 }}>
                  {addWarning}
                </p>
              )}
            </div>

            {/* Rates */}
            {services.length > 0 && (
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 3px' }}>Pricing (optional)</p>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '0 0 10px' }}>Enter a number and pick a unit, we'll confirm what we understood.</p>
                {services.map(s => {
                  const unit      = rateUnits[s] || '';
                  const currency  = rateCurrencies[s] || '$';
                  const isContact = unit === 'contact';
                  const confirmText = (() => {
                    if (!unit || isContact || !rateAmounts[s]) return null;
                    const amt = parseFloat(rateAmounts[s]);
                    if (isNaN(amt)) return null;
                    const eff = unit === 'per_word'     ? amt * 80000
                              : unit === 'per_1k_words' ? (amt / 1000) * 80000
                              : unit === 'per_page'     ? amt * 300
                              : amt;
                    return rateConfirmation(amt, unit, eff, currency);
                  })();
                  return (
                    <div key={s} style={{ marginBottom: 'var(--space-10)', padding: '10px 12px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', border: '1px solid #f0e9dc' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                        <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)' }}>{s}</span>
                        <button onClick={() => setServices(services.filter(x => x !== s))}
                          style={{ background: 'none', border: 'none', color: 'var(--neutral-300)', cursor: 'pointer', fontSize: 'var(--font-size-lg)', lineHeight: 1 }}
                          onMouseEnter={e => e.currentTarget.style.color='#dc2626'}
                          onMouseLeave={e => e.currentTarget.style.color='#d6d3d1'}>×</button>
                      </div>
                      <div style={{ display: 'flex', gap: 'var(--space-6)' }}>
                        <select value={currency} onChange={e => setRateCurrencies(r => ({ ...r, [s]: e.target.value }))}
                          style={{ width: 52, padding: '6px 4px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', background: 'var(--brand-white)', cursor: 'pointer' }}>
                          {['$','£','€','¥'].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {!isContact && (
                          <input type="number" min="0" step="any" value={rateAmounts[s] || ''}
                            onChange={e => { setRateAmounts(r => ({ ...r, [s]: e.target.value })); setSaveError(''); }}
                            placeholder="0.00"
                            style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', background: 'var(--brand-white)' }} />
                        )}
                        <select value={unit} onChange={e => { setRateUnits(r => ({ ...r, [s]: e.target.value })); setSaveError(''); }}
                          aria-invalid={needsUnit(s) ? 'true' : undefined}
                          style={{ flex: isContact?2:0, padding: '6px 8px', border: needsUnit(s) ? '1px solid var(--feedback-warning-border)' : '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', outline: 'none', background: 'var(--brand-white)', cursor: 'pointer' }}>
                          <option value="">select</option>
                          {RATE_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </select>
                      </div>
                      {confirmText && (
                        <p style={{ fontSize: 'var(--font-size-xs)', color: '#16a34a', margin: '6px 0 0', fontWeight: 600 }}>✓ We read this as: {confirmText}</p>
                      )}
                      {needsUnit(s) && (
                        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning-ink)', margin: '6px 0 0', lineHeight: 1.4 }}>Pick a unit, or this price won't save.</p>
                      )}
                    </div>
                  );
                })}           
              </div>
            )}


            {saveError && (
              <p role="alert" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-error)', margin: '0 0 10px', lineHeight: 1.4 }}>
                {saveError}
              </p>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={() => setShowEditModal(false)} disabled={saving}
                style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: saving ? 'default' : 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                style={{ flex: 1, padding: '11px', background: 'var(--neutral-800)', border: 'none', borderRadius: 'var(--radius-10)', cursor: saving ? 'default' : 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--brand-white)', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Share prompt: shown after a save that added a service ── */}
      {sharePrompt && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}
          onClick={() => { if (!sharePrompt.sharing) setSharePrompt(null); }}>
          <div
            style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 360, width: '100%', padding: 'var(--space-xxl)', textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <p style={{ fontSize: 22, margin: '0 0 8px' }}>💼</p>
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Share to community?</p>
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 20px', lineHeight: 1.5 }}>
              Let readers know you're now available for <strong>{sharePrompt.services.join(', ')}</strong>
            </p>
            {sharePrompt.error && (
              <p role="alert" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-error)', margin: '0 0 12px', lineHeight: 1.4 }}>
                {sharePrompt.error}
              </p>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={() => setSharePrompt(null)} disabled={sharePrompt.sharing}
                style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: sharePrompt.sharing ? 'default' : 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>
                Skip
              </button>
              <button onClick={shareNewServices} disabled={sharePrompt.sharing}
                style={{ flex: 1, padding: '11px', background: 'var(--neutral-800)', border: 'none', borderRadius: 'var(--radius-10)', cursor: sharePrompt.sharing ? 'default' : 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--brand-white)', opacity: sharePrompt.sharing ? 0.6 : 1 }}>
                {sharePrompt.sharing ? 'Sharing…' : 'Share to Community'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════
// ✍️ SUBSTACK
// ═══════════════════════════════════════════════════════════════
export function SubstackBlock({ profile, onUpdate, readOnly = false }) {
  const [showModal, setShowModal] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  // A feed that would not load is not the same as a feed with no posts.
  const [loadFailed, setLoadFailed] = useState(false);

  // Saved value may predate validation, so it can still be junk.
  const target = parseNewsletterUrl(profile?.substack_url);
  // When the saved link does not parse we do not know the platform, so say
  // nothing rather than guessing. Defaulting to "Substack" put that word on a
  // Medium writer's live profile — the exact thing this block was changed to
  // stop doing, sitting in the fallback where it was easy to miss.
  const platform = target?.platform || 'my newsletter';

  useEffect(() => { setUrlInput(profile?.substack_url || ''); }, [profile?.substack_url]);
  useEffect(() => { if (target) fetchPosts(target.feed); }, [profile?.substack_url]);
// `url` is the feed address from parseNewsletterUrl (target.feed), never the
// protocol-less stored address: the function reads an address with no http as a
// Substack name, so a Medium handle was fetched as somebody's Substack.
async function fetchPosts(url) {
  if (!url) return;
  setLoadingPosts(true);
  setLoadFailed(false);
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetchWithTimeout(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/substack-rss-feed`,
      {
        method: 'POST',
        headers: bookSearchHeaders(session, SUPABASE_PUBLISHABLE_KEY),
        body: JSON.stringify({ rss_url: url }),
      },
      FEED_TIMEOUT_MS
    );
    if (!res.ok) throw new Error(`Feed answered ${res.status}`);
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    setPosts((data.items || []).slice(0, 3));
  } catch (err) {
    console.warn('Substack RSS error:', err.message);
    setPosts([]);
    setLoadFailed(true);
  } finally {
    setLoadingPosts(false);
  }
}

  async function save() {
    // Validate before storing. This used to strip the protocol off whatever was
    // typed and save it, so emails and bare handles reached Reader's Corner and
    // became `https://<junk>` at click time.
    const parsed = parseNewsletterUrl(urlInput);
    if (!parsed) {
      setUrlError('Use a Substack or Medium address, like yourname.substack.com or medium.com/@yourname');
      return;
    }
    setUrlError(null);
    if (!(await saved(onUpdate({ substack_url: parsed.stored })))) {
      setUrlError(SAVE_FAILED);
      return;
    }
    await fetchPosts(parsed.feed);
    setShowModal(false);
  }

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onClick={() => { setUrlInput(profile?.substack_url || ''); setUrlError(null); setShowModal(true); }} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>✍️</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>Latest from {platform}</span>
      </div>

      {target ? (
        loadingPosts ? (
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', textAlign: 'center', padding: '16px 0' }}>Loading posts...</p>
        ) : posts.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
            {posts.map((post, i) => {
              const excerpt = post.description ? post.description.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().slice(0, 100) : null;
              return (
                <a key={i} href={post.link} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'block', padding: '10px 12px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', border: '1px solid #f0f0ef', textDecoration: 'none', transition: 'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                  onMouseLeave={e => e.currentTarget.style.background = '#faf8f5'}>
                  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.title}</p>
                  {excerpt && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 3px', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{excerpt}...</p>}
                  <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>{new Date(post.pubDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ↗</p>
                </a>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '0 0 6px' }}>{loadFailed ? <>Couldn't load posts right now.</> : <>No posts found for <strong>{target.stored}</strong></>}</p>
            {!readOnly && <button onClick={() => setShowModal(true)} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-warning)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Update URL</button>}
          </div>
        )
      ) : readOnly ? <VisitorEmpty /> : (
        <button onClick={() => setShowModal(true)} style={{ width: '100%', padding: 'var(--space-lg)', background: 'var(--brand-paper)', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>
          + Connect your Substack or Medium
        </button>
      )}

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }} onClick={() => setShowModal(false)}>
          <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 400, width: '100%', padding: 'var(--space-xxl)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: '0 0 6px' }}>✍️ Connect Substack or Medium</h3>
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 16px' }}>Your latest posts will pull automatically</p>
            <input value={urlInput} onChange={e => { setUrlInput(e.target.value); setUrlError(null); }}
              onKeyDown={e => { if (e.key === 'Enter') save(); }}
              placeholder="yourname.substack.com or medium.com/@you" autoFocus autoComplete='off'
              style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${urlError ? '#dc2626' : '#e8e4dc'}`, borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', outline: 'none', boxSizing: 'border-box', marginBottom: urlError ? 8 : 16 }} />
            {urlError && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-error)', margin: '0 0 12px', lineHeight: 1.4 }}>{urlError}</p>}
            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>Cancel</button>
              <button onClick={save} disabled={!urlInput.trim()}
                style={{ flex: 1, padding: '11px', background: urlInput.trim() ? 'var(--neutral-800)' : 'var(--brand-sand)', border: 'none', borderRadius: 'var(--radius-10)', cursor: urlInput.trim() ? 'pointer' : 'default', fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--brand-white)' }}>
                Connect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// What the two Share buttons say when the post did not go in. Same words as
// the Showcase share's alert.
const SHARE_FAILED = "That didn't post. Check your connection and try again.";

// ── ShareShelfButton ─────────────────────────────────────────
function ShareShelfButton({ shelfName, shelfEmoji, books }) {
  const [shared, setShared] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState('');

  // Finishes either way: "Shared" once the post is in, or back to the button
  // with the reason (audit 15.1). Once shared the button stays off, so a second
  // tap cannot write the post again.
  async function share() {
    if (sharing || shared) return;
    setSharing(true);
    setShareError('');
    try {
      const user = await getCurrentUser();
      if (!user) { setShareError(SHARE_FAILED); return; }
      const { error } = await supabase.from('shelf_activity_events').insert({
        user_id:     user.id,
        event_type:  'custom_shelf',
        shelf_name:  shelfName,
        shelf_emoji: shelfEmoji,
        book_title:  books.slice(0, 3).map(b => b.title).join(', ') + (books.length > 3 ? ` +${books.length - 3} more` : ''),
      });
      if (error) {
        reportSaveFailure('Share shelf to community', error);
        setShareError(SHARE_FAILED);
        return;
      }
      setShared(true);
    } catch (err) {
      reportSaveFailure('Share shelf to community', err);
      setShareError(SHARE_FAILED);
    } finally {
      setSharing(false);
    }
  }

  return (
    <>
      <button onClick={share} disabled={sharing || shared}
        style={{ width: '100%', marginTop: 'var(--space-sm)', padding: 'var(--space-sm)', background: 'transparent', border: '1px dashed var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: shared ? '#10b981' : 'var(--neutral-400)', cursor: 'pointer', transition: 'all 0.15s' }}
        onMouseEnter={e => { if (!shared) { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.color = '#d97706'; } }}
        onMouseLeave={e => { if (!shared) { e.currentTarget.style.borderColor = '#e8e4dc'; e.currentTarget.style.color = '#a8a29e'; } }}>
        {shared ? '✓ Shared to Community!' : sharing ? 'Sharing...' : '📤 Share shelf to Community'}
      </button>
      <SaveError message={shareError} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// 📚 CUSTOM SHELF
// ═══════════════════════════════════════════════════════════════
export function CustomShelfBlock({ blockData, profile, onUpdateBlock, readOnly = false }) {
  const [showModal,     setShowModal]     = useState(false);
  const [name,          setName]          = useState(blockData?.shelf_name  || '');
  const [emoji,         setEmoji]         = useState(blockData?.shelf_emoji || '📚');
  const [books,         setBooks]         = useState(blockData?.books       || []);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [outsideFailed, setOutsideFailed] = useState(false);
  const [shelfSaving,   setShelfSaving]   = useState(false);
  const [shelfError,    setShelfError]    = useState('');

  useEffect(() => {
    setName(blockData?.shelf_name  || '');
    setEmoji(blockData?.shelf_emoji || '📚');
    setBooks(blockData?.books       || []);
  }, [blockData]);

  const SHELF_EMOJIS = ['📚', '💔', '⚡', '🌙', '🔥', '💕', '🎭', '✨', '🌊', '🍂', '🎯', '💫'];

  // ── Search — Reanlo + Open Library ───────────────────────────────────────
  async function searchBooks(q) {
    if (!q.trim()) { setSearchResults([]); setOutsideFailed(false); return; }
    setSearching(true);
    setOutsideFailed(false);

    const results = [];

    // 1. Reanlo platform books
    const { data: reanloBooks } = await supabase
      .from('books')
      .select('id, title, author_name, cover_image_url')
      .or(`title.ilike.%${q}%,author_name.ilike.%${q}%`)
      .eq('status', 'live')
      .limit(5);

    (reanloBooks || []).forEach(b => results.push({ ...b, source: 'reanlo' }));

    // Show Reanlo's own books before asking Open Library. The fallback below
    // adds to this list; it is not a condition for showing it.
    setSearchResults([...results]);

    // 2. Outside sources, through the edge function rather than straight to
    // Open Library — one search path, and the Google key stays server-side.
    const ext = await searchExternalBooks(q);
    setOutsideFailed(!ext.ok);
    for (const b of ext.results) {
      const duplicate = results.some(r =>
        r.title?.toLowerCase() === b.title?.toLowerCase() &&
        r.author_name?.toLowerCase() === b.author_name?.toLowerCase()
      );
      if (duplicate) continue;
      results.push({
        id:              null,
        title:           b.title,
        author_name:     b.author_name || 'Unknown Author',
        cover_image_url: b.cover_image_url || null,
        source:          b.source || 'openlibrary',
      });
    }

    setSearchResults(results);
    setSearching(false);
  }

  function addBook(book) {
    const alreadyAdded = books.find(b =>
      b.id
        ? b.id === book.id
        : b.title?.toLowerCase() === book.title?.toLowerCase()
    );
    if (alreadyAdded) return;
    setBooks(prev => [...prev, {
      id:        book.id            || null,
      title:     book.title,
      author:    book.author_name,
      cover_url: book.cover_image_url || null,
      source:    book.source          || 'external',
    }]);
    setSearchQuery('');
    setSearchResults([]);
  }

  async function save() {
    if (!name.trim() || shelfSaving) return;
    setShelfSaving(true);
    setShelfError('');
    const ok = await saved(onUpdateBlock({ shelf_name: name, shelf_emoji: emoji, books }));
    setShelfSaving(false);
    // The shelf's name and books stay in the editor for a retry.
    if (!ok) { setShelfError(SAVE_FAILED); return; }
    setShowModal(false);
  }

  function openModal() {
    setShelfError('');
    setName(blockData?.shelf_name  || '');
    setEmoji(blockData?.shelf_emoji || '📚');
    setBooks([...(blockData?.books  || [])]);
    setSearchQuery('');
    setSearchResults([]);
    setShowModal(true);
  }

  const shelfName  = blockData?.shelf_name;
  const shelfEmoji = blockData?.shelf_emoji || '📚';
  const shelfBooks = blockData?.books || [];

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onClick={openModal} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
        <span style={{ fontSize: 'var(--font-size-xxl)', flexShrink: 0 }}>{shelfEmoji}</span>
        <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)' }}>{shelfName || 'My Shelf'}</span>
      </div>

      {shelfBooks.length > 0 ? (
        <div style={{ display: 'flex', gap: 'var(--space-6)', overflowX: 'auto', paddingBottom: 'var(--space-xs)', scrollbarWidth: 'none' }}>
          {shelfBooks.map((book, i) => (
            <div key={i} style={{ flexShrink: 0, width: 52, height: 74, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--neutral-100)', boxShadow: '1px 1px 6px rgba(0,0,0,0.1)' }}>
              {book.cover_url
                ? <img src={book.cover_url} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xl)' }}>📚</div>}
            </div>
          ))}
        </div>
      ) : readOnly ? <VisitorEmpty /> : (
        <button onClick={openModal}
          style={{ width: '100%', padding: 'var(--space-md)', background: 'var(--brand-paper)', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>
          + Add books to this shelf
        </button>
      )}

      {shelfBooks.length > 0 && shelfName && !readOnly && (
        <ShareShelfButton shelfName={shelfName} shelfEmoji={shelfEmoji} books={shelfBooks} />
      )}

      {/* ── Modal ── */}
      {showModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 500, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--neutral-100)', flexShrink: 0 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: '0 0 14px' }}>📚 Custom Shelf</h3>

              {/* Emoji picker */}
              <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap', marginBottom: 'var(--space-md)' }}>
                {SHELF_EMOJIS.map(e => (
                  <button key={e} onClick={() => setEmoji(e)}
                    style={{ width: 34, height: 34, borderRadius: 'var(--radius-md)', border: emoji === e ? '2px solid var(--feedback-warning)' : '1px solid var(--brand-sand)', background: emoji === e ? 'var(--feedback-warning-bg)' : 'var(--brand-white)', cursor: 'pointer', fontSize: 'var(--font-size-xl)' }}>
                    {e}
                  </button>
                ))}
              </div>

              {/* Shelf name */}
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Shelf name (e.g. Books That Destroyed Me)"
                autoFocus
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-md)' }}
              />

              {/* Books already added */}
              {books.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)', marginBottom: 'var(--space-md)' }}>
                  {books.map((book, i) => (
                    <div key={i} style={{ position: 'relative', width: 44, height: 62, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0 }}>
                      {book.cover_url
                        ? <img src={book.cover_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)' }}>📚</div>}
                      <button
                        onClick={() => setBooks(books.filter((_, j) => j !== i))}
                        style={{ position: 'absolute', top: -2, right: -2, width: 16, height: 16, borderRadius: 'var(--radius-circle)', background: 'var(--feedback-error)', border: 'none', color: 'var(--brand-white)', fontSize: 'var(--font-size-tiny)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Search input */}
              <input
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); searchBooks(e.target.value); }}
                placeholder="Search Books..."
                style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }}
              />
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '4px 0 0' }}>Searches Reanlo + Open Library</p>
            </div>

            {/* Results */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 24px', scrollbarWidth: 'none' }}>
              {searching && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '10px 0' }}>
                  <div style={{ width: 14, height: 14, border: '2px solid var(--brand-sand)', borderTopColor: 'var(--feedback-warning)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.8s linear infinite' }} />
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Searching...</p>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
                {searchResults
                  .filter(r => !books.find(b =>
                    b.id
                      ? b.id === r.id
                      : b.title?.toLowerCase() === r.title?.toLowerCase()
                  ))
                  .map((book, idx) => (
                  <button key={book.id || idx} onClick={() => addBook(book)}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '10px 12px', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <div style={{ width: 30, height: 44, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {book.cover_image_url
                        ? <img src={book.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                        : <span style={{ fontSize: 'var(--font-size-md)' }}>📚</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.title}</p>
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{book.author_name}</p>
                    </div>
                    <SourceBadge source={book.source || 'openlibrary'} />
                  </button>
                ))}

                {!searching && searchQuery.trim() && searchResults.length === 0 && (
                  <p style={{ fontSize: 'var(--font-size-sm)', color: outsideFailed ? '#b85c38' : 'var(--neutral-400)', textAlign: 'center', padding: '16px 0' }}>
                    {outsideFailed ? SEARCH_FAILED_MESSAGE : 'No results found'}
                  </p>
                )}

                {!searchQuery.trim() && books.length === 0 && (
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', textAlign: 'center', padding: '16px 0', fontStyle: 'italic' }}>
                    Search for books to add to your shelf
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            {shelfError && (
              <div style={{ padding: '10px 24px 0', flexShrink: 0 }}>
                <SaveError message={shelfError} style={{ margin: 0, fontSize: 'var(--font-size-sm)' }} />
              </div>
            )}
            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--neutral-100)', display: 'flex', gap: 'var(--space-10)', flexShrink: 0 }}>
              <button onClick={() => setShowModal(false)}
                style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>
                Cancel
              </button>
              <button onClick={save} disabled={!name.trim() || shelfSaving}
                style={{ flex: 1, padding: '11px', background: name.trim() ? 'var(--neutral-800)' : 'var(--brand-sand)', border: 'none', borderRadius: 'var(--radius-10)', cursor: name.trim() && !shelfSaving ? 'pointer' : 'default', fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--brand-white)', opacity: shelfSaving ? 0.6 : 1 }}>
                {shelfSaving ? 'Saving…' : 'Save Shelf'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// MiniEventMap — sits ABOVE the card div, never inside any overflow:hidden container.
// The card below should have borderRadius:'0 0 14px 14px' when the map is visible,
// and the map itself has borderRadius:'14px 14px 0 0' so they connect seamlessly.

// MiniEventMap — sits ABOVE the card div, never inside any overflow:hidden container.
// The card below should have borderRadius:'0 0 14px 14px' when the map is visible,
// and the map itself has borderRadius:'14px 14px 0 0' so they connect seamlessly.



 

// ═══════════════════════════════════════════════════════════════
// 📍 EVENTS BLOCK — combined
// ═══════════════════════════════════════════════════════════════

const ROLE_BADGE = {
  hosting:    { bg: '#B85C38', color: 'var(--brand-white)',    label: 'Hosting'    },
  going:      { bg: '#ecfdf5', color: 'var(--feedback-success)', label: 'Going'      },
  interested: { bg: '#f5f5f4', color: 'var(--neutral-500)', label: 'Interested' },
};

function EventStub({ ev, onClick }) {
  const badge = ROLE_BADGE[ev.role] || ROLE_BADGE.interested;
  return (
    <div onClick={onClick}
      style={{ display: 'flex', borderRadius: 'var(--radius-10)', overflow: 'hidden', border: '1px solid var(--brand-sand)', cursor: 'pointer', marginBottom: 'var(--space-sm)' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = '#d6d3d1'}
      onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e4dc'}>
      <div style={{ width: 8, background: ev.color || 'var(--brand-rust)', flexShrink: 0 }} />
      <div style={{ width: 6, position: 'relative', flexShrink: 0, background: 'var(--brand-white)' }}>
        {[0,9,18,27,36].map(y => (
          <div key={y} style={{ position: 'absolute', top: y+4, left: -2, width: 5, height: 5, borderRadius: 'var(--radius-circle)', background: 'var(--brand-paper)' }} />
        ))}
      </div>
      <div style={{ flex: 1, padding: '8px 12px', background: 'var(--brand-white)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', minWidth: 0 }}>
        <span style={{ fontSize: 'var(--font-size-xl)' }}>{ev.emoji || '📍'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.name}</p>
          <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>
            {formatEventDate(ev.date)}
            {(ev.city || ev.venue) ? ` · ${ev.city || ev.venue}` : ''}
          </p>
        </div>
        <span style={{ fontSize: 'var(--font-size-micro)', padding: '2px 8px', borderRadius: 6, background: badge.bg, color: badge.color, fontWeight: 700, flexShrink: 0 }}>
          {badge.label}
        </span>
      </div>
    </div>
  );
}


function MiniEventMap({ events }) {
  const mapElRef   = useRef(null);
  const leafletRef = useRef(null);
  const markersRef = useRef([]);
  const [leafletReady, setLeafletReady] = useState(!!window.L);
  const [mapReady, setMapReady]         = useState(false);
  const [isMobile, setIsMobile]         = useState(window.innerWidth < 768);
  const [tooltip, setTooltip]           = useState(null); // { ev, x, y }

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const mapEvents = (events || []).filter(e => e.lat && e.lng);

  // ── Block browser drag / text-selection on desktop ────────────────────────
  // On desktop, click+drag triggers the browser's native text-selection or
  // element-drag behaviour. This creates a transparent overlay that greys the
  // map and swallows pointer events from everything below. Preventing these
  // events at the container level gives Leaflet full control.
  useEffect(() => {
    const el = mapElRef.current;
    if (!el) return;

    const stop       = (e) => e.preventDefault();
    const stopSelect = (e) => e.preventDefault();

    el.addEventListener('mousedown',   stop,       { passive: false });
    el.addEventListener('dragstart',   stop,       { passive: false });
    el.addEventListener('selectstart', stopSelect, { passive: false });

    // Mobile: also block page scroll competing with Leaflet pan
    const blockScroll = (e) => { if (e.cancelable) e.preventDefault(); };
    el.addEventListener('touchstart', blockScroll, { passive: false });
    el.addEventListener('touchmove',  blockScroll, { passive: false });

    return () => {
      el.removeEventListener('mousedown',   stop);
      el.removeEventListener('dragstart',   stop);
      el.removeEventListener('selectstart', stopSelect);
      el.removeEventListener('touchstart',  blockScroll);
      el.removeEventListener('touchmove',   blockScroll);
    };
  }, []);

  // ── Load Leaflet ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }
    if (!document.querySelector('#leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css'; link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (!document.querySelector('#leaflet-js')) {
      const script = document.createElement('script');
      script.id = 'leaflet-js';
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => setLeafletReady(true);
      document.head.appendChild(script);
    }
  }, []);

  // ── Init map ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!leafletReady || !mapElRef.current || leafletRef.current) return;
    const L   = window.L;
    const map = L.map(mapElRef.current, {
      center: [20, 0], zoom: 2,
      zoomControl:         false,
      scrollWheelZoom:     false,
      attributionControl:  false,
      tap:                 false,
      zoomAnimation:       false,
      fadeAnimation:       false,
      markerZoomAnimation: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18, keepBuffer: 8,
    }).addTo(map);

    // Prevent Leaflet's scroll propagation to parent containers on desktop
    L.DomEvent.disableScrollPropagation(mapElRef.current);

    leafletRef.current = map;
    setMapReady(true);
  }, [leafletReady]);

  // ── Markers ───────────────────────────────────────────────────────────────
  const mapKey = mapEvents.map(e => `${e.id}:${e.lat},${e.lng}`).join('|');
  useEffect(() => {
    if (!leafletReady || !leafletRef.current || mapEvents.length === 0) return;
    const L   = window.L;
    const map = leafletRef.current;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    mapEvents.forEach(ev => {
      const color = ev.color || (ev.role === 'hosting' ? '#B85C38' : '#2D4A3E');
      let day = '';
      if (ev.date) {
        day = eventDayOfMonth(ev.date);
      }

      // Teardrop pin — border-radius:50% 50% 50% 0 rotated -45deg
      // places the sharp corner at the bottom, pointing at the location.
      const html = `
        <div style="width:26px;height:30px;position:relative;">
          <div style="
            position:absolute;top:0;left:0;
            width:26px;height:26px;
            background:${color};
            border-radius:50% 50% 50% 0;
            transform:rotate(-45deg);
            border:2.5px solid #fff;
            box-shadow:0 2px 8px rgba(0,0,0,0.22);
            display:flex;align-items:center;justify-content:center;
          ">
            <span style="
              transform:rotate(45deg);
              font-size:10px;font-weight:800;
              color:#fff;font-family:system-ui,sans-serif;
              line-height:1;
            ">${day}</span>
          </div>
        </div>`;

      const icon = L.divIcon({
        className:  '',
        html,
        iconSize:   [26, 30],
        iconAnchor: [4, 28],  // tip of the teardrop (bottom-left of rotated shape)
      });
      const marker = L.marker([ev.lat, ev.lng], { icon }).addTo(map);

      // Desktop: hover to show tooltip
      marker.on('mouseover', function(e) {
        const containerPoint = map.latLngToContainerPoint(e.latlng);
        setTooltip({ ev, x: containerPoint.x, y: containerPoint.y });
      });
      marker.on('mouseout', function() {
        setTooltip(null);
      });

      // Mobile: tap to show/hide tooltip
      marker.on('click', function(e) {
        const containerPoint = map.latLngToContainerPoint(e.latlng);
        setTooltip(prev => prev?.ev?.id === ev.id ? null : { ev, x: containerPoint.x, y: containerPoint.y });
      });

      markersRef.current.push(marker);
    });

    if (mapEvents.length > 0) {
      const bounds = L.latLngBounds(mapEvents.map(e => [e.lat, e.lng]));
      map.fitBounds(bounds, {
        paddingTopLeft:     [32, 56],
        paddingBottomRight: [32, 24],
        maxZoom: 9,
      });
    }
    setTimeout(() => leafletRef.current?.invalidateSize(), 200);
  }, [mapKey, leafletReady, mapReady]);

  // Always render the map div so Leaflet has a DOM node to attach to.
  // The markers effect skips when mapEvents is empty — the map just shows
  // as a blank world view until events load.
  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={mapElRef}
        style={{
          height:           160,
          borderRadius:     '14px 14px 0 0',
          border:           '1px solid var(--brand-sand)',
          borderBottom:     'none',
          background:       '#f0ede8',
          overflow:         'hidden',
          touchAction:      'none',
          userSelect:       'none',
          WebkitUserSelect: 'none',
          cursor:           'grab',
        }}
      />
      {!isMobile && (
        <div style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          <button
            onClick={() => leafletRef.current?.zoomIn()}
            aria-label="Zoom in"
            style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: 'var(--neutral-800)', fontSize: 15, fontWeight: 700, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }}
          >+</button>
          <button
            onClick={() => leafletRef.current?.zoomOut()}
            aria-label="Zoom out"
            style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: 'var(--neutral-800)', fontSize: 15, fontWeight: 700, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }}
          >−</button>
        </div>
      )}

      {/* ── Pin tooltip — hover on desktop, tap on mobile ── */}
      {tooltip && (() => {
        const ev = tooltip.ev;
        const dateStr = ev.date
          ? formatEventDate(ev.date, { month: 'short', day: 'numeric', year: 'numeric' })
          : '';
        // Keep tooltip inside the 160px-tall map; flip above pin if too close to bottom
        const TOOLTIP_H = 80;
        const tipY = tooltip.y - TOOLTIP_H - 36 < 0 ? tooltip.y + 16 : tooltip.y - TOOLTIP_H - 10;
        const tipX = Math.min(Math.max(tooltip.x - 90, 8), (mapElRef.current?.offsetWidth || 300) - 188);
        return (
          <div
            onClick={() => isMobile && setTooltip(null)}
            style={{
              position: 'absolute',
              left: tipX, top: tipY,
              zIndex: 1200,
              background: 'var(--brand-white)',
              borderRadius: 'var(--radius-10)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
              border: '1px solid var(--brand-sand)',
              padding: '10px 13px',
              minWidth: 180, maxWidth: 220,
              pointerEvents: isMobile ? 'auto' : 'none',
            }}
          >
            <p style={{ margin: 'var(--space-none)', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', fontFamily: 'var(--font-heading)', lineHeight: 1.3 }}>
              {ev.emoji || '📍'} {ev.name}
            </p>
            {dateStr && (
              <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', fontFamily: 'var(--font-body)' }}>
                📅 {dateStr}
              </p>
            )}
            <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xxs)', fontWeight: 600, color: ev.color || 'var(--brand-forest)', fontFamily: 'var(--font-heading)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {ev.role === 'hosting' ? 'Hosting' : ev.role === 'going' ? 'Going' : 'Interested'}
            </p>
          </div>
        );
      })()}
    </div>
  );
}

// Best-effort geocode for an event. Tries each non-empty query in order
// (most specific first) and returns the first match. Combined
// "address, venue" strings often fail to match in Nominatim, so falling
// back to address-alone or venue-alone catches most of those misses.
// A short pause between attempts respects Nominatim's 1 req/sec policy.
async function geocodeFallback(...queries) {
  const tried = new Set();
  for (const q of queries) {
    const query = (q || '').trim();
    if (!query || tried.has(query)) continue;
    tried.add(query);
    try {
      const res = await fetchWithTimeout(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
        { headers: { 'Accept-Language': 'en' } },
        5000
      );
      const geo = await res.json();
      if (geo[0]) return { lat: parseFloat(geo[0].lat), lng: parseFloat(geo[0].lon) };
    } catch { /* try next query, including a timeout */ }
    await new Promise(r => setTimeout(r, 1100));
  }
  return { lat: null, lng: null };
}

// `readOnly` is passed explicitly by PublicProfile, the same way CustomLinksBlock
// takes it, and for the same reason its comment there gives: a noop `onUpdate`
// is not enough, because a visitor must not see the editing controls at all.
//
// Relying on CSS to hide them was what this block did before, and it only ever
// worked on the desktop grid -- PublicProfile applies the `.readonly-blocks`
// class there but not to the mobile list, so on a phone a visitor saw the edit
// pencil on somebody else's events and could open the Host an Event modal from
// it. The rule hides exactly one button, too, so the delete control was never
// covered by it on any screen.
export function EventsBlock({ profile, readOnly = false }) {
  const [myEvents,         setMyEvents]         = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [currentUserId,    setCurrentUserId]    = useState(null);

  // Modals
  const [showAddModal,     setShowAddModal]     = useState(false);
  const [showMyEvents,     setShowMyEvents]     = useState(false); // user's own events "see more"
  const [showBrowser,      setShowBrowser]      = useState(false); // EventsBrowser
  const [viewEvent,        setViewEvent]        = useState(null);

  // Every upcoming event that has coordinates, for the map's pins when this
  // reader has none of their own.
  //
  // This used to be overwritten by the browse popup, which loaded `posts` rows
  // into the same state. Those keep lat/lng inside `event_data` rather than at
  // the top level, so MiniEventMap's `e.lat && e.lng` filter dropped all of
  // them: opening Browse once and closing it emptied the map. The browser owns
  // its own data now, so nothing else writes here.
  const [allEvents,        setAllEvents]        = useState([]);

  // Add modal — starts directly at hosting form
  const [confirmed,        setConfirmed]        = useState(false);
  // What the success screen is allowed to claim actually happened. Both start
  // false so a path that forgets to set them under-claims rather than lies.
  const [feedPosted,       setFeedPosted]       = useState(false);
  const [mapPinned,        setMapPinned]        = useState(false);
  const [saving,           setSaving]           = useState(false);

  const [form, setForm] = useState({
    name: '', date: '', time: '', venue: '', address: '',
    description: '', ticket_link: '', emoji: '📚',
  });
  const EMOJI_OPTS = ['📚','🎤','🌙','✍️','📖','🎊','☕','🎭','🕯️','🌿','🎪','🐉'];
  const PREVIEW = 3;

  const isOwner = !!currentUserId && (!profile?.user_id || profile?.user_id === currentUserId);

  // Computed since this block was written and never once referenced, which is
  // why every control in it was reachable by anyone. `currentUserId` arrives
  // from an async getCurrentUser(), so this is false on the first render and
  // for every signed-out visitor -- both of which should see no controls, so
  // the safe answer is also the default one.
  //
  // What this does NOT gate, deliberately:
  //   - "Browse events": reading is not editing.
  //   - Going / Interested in the browse list: those insert a row for the
  //     VIEWER (handleBrowseRsvp writes user_id: user.id), so they add to your
  //     own events rather than touching the profile owner's. Finding an event
  //     on someone else's profile and saying you are going is the feature.
  const canEdit = !readOnly && isOwner;

  useEffect(() => {
    getCurrentUser().then((user) => {
      if (user) setCurrentUserId(user.id);
    });
  }, []);

  useEffect(() => { fetchMyEvents(); }, [profile]);

  // Every upcoming event with coordinates, for the map pins when this reader
  // has none of their own.
  //
  // excludeRsvpCopies() keeps a reader's RSVP copy from becoming a second pin
  // on top of the original. The lat filter does NOT already do that: both RSVP
  // paths copy the original's coordinates across (Community.jsx:1041, :1565),
  // so a copy of a placed event is placed too and would draw its own pin.
  //
  // The map draws markers, so it only needs the fields a marker uses. select('*')
  // pulled every description on the platform across the wire to render dots.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await onlyUpcoming(excludeRsvpCopies(supabase
          .from('profile_events')
          // Exactly the fields MiniEventMap reads — id, name, date, lat, lng,
          // emoji, color, role. `role` is easy to miss: it is the fallback the
          // marker colour uses when an event has no `color` of its own.
          .select('id, name, date, lat, lng, emoji, color, role'))
          .not('lat', 'is', null))
          .order('date', { ascending: true })
          .limit(500);
        setAllEvents(data || []);
      } catch (err) {
        console.error('EventsBlock map prefetch error:', err.message);
      }
    })();
  }, []);

  async function fetchMyEvents() {
    setLoading(true);
    try {
      const user = await getCurrentUser();
      const userId = profile?.user_id || user?.id;
      if (!userId) { setLoading(false); return; }
      const { data } = await onlyUpcoming(supabase
        .from('profile_events')
        .select('*')
        .eq('user_id', userId))
        .order('date', { ascending: true });
      setMyEvents(data || []);
    } catch (err) {
      console.error('EventsBlock fetch error:', err.message);
    } finally {
      setLoading(false);
    }
  }

  function resetModal() {
    setConfirmed(false);
    setSaving(false);
    setForm({ name: '', date: '', time: '', venue: '', address: '', description: '', ticket_link: '', emoji: '📚' });
  }

  async function saveHostedEvent() {
    if (!form.name.trim() || !form.date || saving) return;
    setSaving(true);
    try {
      const user = await getCurrentUser();
      const { lat, lng } = await geocodeFallback(form.address);
      const { data, error } = await supabase.from('profile_events').insert({
        user_id:     user.id,
        name:        form.name,
        date:        form.date,
        time:        form.time        || null,
        venue:       form.venue       || null,
        address:     form.address     || null,
        description: form.description || null,
        role:        'hosting',
        ticket_link: form.ticket_link || null,
        emoji:       form.emoji,
        color:       'var(--brand-rust)',
        lat, lng,
      }).select().single();
      if (error) throw error;

      // post_type was 'text'. The Community events tab selects
      // post_type === 'event' (Community.jsx:3502, :3725, useCommunity.js:47),
      // so a hosted event has never once reached the feed the success screen
      // below claims it reached.
      const { error: postErr } = await supabase.from('posts').insert({
        user_id:    user.id,
        post_type:  'event',
        content:    `📍 Hosting: ${form.name}${form.venue ? ` at ${form.venue}` : ''}`,
        event_data: {
          title: form.name, date: form.date, time: form.time,
          location: form.venue, role: 'hosting', emoji: form.emoji,
          color: 'var(--brand-rust)', lat, lng, ticket_link: form.ticket_link,
          profile_event_id: data.id,
        },
      });
      // Not thrown: the event itself saved, and losing the feed post is not a
      // reason to tell someone their event failed. But it is a reason not to
      // tick "Posted to community feed" at them.
      if (postErr) console.error('EventsBlock: community post failed:', postErr.message);

      setMyEvents(prev => [...prev, data].sort((a, b) => new Date(a.date) - new Date(b.date)));
      // Both of these were printed as unconditional ✓ marks. A geocode that
      // found nothing returns { lat: null, lng: null }, so "Pinned to map" was
      // just as capable of being false as the feed line.
      setFeedPosted(!postErr);
      setMapPinned(lat != null && lng != null);
      setConfirmed(true);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  }


  // The row came out of local state unconditionally, so a delete RLS refused
  // still looked like it worked -- the stub vanished, and came back on the next
  // reload. "Users manage own events" (live_schema_snapshot.sql:5518) matches
  // only auth.uid() = user_id, and Supabase RETURNS that refusal rather than
  // throwing it, so nothing here ever saw it.
  //
  // A delete that matches no row is not an error either: Postgres reports
  // success having deleted nothing. That is why this checks the returned rows
  // rather than only the error -- "it did not fail" and "it deleted something"
  // are different facts.
  async function deleteEvent(id) {
    const { data: removed, error } = await supabase
      .from('profile_events')
      .delete()
      .eq('id', id)
      .select('id');

    if (error) {
      alert('Could not delete that event: ' + error.message);
      return;
    }
    if (!removed?.length) {
      alert('That event was not deleted, it is not yours to remove.');
      return;
    }

    setMyEvents(prev => prev.filter(e => e.id !== id));
    setViewEvent(null);
  }



  return (
    <>
      {/* ══════ BLOCK ══════ */}
      <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', overflow: 'hidden', position: 'relative' }}>
        {canEdit && <EDIT_BTN onClick={() => { resetModal(); setShowAddModal(true); }} />}

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `14px ${canEdit ? EDGE_CLEAR_OF_EDIT : 16}px 10px 16px` }}>
          <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>📍 My Events</p>
        </div>

        {loading && <div style={{ height: 80, margin: '0 16px 16px', background: 'var(--neutral-100)', borderRadius: 'var(--radius-10)' }} />}

        {/* ── Map — always visible; falls back to all platform events when user has none ── */}
        {!loading && (
          <div>
            <MiniEventMap
              events={myEvents.length > 0 ? myEvents : allEvents}
            />

            {/* Empty state CTA — shown below the map when user has no events */}
            {myEvents.length === 0 && (
              <div style={{ padding: 'var(--space-lg)', textAlign: 'center' }}>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 12px' }}>
                  No upcoming events yet. Hosting something? Going somewhere bookish?
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'center' }}>
                  {canEdit && (
                    <button onClick={() => { resetModal(); setShowAddModal(true); }}
                      style={{ padding: '8px 16px', background: 'var(--brand-rust)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>
                      Host an event
                    </button>
                  )}
                  <button onClick={() => setShowBrowser(true)}
                    style={{ padding: '8px 16px', background: 'var(--brand-white)', color: 'var(--neutral-800)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>
                    Browse events
                  </button>
                </div>
              </div>
            )}

            {/* Ticket stubs — max 3 */}
            <div style={{ padding: '12px 16px 8px' }}>
              {myEvents.slice(0, PREVIEW).map(ev => (
                <EventStub key={ev.id} ev={ev} onClick={() => setViewEvent(ev)} />
              ))}
            </div>

            {/* Footer buttons */}
            <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              {myEvents.length > PREVIEW && (
                <button onClick={() => setShowMyEvents(true)}
                  style={{ width: '100%', padding: 'var(--space-sm)', background: 'transparent', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--neutral-500)', cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--neutral-800)'; e.currentTarget.style.color = 'var(--neutral-800)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--brand-sand)'; e.currentTarget.style.color = 'var(--neutral-500)'; }}>
                   + {myEvents.length - PREVIEW} more
                </button>
              )}

              {/* The landing block had no add/host button outside its empty
                  state -- only the pencil, which a visitor does not see. */}
              {canEdit && (
                <button onClick={() => { resetModal(); setShowAddModal(true); }}
                  style={{ width: '100%', padding: 'var(--space-10)', background: 'var(--brand-rust)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' }}>
                  + Add or Host an Event
                </button>
              )}

              <div style={{ textAlign: 'center' }}>
                <button onClick={() => setShowBrowser(true)}
                  style={{ fontSize: 'var(--font-size-xs)', color: 'var(--brand-rust)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
                  Browse all events →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══════ MY EVENTS MODAL (see all 9) ══════ */}
      {showMyEvents && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)' }}
          onClick={() => setShowMyEvents(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'relative', width: 420, maxHeight: '82vh', background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--neutral-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>📍 My Events</h3>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '2px 0 0' }}>{myEvents.length} event{myEvents.length !== 1 ? 's' : ''}</p>
              </div>
              <button onClick={() => setShowMyEvents(false)}
                style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', color: 'var(--neutral-400)', cursor: 'pointer', padding: 'var(--space-none)', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px 20px', scrollbarWidth: 'none' }}>
              {myEvents.map(ev => (
                <EventStub key={ev.id} ev={ev} onClick={() => { setShowMyEvents(false); setViewEvent(ev); }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══════ EVENT DETAIL POPUP ══════ */}
      {viewEvent && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)' }}
          onClick={() => setViewEvent(null)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'relative', width: 380, background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: viewEvent.color || 'var(--brand-rust)', padding: '24px 24px 20px', position: 'relative' }}>
              <div style={{ position: 'absolute', inset: 0, opacity: 0.06, background: 'repeating-linear-gradient(45deg,var(--brand-white) 0,var(--brand-white) 1px,transparent 1px,transparent 6px)' }} />
              <button onClick={() => setViewEvent(null)}
                style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 'var(--radius-circle)', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--brand-white)', fontSize: 'var(--font-size-lg)' }}>×</button>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h3)' }}>
                  {viewEvent.emoji || '📍'}
                </div>
                <div>
                  <h2 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--brand-white)', margin: '0 0 4px', fontFamily: 'var(--font-display)' }}>{viewEvent.name}</h2>
                  <span style={{ fontSize: 'var(--font-size-xxs)', padding: '2px 10px', borderRadius: 'var(--radius-md)', background: 'rgba(255,255,255,0.2)', color: 'var(--brand-white)', fontWeight: 600 }}>
                    {ROLE_BADGE[viewEvent.role]?.label || viewEvent.role}
                  </span>
                </div>
              </div>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-10)', marginBottom: 'var(--space-lg)' }}>
                {viewEvent.date && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-10)' }}>
                    <span style={{ fontSize: 'var(--font-size-lg)', marginTop: 1 }}>📅</span>
                    <div>
                      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>
                        {formatEventDate(viewEvent.date, { weekday: 'long', month: 'long', day: 'numeric' })}
                      </p>
                      {viewEvent.time && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{viewEvent.time}</p>}
                    </div>
                  </div>
                )}
                {(viewEvent.venue || viewEvent.address) && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-10)' }}>
                    <span style={{ fontSize: 'var(--font-size-lg)', marginTop: 1 }}>📍</span>
                    <div>
                      {viewEvent.venue && <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>{viewEvent.venue}</p>}
                      {viewEvent.address && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{viewEvent.address}</p>}
                    </div>
                  </div>
                )}
              </div>
              {viewEvent.description && (
                <div style={{ padding: '12px 14px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--space-lg)' }}>
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', margin: 'var(--space-none)', lineHeight: 1.55 }}>{viewEvent.description}</p>
                </div>
              )}
              {viewEvent.ticket_link && (
                <a href={ensureUrl(viewEvent.ticket_link)} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-6)', padding: 'var(--space-10)', background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', textDecoration: 'none', marginBottom: 'var(--space-md)' }}>
                  🎟️ View Tickets / RSVP <span style={{ color: 'var(--neutral-400)' }}>↗</span>
                </a>
              )}
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                {/* The one control the `.readonly-blocks` CSS never covered: it
                    matches on `right: 36px`, which is the edit pencil's inline
                    style, so this deletes-someone-else's-event button was live
                    on a visitor's screen on every breakpoint. RLS refuses the
                    delete, but the row still vanished from their view -- see
                    deleteEvent, which drops it from local state whether or not
                    the database agreed. */}
                {canEdit && (
                  <button onClick={() => deleteEvent(viewEvent.id)}
                    style={{ flex: 1, padding: '11px', background: 'var(--brand-white)', color: 'var(--feedback-error)', border: '1px solid #fecaca', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer' }}>
                    {viewEvent.role === 'going' ? "Can't make it" : viewEvent.role === 'interested' ? 'Remove' : 'Cancel event'}
                  </button>
                )}
                <button onClick={() => setViewEvent(null)}
                  style={{ flex: 1, padding: '11px', background: 'var(--neutral-800)', color: 'var(--brand-paper)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════ BROWSE BY LOCATION ══════ */}
      {showBrowser && (
        <EventsBrowser
          onClose={() => setShowBrowser(false)}
          onHostEvent={() => { resetModal(); setShowAddModal(true); }}
          onRsvpAdded={fetchMyEvents}
        />
      )}

      {/* ══════ HOST EVENT MODAL (direct — no role picker) ══════ */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)' }}
          onClick={() => setShowAddModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'relative', width: 400, maxHeight: '85vh', background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>

            <div style={{ padding: '20px 24px 16px', position: 'sticky', top: 0, background: 'var(--brand-white)', zIndex: 5, borderBottom: '1px solid var(--neutral-100)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xxl)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>🎤 Host an Event</h2>
                  <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Saves to profile · posts to community · pins to map.</p>
                </div>
                <button onClick={() => setShowAddModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', color: 'var(--neutral-400)', cursor: 'pointer', padding: 'var(--space-none)', lineHeight: 1 }}>×</button>
              </div>
            </div>

            <div style={{ padding: '16px 24px 24px' }}>
              {!confirmed ? (
                <>
                  {[
                    { label: 'Event name *',              field: 'name',        placeholder: 'e.g. Toronto Book Swap' },
                    { label: 'Venue',                     field: 'venue',       placeholder: 'Venue name' },
                    { label: 'Full address (for map pin)',field: 'address',     placeholder: '123 Street, City' },
                    { label: 'Ticket / RSVP link (opt.)', field: 'ticket_link', placeholder: 'https://...' },
                  ].map(f => (
                    <div key={f.field} style={{ marginBottom: 'var(--space-md)' }}>
                      <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', display: 'block', marginBottom: 'var(--space-xs)' }}>{f.label}</label>
                      <input value={form[f.field]} onChange={e => setForm(p => ({ ...p, [f.field]: e.target.value }))}
                        placeholder={f.placeholder}
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  ))}

                  <div style={{ display: 'flex', gap: 'var(--space-10)', marginBottom: 'var(--space-md)' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', display: 'block', marginBottom: 'var(--space-xs)' }}>Date *</label>
                      <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', display: 'block', marginBottom: 'var(--space-xs)' }}>Time</label>
                      <input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))}
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  </div>

                  <div style={{ marginBottom: 'var(--space-md)' }}>
                    <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', display: 'block', marginBottom: 'var(--space-6)' }}>Event emoji</label>
                    <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                      {EMOJI_OPTS.map(e => (
                        <button key={e} onClick={() => setForm(p => ({ ...p, emoji: e }))}
                          style={{ width: 36, height: 36, borderRadius: 'var(--radius-10)', border: form.emoji === e ? '2px solid var(--feedback-warning)' : '1px solid var(--brand-sand)', background: form.emoji === e ? 'var(--feedback-warning-bg)' : 'var(--brand-white)', fontSize: 'var(--font-size-xl)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: 'var(--space-xl)' }}>
                    <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', display: 'block', marginBottom: 'var(--space-xs)' }}>Description (optional)</label>
                    <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="What should people know?" rows={3}
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  </div>

                  <button onClick={saveHostedEvent} disabled={!form.name.trim() || !form.date || saving}
                    style={{ width: '100%', padding: '13px', background: form.name.trim() && form.date ? 'var(--brand-rust)' : 'var(--brand-sand)', color: form.name.trim() && form.date ? 'var(--brand-white)' : 'var(--neutral-400)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: 'pointer' }}>
                    {saving ? 'Saving...' : 'Post Event'}
                  </button>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-circle)', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', border: '2px solid #a7f3d0', fontSize: 'var(--font-size-h4)' }}>✓</div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Event created!</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', margin: '8px 0 16px' }}>
                    {/* "Added to your profile" is the one claim that is always
                        true here: the insert above throws on error, so getting
                        this far means the row landed. The other two are things
                        that can quietly not happen, and each now says which. */}
                    <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-success)' }}>✓ Added to your profile</span>
                    <span style={{ fontSize: 'var(--font-size-xxs)', color: feedPosted ? 'var(--feedback-success)' : 'var(--neutral-500)' }}>
                      {feedPosted ? '✓ Posted to community feed' : '· Not posted to the community feed'}
                    </span>
                    <span style={{ fontSize: 'var(--font-size-xxs)', color: mapPinned ? 'var(--feedback-success)' : 'var(--neutral-500)' }}>
                      {mapPinned ? '✓ Pinned to map' : '· No map pin, we could not place that address'}
                    </span>
                  </div>
                  <button onClick={() => { setShowAddModal(false); resetModal(); }}
                    style={{ padding: '10px 28px', background: 'var(--neutral-800)', color: 'var(--brand-paper)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer' }}>
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
// ═══════════════════════════════════════════════════════════════
// 🎨 SHOWCASE
// ═══════════════════════════════════════════════════════════════
export function ShowcaseBlock({ profile, onUpdate, readOnly = false }) {
  const [showModal, setShowModal] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [form, setForm] = useState({ title: '', link: '' });
  const [uploading, setUploading] = useState(false);
  const [showcaseError, setShowcaseError] = useState('');
  const fileRef = React.useRef();
  const items = profile?.showcase_items || [];
  useEffect(() => { if (showModal) setShowcaseError(''); }, [showModal]);

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const user = await getCurrentUser();
      const ext = file.name.split('.').pop();
      const path = `showcase/${user.id}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
      setForm(f => ({ ...f, imageUrl: publicUrl }));
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  }

  async function addItem() {
    if (!form.imageUrl || !form.title.trim()) return;
    if (items.length >= 5) { alert('Max 5 showcase items'); return; }
    setShowcaseError('');
    if (!(await saved(onUpdate({ showcase_items: [...items, { url: form.imageUrl, title: form.title, link: form.link }] })))) {
      setShowcaseError(SAVE_FAILED);   // the image, title and link stay in the form
      return;
    }
    setForm({ title: '', link: '', imageUrl: '' });
  }

  async function removeItem(idx) {
    setShowcaseError('');
    if (!(await saved(onUpdate({ showcase_items: items.filter((_, i) => i !== idx) })))) {
      setShowcaseError("That didn't remove. Check your connection and try again.");
    }
  }

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onClick={() => setShowModal(true)} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>🎨</span>
        <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: 1, textTransform: 'uppercase' }}>Showcase</span>
      </div>
      {items.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
          {items.map((item, i) => (
            <div key={i} onClick={() => setLightbox(item)}
              style={{ position: 'relative', borderRadius: 'var(--radius-10)', overflow: 'hidden', cursor: 'pointer', aspectRatio: '16/9', background: 'var(--neutral-100)' }}>
              <img src={item.url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.7)', padding: '16px 10px 8px' }}>
                <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--brand-white)', margin: 'var(--space-none)' }}>{item.title}</p>
              </div>
            </div>
          ))}
        </div>
      ) : readOnly ? <VisitorEmpty /> : (
        <button onClick={() => setShowModal(true)} style={{ width: '100%', padding: 'var(--space-xl)', background: 'var(--brand-paper)', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>
          + Add your creative work
        </button>
      )}

      {lightbox && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 'var(--space-xl)' }} onClick={() => setLightbox(null)}>
          <button style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 'var(--radius-circle)', width: 36, height: 36, color: 'var(--brand-white)', fontSize: 'var(--font-size-xl)', cursor: 'pointer' }} onClick={() => setLightbox(null)}>✕</button>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh' }}>
            <img src={lightbox.url} alt={lightbox.title} style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 'var(--radius-10)', objectFit: 'contain' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-md)' }}>
              <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--brand-white)', fontWeight: 700, margin: 'var(--space-none)' }}>{lightbox.title}</p>
              {lightbox.link && <a href={lightbox.link.startsWith('http') ? lightbox.link : `https://${lightbox.link}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--font-size-base)', color: 'var(--feedback-warning)', fontWeight: 600, textDecoration: 'none' }}>View full work ↗</a>}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }} onClick={() => setShowModal(false)}>
          <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 480, width: '100%', padding: 'var(--space-xxl)', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: '0 0 16px' }}>🎨 Showcase</h3>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '0 0 12px' }}>{items.length}/5 pieces</p>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '8px 10px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-6)' }}>
                <img src={item.url} alt={item.title} style={{ width: 48, height: 36, objectFit: 'cover', borderRadius: 6 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</p>
                  {item.link && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.link}</p>}
                </div>
                <button onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: 'var(--feedback-error)', cursor: 'pointer', fontSize: 'var(--font-size-lg)', flexShrink: 0 }}>✕</button>
              </div>
            ))}
            {items.length < 5 && (
              <div style={{ marginTop: items.length > 0 ? 12 : 0 }}>
                <div onClick={() => fileRef.current?.click()}
                  style={{ width: '100%', aspectRatio: '16/9', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-10)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: form.imageUrl ? 'none' : 'var(--brand-paper)', overflow: 'hidden', marginBottom: 'var(--space-10)' }}>
                  {form.imageUrl ? <img src={form.imageUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <><span style={{ fontSize: 'var(--font-size-h3)', marginBottom: 'var(--space-6)' }}>🖼️</span><p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>{uploading ? 'Uploading...' : 'Click to upload image (JPG/PNG)'}</p></>}
                </div>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png" onChange={handleUpload} style={{ display: 'none' }} />
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Title / description *"
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-sm)' }} />
                <input value={form.link} onChange={e => setForm(f => ({ ...f, link: e.target.value }))} placeholder="Link to full work (Instagram, Etsy, etc.)"
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-10)' }} />
                <button onClick={addItem} disabled={!form.imageUrl || !form.title.trim()}
                  style={{ width: '100%', padding: 'var(--space-10)', background: form.imageUrl && form.title.trim() ? 'var(--neutral-800)' : 'var(--brand-sand)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: form.imageUrl && form.title.trim() ? 'pointer' : 'default', color: 'var(--brand-white)' }}>
                  Add to Showcase
                </button>
              </div>
            )}
            <SaveError message={showcaseError} style={{ margin: '8px 0 0', fontSize: 'var(--font-size-sm)' }} />
            <button onClick={() => setShowModal(false)} style={{ width: '100%', marginTop: 'var(--space-10)', padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>Done</button>
          </div>
        </div>
      )}

      {items.length > 0 && !showModal && !readOnly && (
        <button onClick={async () => {
          const user = await getCurrentUser();
          if (!user) return;
          const latest = items[items.length - 1];
          const { error } = await supabase.from('posts').insert({
            user_id: user.id, post_type: 'text',
            content: `🎨 New in my showcase: ${latest.title}${latest.link ? `, ${latest.link}` : ''}`,
            book_id: null,
          });
          // This said "Shared to community!" whether or not the post went in
          // (audit 12.2).
          if (error) {
            reportSaveFailure('showcase share', error);
            alert("That didn't post. Check your connection and try again.");
            return;
          }
          alert('Shared to community!');
        }}
          style={{ width: '100%', marginTop: 'var(--space-sm)', padding: 'var(--space-sm)', background: 'transparent', border: '1px dashed var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', cursor: 'pointer' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.color = '#d97706'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e4dc'; e.currentTarget.style.color = '#a8a29e'; }}>
          📤 Share latest piece to Community
        </button>
      )}
    </div>
  );
}

// ── ShareRecButton ────────────────────────────────────────────
function ShareRecButton({ recommendations }) {
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [shareError, setShareError] = useState('');

  // Finishes either way (audit 13.2): on success it says "Shared!" and closes
  // the picker; on a failure it says so and leaves the picker to try again.
  // Once shared the button stays off, so a second tap cannot write it again.
  async function share(rec) {
    if (sharing || shared) return;
    setSharing(true);
    setShareError('');
    try {
      const user = await getCurrentUser();
      if (!user) { setShareError(SHARE_FAILED); return; }
      const { error } = await supabase.from('shelf_activity_events').insert({
        user_id:        user.id,
        event_type:     'recommend_me',
        book_id:        rec.book_id || null,
        book_title:     rec.title,
        book_author:    rec.author,
        book_cover_url: rec.cover_url || null,
      });
      if (error) {
        reportSaveFailure('Share recommendation to community', error);
        setShareError(SHARE_FAILED);
        return;
      }
      setShared(true);
      setShowPicker(false);
    } catch (err) {
      reportSaveFailure('Share recommendation to community', err);
      setShareError(SHARE_FAILED);
    } finally {
      setSharing(false);
    }
  }

  return (
    <div style={{ position: 'relative', marginTop: 'var(--space-sm)' }}>
      <button onClick={() => setShowPicker(p => !p)} disabled={shared}
        style={{ width: '100%', padding: 'var(--space-sm)', background: 'transparent', border: '1px dashed var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: shared ? '#10b981' : 'var(--neutral-400)', cursor: 'pointer', transition: 'all 0.15s' }}
        onMouseEnter={e => { if (!shared) { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.color = '#d97706'; } }}
        onMouseLeave={e => { if (!shared) { e.currentTarget.style.borderColor = '#e8e4dc'; e.currentTarget.style.color = '#a8a29e'; } }}>
        {shared ? '✓ Shared!' : '📤 Share a recommendation to Community'}
      </button>
      {showPicker && (
        <div style={{ position: 'absolute', bottom: '110%', left: 0, right: 0, background: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--brand-sand)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', zIndex: 10 }}>
          <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-400)', padding: '8px 12px 4px', margin: 'var(--space-none)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pick one to share</p>
          {recommendations.map((rec, i) => (
            <button key={i} onClick={() => share(rec)} disabled={sharing}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', width: '100%', padding: '8px 12px', background: 'none', border: 'none', borderTop: '1px solid var(--neutral-100)', cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              {rec.cover_url && <img src={rec.cover_url} alt="" style={{ width: 24, height: 34, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} onError={onCoverError} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rec.title}</p>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>"{rec.pitch}"</p>
              </div>
            </button>
          ))}
        </div>
      )}
      <SaveError message={shareError} />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// 📦 AMAZON STOREFRONT
// ═══════════════════════════════════════════════════════════════
// A storefront list goes out as the reader saved it. Reanlo adds no tag of its
// own here: this used to swap whatever tag the reader had put on the link for
// Reanlo's, so their commission went to Reanlo (and the two tags lived in this
// file as well as in retailers.js). The Community shop tab already does it this
// way (useReaderCorner.js).
function storefrontHref(url) {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

export function AmazonStorefrontBlock({ profile, onUpdate, readOnly = false }) {
  const [showModal, setShowModal]       = useState(false);
  const [lists, setLists]               = useState([]);
  const [displayName, setDisplayName]   = useState('');
  const [newListName, setNewListName]   = useState('');
  const [newListUrl, setNewListUrl]     = useState('');
  const [newListIcon, setNewListIcon]   = useState('📚');
  const [saving, setSaving]             = useState(false);
  const [storefrontError, setStorefrontError] = useState('');

  const LIST_ICONS  = ['📚', '⭐', '🛋️', '🎁', '🕯️', '🎨', '✏️', '🌿'];
  const LIST_COLORS = ['#f5f0e8', '#fef3c7', '#ede9fe', '#f0fdf4', '#fef2e2', '#f0f9ff', '#fff1f2', '#f5f5f4'];

  const data         = profile?.amazon_storefront;
  const enabledLists = (data?.lists || []).filter(l => l.enabled);

  useEffect(() => {
    if (showModal) {
      setDisplayName(data?.display_name || 'My Storefront');
      setLists(data?.lists || []);
      setStorefrontError('');
    }
  }, [showModal]);

  function addList() {
    if (!newListName.trim() || !newListUrl.trim()) return;
    const colorIdx = lists.length % LIST_COLORS.length;
    const cleaned  = newListUrl.trim().startsWith('http') ? newListUrl.trim() : `https://${newListUrl.trim()}`;
    setLists(prev => [...prev, {
      name:    newListName.trim(),
      url:     cleaned,
      icon:    newListIcon,
      color:   LIST_COLORS[colorIdx],
      enabled: true,
    }]);
    setNewListName('');
    setNewListUrl('');
    setNewListIcon('📚');
  }

  function toggleList(idx) {
    setLists(prev => prev.map((l, i) => i === idx ? { ...l, enabled: !l.enabled } : l));
  }

  function removeList(idx) {
    setLists(prev => prev.filter((_, i) => i !== idx));
  }

  async function save() {
    if (lists.length === 0 || saving) return;
    setSaving(true);
    setStorefrontError('');
    try {
      // onUpdate answers a failure with { error } rather than throwing, so the
      // catch below never saw one: the editor closed as if it had worked and
      // the feed post still went out (audit 16.2).
      const ok = await saved(onUpdate({
        amazon_storefront: {
          display_name: displayName.trim() || 'My Storefront',
          lists,
        }
      }));
      if (!ok) { setStorefrontError(SAVE_FAILED); return; }
      // Saving posts nothing to the community feed. It used to announce the
      // storefront on every save, however small the change (audit 16.1).
      setShowModal(false);
    } catch (err) {
      console.error('Storefront save error:', err.message);
      setStorefrontError(SAVE_FAILED);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', overflow: 'hidden', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onDark onClick={() => setShowModal(true)} />}

      {/* Dark Amazon-style header */}
      <div style={{ background: 'linear-gradient(135deg, #232f3e, #131921)', padding: `12px ${readOnly ? 16 : EDGE_CLEAR_OF_EDIT}px 12px 16px`, display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
        <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>📦</span>
        <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--brand-paper)' }}>
          {data?.display_name || 'My Storefront'}
        </span>
      </div>

      <div style={{ padding: '14px 16px' }}>
        {enabledLists.length > 0 ? (
          <>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 10px' }}>Curated picks from my Amazon storefront</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', marginBottom: 'var(--space-md)' }}>
              {enabledLists.map((list, i) => (
                <a
                  key={i}
                  href={storefrontHref(list.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '8px 10px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', border: '1px solid #f0f0ef', textDecoration: 'none', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                  onMouseLeave={e => e.currentTarget.style.background = '#faf8f5'}
                >
                  <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: list.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 'var(--font-size-lg)' }}>{list.icon}</span>
                  </div>
                  <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {list.name}
                  </span>
                  <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning)', fontWeight: 600 }}>View ↗</span>
                </a>
              ))}
            </div>

            {/* Affiliate disclosure: the links are the reader's own, so it is the reader who may earn. */}
            <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '8px 0 0', lineHeight: 1.4 }}>
              Affiliate links. @{profile?.username} may earn from qualifying purchases.
            </p>
          </>
        ) : readOnly ? <VisitorEmpty /> : (
          <button onClick={() => setShowModal(true)}
            style={{ width: '100%', padding: 'var(--space-lg)', background: 'var(--brand-paper)', border: '1.5px dashed var(--brand-sand)', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>
            + Connect your Amazon Storefront
          </button>
        )}
      </div>

      {/* ── Modal ── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-xl)' }}
          onClick={() => setShowModal(false)}>
          <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 500, width: '100%', padding: 'var(--space-xxl)', maxHeight: '85vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>

            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', margin: '0 0 6px' }}>📦 Amazon Storefront</h3>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 18px', lineHeight: 1.5 }}>
              Connect your Amazon storefront to showcase curated lists. Visitors who shop through your link support you at no extra cost.
            </p>

            {/* Display name */}
            <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Display name</p>
            <input
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="My Storefront"
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 18 }}
            />

            {/* Existing lists */}
            <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>Curated lists</p>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 10px' }}>Each list links to its own Amazon URL, paste the direct list link.</p>

            {lists.map((list, i) => (
              <div key={i}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '9px 12px', border: list.enabled ? '1.5px solid var(--feedback-warning)' : '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', marginBottom: 'var(--space-6)', background: list.enabled ? '#fefce8' : 'var(--brand-white)', cursor: 'pointer' }}
                onClick={() => toggleList(i)}
              >
                <div style={{ width: 20, height: 20, borderRadius: 5, border: list.enabled ? '2px solid var(--feedback-warning)' : '2px solid var(--neutral-300)', background: list.enabled ? 'var(--feedback-warning)' : 'var(--brand-white)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {list.enabled && <span style={{ color: 'var(--brand-white)', fontSize: 'var(--font-size-xxs)', fontWeight: 800 }}>✓</span>}
                </div>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: list.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 'var(--font-size-md)' }}>{list.icon}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{list.name}</p>
                  <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{list.url}</p>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); removeList(i); }}
                  style={{ background: 'none', border: 'none', color: 'var(--neutral-300)', cursor: 'pointer', fontSize: 'var(--font-size-md)', padding: '0 4px', flexShrink: 0 }}
                  onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                  onMouseLeave={e => e.currentTarget.style.color = '#d6d3d1'}
                >✕</button>
              </div>
            ))}

            {/* Add new list */}
            <div style={{ marginTop: 'var(--space-14)', padding: 'var(--space-14)', background: 'var(--brand-paper)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--brand-sand)' }}>
              <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 10px' }}>Add a list</p>

              {/* Icon picker */}
              <div style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 'var(--space-10)' }}>
                {LIST_ICONS.map(ic => (
                  <button key={ic} onClick={() => setNewListIcon(ic)}
                    style={{ width: 30, height: 30, borderRadius: 7, border: newListIcon === ic ? '2px solid var(--feedback-warning)' : '1px solid var(--brand-sand)', background: newListIcon === ic ? 'var(--feedback-warning-bg)' : 'var(--brand-white)', cursor: 'pointer', fontSize: 'var(--font-size-md)' }}>
                    {ic}
                  </button>
                ))}
              </div>

              <input
                value={newListName}
                onChange={e => setNewListName(e.target.value)}
                placeholder="List name (e.g. My All-Time Favorites)"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-sm)' }}
              />
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <input
                  value={newListUrl}
                  onChange={e => setNewListUrl(e.target.value)}
                  placeholder="Amazon list URL (amazon.com or amazon.ca)"
                  onKeyDown={e => { if (e.key === 'Enter' && newListName.trim() && newListUrl.trim()) addList(); }}
                  style={{ flex: 1, padding: '9px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none' }}
                />
                <button onClick={addList} disabled={!newListName.trim() || !newListUrl.trim()}
                  style={{ padding: '9px 14px', background: newListName.trim() && newListUrl.trim() ? 'var(--feedback-warning-bg)' : 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', cursor: newListName.trim() && newListUrl.trim() ? 'pointer' : 'default', color: 'var(--feedback-warning-ink)', fontWeight: 600 }}>
                  Add
                </button>
              </div>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '6px 0 0' }}>
                Supports amazon.com 🇺🇸 and amazon.ca 🇨🇦.
              </p>
            </div>

            {/* Commission note */}
            <div style={{ padding: '10px 12px', background: 'var(--brand-cream)', borderRadius: 'var(--radius-10)', margin: '16px 0', display: 'flex', gap: 'var(--space-sm)' }}>
              <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>💡</span>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: 'var(--space-none)', lineHeight: 1.5 }}>
                You earn a commission on every purchase through your storefront link.
              </p>
            </div>

            <SaveError message={storefrontError} style={{ margin: '0 0 10px', fontSize: 'var(--font-size-sm)' }} />
            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={() => setShowModal(false)}
                style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>
                Cancel
              </button>
              <button onClick={save} disabled={lists.length === 0 || saving}
                style={{ flex: 1, padding: '11px', background: lists.length > 0 ? 'var(--neutral-800)' : 'var(--brand-sand)', border: 'none', borderRadius: 'var(--radius-10)', cursor: lists.length > 0 ? 'pointer' : 'default', fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--brand-white)' }}>
                {saving ? 'Saving...' : 'Save Storefront'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 🎬 YOUTUBE BLOCK
// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// 🎬 YOUTUBE BLOCK
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// 🎬 YOUTUBE BLOCK
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// 🎬 YOUTUBE BLOCK
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// 🎬 YOUTUBE BLOCK
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// 🎬 YOUTUBE BLOCK — manual add + inline playback
// ═══════════════════════════════════════════════════════════════
export function YouTubeBlock({ profile, onUpdate, readOnly = false }) {
  const [showModal,    setShowModal]    = useState(false);
  const [channelUrl,   setChannelUrl]   = useState('');
  const [newVideoUrl,  setNewVideoUrl]  = useState('');
  const [fetchingMeta, setFetchingMeta] = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [youtubeError, setYoutubeError] = useState('');
  const [playingId,    setPlayingId]    = useState(null); // video_id currently playing

  const THUMB_COLORS = ['#880e4f', '#7c3aed', '#B85C38', '#1e40af', '#065f46'];

  const data            = profile?.youtube_data || {};
  const videos          = data.videos           || [];
  const savedChannelUrl = data.channel_url      || '';

  useEffect(() => {
    if (showModal) {
      setChannelUrl(savedChannelUrl);
      setYoutubeError('');
    }
  }, [showModal]);

  // Close inline player when modal opens
  useEffect(() => {
    if (showModal) setPlayingId(null);
  }, [showModal]);

  function extractVideoId(url) {
    const m = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/
    );
    return m ? m[1] : null;
  }

  async function addVideo() {
    if (!newVideoUrl.trim() || videos.length >= 3 || fetchingMeta) return;
    const videoId = extractVideoId(newVideoUrl);
    if (!videoId) { alert('Please paste a valid YouTube video URL'); return; }

    // Check not already added
    if (videos.some(v => v.video_id === videoId)) {
      alert('This video is already added');
      return;
    }

    setYoutubeError('');
    setFetchingMeta(true);
    let videoData = {
      url:           `https://www.youtube.com/watch?v=${videoId}`,
      video_id:      videoId,
      title:         '',
      thumbnail_url: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
      thumb_color:   THUMB_COLORS[videos.length % THUMB_COLORS.length],
    };

    try {
      const res = await fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
      );
      if (res.ok) {
        const d = await res.json();
        videoData.title         = d.title         || '';
        videoData.thumbnail_url = d.thumbnail_url  || videoData.thumbnail_url;
      }
    } catch (err) {
      console.warn('oEmbed failed:', err.message);
    }

    const ok = await saved(onUpdate({
      youtube_data: {
        ...data,
        channel_url: channelUrl || savedChannelUrl,
        videos:      [...videos, videoData],
      }
    }));
    setFetchingMeta(false);
    // The box used to clear whether or not the video saved, so it simply
    // never appeared (audit 17.1). The link stays in the box now.
    if (!ok) { setYoutubeError(SAVE_FAILED); return; }
    setNewVideoUrl('');
  }

  async function removeVideo(idx) {
    const removed = videos[idx];
    if (playingId === removed?.video_id) setPlayingId(null);
    setYoutubeError('');
    if (!(await saved(onUpdate({
      youtube_data: { ...data, videos: videos.filter((_, i) => i !== idx) }
    })))) {
      setYoutubeError("That didn't remove. Check your connection and try again.");
    }
  }

  async function save() {
    setSaving(true);
    setYoutubeError('');
    const ok = await saved(onUpdate({ youtube_data: { ...data, channel_url: channelUrl.trim() } }));
    setSaving(false);
    if (!ok) { setYoutubeError(SAVE_FAILED); return; }
    setShowModal(false);
  }

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', overflow: 'hidden', position: 'relative' }}>
      {!readOnly && <EDIT_BTN onClick={() => setShowModal(true)} />}

      {/* ── Header ── */}
      <div style={{ padding: `14px ${readOnly ? 16 : EDGE_CLEAR_OF_EDIT}px 10px 16px` }}>
        <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>🎬 My YouTube</p>
      </div>

      {/* ── Empty state ── */}
      {videos.length === 0 && readOnly && (
        <div style={{ padding: '4px 16px 16px' }}><VisitorEmpty /></div>
      )}
      {videos.length === 0 && !readOnly && (
        <div style={{ padding: '8px 16px 20px', textAlign: 'center' }}>
          <span style={{ fontSize: 'var(--font-size-h2)', display: 'block', marginBottom: 'var(--space-sm)', opacity: 0.25 }}>🎬</span>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Showcase your videos, paste any YouTube link and the title and thumbnail pull automatically.
          </p>
          <button onClick={() => setShowModal(true)}
            style={{ padding: '8px 20px', background: 'var(--feedback-error)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' }}>
            + Add Videos
          </button>
        </div>
      )}

      {/* ── Populated state ── */}
      {videos.length > 0 && (
        <div style={{ padding: '0 16px 14px' }}>
          {videos.map((v, i) => (
            <div key={v.video_id || i}
              style={{ padding: '10px 0', borderTop: i > 0 ? '1px solid var(--neutral-100)' : 'none' }}>

              {/* ── Inline player (shown when playing) ── */}
              {playingId === v.video_id ? (
                <div style={{ position: 'relative' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${v.video_id}?autoplay=1&rel=0`}
                    title={v.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ width: '100%', aspectRatio: '16/9', borderRadius: 'var(--radius-10)', border: 'none', display: 'block' }}
                  />
                  {/* Close inline player */}
                  <button
                    onClick={() => setPlayingId(null)}
                    style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: 'var(--radius-circle)', background: 'rgba(0,0,0,0.7)', border: 'none', color: 'var(--brand-white)', fontSize: 'var(--font-size-base)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                    ✕
                  </button>
                  {/* Title below player */}
                  <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: '8px 0 0', lineHeight: 1.3 }}>
                    {v.title || 'YouTube Video'}
                  </p>
                </div>
              ) : (
                /* ── Thumbnail row (default state) ── */
                <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
                  {/* Thumbnail — click to play inline */}
                  <div
                    onClick={() => setPlayingId(v.video_id)}
                    style={{ width: 112, height: 63, borderRadius: 'var(--radius-md)', overflow: 'hidden', flexShrink: 0, position: 'relative', cursor: 'pointer', background: `linear-gradient(135deg, ${v.thumb_color || THUMB_COLORS[i % THUMB_COLORS.length]}, ${v.thumb_color || THUMB_COLORS[i % THUMB_COLORS.length]}cc)` }}
                    onMouseEnter={e => { const overlay = e.currentTarget.querySelector('.play-overlay'); if (overlay) overlay.style.background = 'rgba(0,0,0,0.75)'; }}
                    onMouseLeave={e => { const overlay = e.currentTarget.querySelector('.play-overlay'); if (overlay) overlay.style.background = 'rgba(0,0,0,0.55)'; }}>
                    <img src={v.thumbnail_url} alt={v.title}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { e.currentTarget.style.display = 'none'; }} />
                    <div className="play-overlay"
                      style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', transition: 'background 0.15s' }}>
                      <div style={{ width: 30, height: 30, borderRadius: 'var(--radius-circle)', background: 'var(--feedback-error)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                        <div style={{ width: 0, height: 0, borderLeft: '9px solid var(--brand-white)', borderTop: '6px solid transparent', borderBottom: '6px solid transparent', marginLeft: 3 }} />
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 6px', lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {v.title || 'YouTube Video'}
                    </p>
                    <button
                      onClick={() => setPlayingId(v.video_id)}
                      style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-error)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--space-none)' }}>
                      ▶ Play
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {savedChannelUrl && (
            <a href={savedChannelUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'block', textAlign: 'center', fontSize: 'var(--font-size-xs)', color: 'var(--feedback-error)', fontWeight: 700, textDecoration: 'none', marginTop: 'var(--space-10)', padding: 'var(--space-sm)', background: '#fef2f2', borderRadius: 'var(--radius-md)' }}>
              View full channel →
            </a>
          )}
        </div>
      )}

      {/* ── Edit Modal ── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)' }}
          onClick={() => setShowModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'relative', width: 400, background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--neutral-100)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>🎬 My YouTube</h3>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>
                    Add up to 3 videos. Title and thumbnail pull automatically from the URL.
                  </p>
                </div>
                <button onClick={() => setShowModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', color: 'var(--neutral-400)', cursor: 'pointer', lineHeight: 1, padding: 'var(--space-none)' }}>×</button>
              </div>
            </div>

            <div style={{ padding: '16px 24px 24px' }}>

              {/* Channel URL — for "View channel" link only */}
              <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 4px' }}>
                Channel URL <span style={{ fontWeight: 400, color: 'var(--neutral-400)' }}>· optional</span>
              </p>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '0 0 8px' }}>
                Used for the "View full channel →" link at the bottom of the block.
              </p>
              <input
                value={channelUrl}
                onChange={e => setChannelUrl(e.target.value)}
                placeholder="https://youtube.com/@yourchannel"
                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-xl)' }}
              />

              {/* Add video */}
              <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 4px' }}>Add a video</p>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '0 0 8px' }}>
                Paste any YouTube URL, title and thumbnail are fetched automatically.
              </p>
              <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: videos.length >= 3 ? 6 : 18 }}>
                <input
                  value={newVideoUrl}
                  onChange={e => setNewVideoUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addVideo(); }}
                  placeholder="https://youtube.com/watch?v=..."
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }}
                />
                <button
                  onClick={addVideo}
                  disabled={!newVideoUrl.trim() || fetchingMeta || videos.length >= 3}
                  style={{ padding: '10px 16px', background: newVideoUrl.trim() && !fetchingMeta && videos.length < 3 ? 'var(--feedback-error)' : 'var(--brand-sand)', color: newVideoUrl.trim() && videos.length < 3 ? 'var(--brand-white)' : 'var(--neutral-400)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer', flexShrink: 0, minWidth: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {fetchingMeta
                    ? <div style={{ width: 12, height: 12, border: '2px solid rgba(220,38,38,0.3)', borderTopColor: 'var(--feedback-error)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.8s linear infinite' }} />
                    : '+ Add'}
                </button>
              </div>
              {videos.length >= 3 && (
                <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: '0 0 16px' }}>
                  Max 3 videos, remove one to add another
                </p>
              )}

              {/* Currently showing */}
              {videos.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 600, color: 'var(--neutral-400)', letterSpacing: 0.5, textTransform: 'uppercase', margin: '0 0 8px' }}>
                    Currently showing ({videos.length}/3)
                  </p>
                  {videos.map((v, i) => (
                    <div key={v.video_id || i}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '8px 0', borderTop: i > 0 ? '1px solid var(--neutral-100)' : 'none' }}>
                      <div style={{ width: 56, height: 32, borderRadius: 5, overflow: 'hidden', flexShrink: 0, background: `linear-gradient(135deg, ${v.thumb_color || THUMB_COLORS[i % THUMB_COLORS.length]}, ${v.thumb_color || THUMB_COLORS[i % THUMB_COLORS.length]}cc)`, position: 'relative' }}>
                        <img src={v.thumbnail_url} alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => { e.currentTarget.style.display = 'none'; }} />
                      </div>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-800)', margin: 'var(--space-none)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v.title || 'YouTube Video'}
                      </p>
                      <button onClick={() => removeVideo(i)}
                        style={{ background: 'none', border: 'none', color: 'var(--neutral-300)', cursor: 'pointer', fontSize: 'var(--font-size-md)', padding: '0 2px', flexShrink: 0 }}
                        onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                        onMouseLeave={e => e.currentTarget.style.color = '#d6d3d1'}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              <SaveError message={youtubeError} style={{ margin: '0 0 10px', fontSize: 'var(--font-size-sm)' }} />
              <button onClick={save} disabled={saving}
                style={{ width: '100%', padding: 'var(--space-md)', background: 'var(--neutral-800)', color: 'var(--brand-paper)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer' }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════
// 🎙️ PODCAST BLOCK
// ═══════════════════════════════════════════════════════════════
export function PodcastBlock({ profile, onUpdate, readOnly = false }) {
  const [showModal,    setShowModal]    = useState(false);
  const [podcastName,  setPodcastName]  = useState('');
  const [rssUrl,       setRssUrl]       = useState('');
  const [spotifyUrl,   setSpotifyUrl]   = useState('');
  const [appleUrl,     setAppleUrl]     = useState('');
  const [connected,    setConnected]    = useState(false);
  const [fetching,     setFetching]     = useState(false);
  const [manualTitle,  setManualTitle]  = useState('');
  const [manualUrl,    setManualUrl]    = useState('');
  const [saving,       setSaving]       = useState(false);
  const [podcastError, setPodcastError] = useState('');
  const [playingId,    setPlayingId]    = useState(null);
  const [progress,     setProgress]     = useState(0);
  const [currentTime,  setCurrentTime]  = useState('0:00');
  const [isOwner,      setIsOwner]      = useState(false);
  const audioRef = useRef(null);

  const data          = profile?.podcast_data  || {};
  const episodes      = data.episodes          || [];
  const savedName     = data.podcast_name      || '';
  const savedRss      = data.rss_url           || '';
  const savedSpotify  = data.spotify_url       || '';
  const savedApple    = data.apple_url         || '';
  const lastFetched   = data.last_fetched_at   || null;

  useEffect(() => {
    getCurrentUser().then((user) => {
      // Only the matching user id is the owner. `!profile?.user_id ||` used
      // to make any signed-in reader the owner of a profile with no user_id
      // (audit 18.2).
      if (user && profile?.user_id && profile.user_id === user.id) {
        setIsOwner(true);
      }
    });
  }, [profile]);

  useEffect(() => {
    if (showModal) {
      setPodcastName(savedName);
      setRssUrl(savedRss);
      setSpotifyUrl(savedSpotify);
      setAppleUrl(savedApple);
      setConnected(!!savedRss);
      setPodcastError('');
    }
  }, [showModal]);

  // ── Audio playback ────────────────────────────────────────────
  useEffect(() => {
    if (!audioRef.current) return;
    const audio = audioRef.current;
    function onTimeUpdate() {
      const pct = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
      setProgress(pct);
      const mins = Math.floor(audio.currentTime / 60);
      const secs = Math.floor(audio.currentTime % 60).toString().padStart(2, '0');
      setCurrentTime(`${mins}:${secs}`);
    }
    function onEnded() { setPlayingId(null); setProgress(0); setCurrentTime('0:00'); }
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, [playingId]);

  function handlePlay(ep, idx) {
    if (playingId === idx) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (ep.audio_url && audioRef.current) {
      audioRef.current.src = ep.audio_url;
      audioRef.current.play().catch(() => {
        if (ep.url) window.open(ep.url, '_blank');
      });
      setPlayingId(idx);
      setProgress(0);
      setCurrentTime('0:00');
    } else if (ep.url) {
      window.open(ep.url, '_blank');
    }
  }

  // ── Shared fetch logic (connect + refresh both use this) ──────
  async function fetchLatestEpisodes(urlToUse) {
    const url = (urlToUse || rssUrl).trim();
    if (!url) return;
    setFetching(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetchWithTimeout(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/podcast-rss-episodes`,
        {
          method: 'POST',
          headers: bookSearchHeaders(session, SUPABASE_PUBLISHABLE_KEY),
          body: JSON.stringify({ rss_url: url }),
        },
        FEED_TIMEOUT_MS
      );
      const d = await res.json();
      if (d.error) { alert(`Could not fetch podcast: ${d.error}`); return; }

      if (!podcastName.trim() && d.podcast_title) setPodcastName(d.podcast_title);

      const updated = {
        ...data,
        rss_url:         url,
        podcast_name:    podcastName.trim() || d.podcast_title || savedName,
        spotify_url:     spotifyUrl.trim()  || savedSpotify,
        apple_url:       appleUrl.trim()    || savedApple,
        last_fetched_at: new Date().toISOString(),
        episodes:        d.episodes,
      };
      if (!(await saved(onUpdate({ podcast_data: updated })))) {
        alert("Your podcast didn't save. Check your connection and try again.");
        return;
      }
      setConnected(true);
    } catch (err) {
      alert('Could not connect. Please check your RSS URL.');
      console.error(err);
    } finally {
      setFetching(false);
    }
  }

  async function handleConnect() {
    await fetchLatestEpisodes(rssUrl);
  }

  async function addEpisodeManually() {
    if (!manualTitle.trim() || !manualUrl.trim()) return;
    const entry = { title: manualTitle.trim(), url: manualUrl.trim(), description: '', duration: '', date: '', audio_url: '' };
    setPodcastError('');
    if (!(await saved(onUpdate({ podcast_data: { ...data, episodes: [...episodes, entry] } })))) {
      setPodcastError(SAVE_FAILED);   // the title and link stay in the boxes
      return;
    }
    setManualTitle('');
    setManualUrl('');
  }

  async function removeEpisode(idx) {
    setPodcastError('');
    if (!(await saved(onUpdate({ podcast_data: { ...data, episodes: episodes.filter((_, i) => i !== idx) } })))) {
      setPodcastError("That didn't remove. Check your connection and try again.");
    }
  }

  async function save() {
    setSaving(true);
    setPodcastError('');
    const ok = await saved(onUpdate({
      podcast_data: {
        ...data,
        podcast_name: podcastName.trim() || savedName,
        spotify_url:  spotifyUrl.trim(),
        apple_url:    appleUrl.trim(),
      }
    }));
    setSaving(false);
    if (!ok) { setPodcastError(SAVE_FAILED); return; }
    setShowModal(false);
  }

  const listenUrl = savedSpotify || savedApple || '';

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', overflow: 'hidden', position: 'relative' }}>
     
      <audio ref={audioRef} style={{ display: 'none' }} />

      {/* ── Header. Its own pencil and refresh sit at the right end, so the
          row stops short of the ✕ only. ── */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `14px ${readOnly ? 16 : EDGE_CLEAR_OF_REMOVE}px 10px 16px` }}>
  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>🎙️ My Podcast</p>
  <div style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'center' }}>
    {!readOnly && isOwner && savedRss && (
      <button
        onClick={() => fetchLatestEpisodes(savedRss)}
        disabled={fetching}
        title="Refresh latest episodes"
        style={{ width: 24, height: 24, borderRadius: 'var(--radius-circle)', background: 'var(--neutral-100)', border: 'none', cursor: fetching ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-sm)' }}
        onMouseEnter={e => { if (!fetching) e.currentTarget.style.background = '#e8e4dc'; }}
        onMouseLeave={e => e.currentTarget.style.background = '#f5f5f4'}>
        <span style={{ display: 'inline-block', animation: fetching ? 'spin 0.8s linear infinite' : 'none' }}>🔄</span>
      </button>
    )}
    {!readOnly && (
    <button
      onClick={() => setShowModal(true)}
      style={{ width: 24, height: 24, borderRadius: 'var(--radius-circle)', background: 'var(--neutral-100)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseEnter={e => e.currentTarget.style.background = '#e8e4dc'}
      onMouseLeave={e => e.currentTarget.style.background = '#f5f5f4'}>
      <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)' }}>✎</span>
    </button>
    )}
  </div>
</div>

      {/* ── Empty state ── */}
      {episodes.length === 0 && readOnly && (
        <div style={{ padding: '4px 16px 16px' }}><VisitorEmpty /></div>
      )}
      {episodes.length === 0 && !readOnly && (
        <div style={{ padding: '20px 16px', textAlign: 'center' }}>
          <span style={{ fontSize: 'var(--font-size-h2)', display: 'block', marginBottom: 'var(--space-sm)', opacity: 0.3 }}>🎙️</span>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 12px' }}>Connect your podcast to show your latest episodes.</p>
          <button onClick={() => setShowModal(true)}
            style={{ padding: '8px 20px', background: '#7c3aed', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>
            Connect Podcast
          </button>
        </div>
      )}

      {/* ── Populated state ── */}
      {episodes.length > 0 && (
        <div style={{ padding: '0 16px 14px' }}>
          {/* Podcast header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', paddingBottom: 'var(--space-10)', marginBottom: 'var(--space-xxs)' }}>
            <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-10)', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xxl)', flexShrink: 0 }}>
              🎙️
            </div>
            <div>
              <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>{savedName || 'My Podcast'}</p>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>{episodes.length} recent episode{episodes.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {/* Episodes */}
          {episodes.map((ep, i) => {
            const isPlaying = playingId === i;
            return (
              <div key={i} style={{ padding: '10px 0', borderTop: '1px solid var(--neutral-100)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-10)', alignItems: 'flex-start' }}>
                  <button onClick={() => handlePlay(ep, i)}
                    style={{ width: 36, height: 36, borderRadius: 'var(--radius-circle)', background: isPlaying ? '#7c3aed' : 'var(--neutral-100)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 'var(--space-xxs)', transition: 'background 0.15s' }}>
                    {isPlaying ? (
                      <div style={{ display: 'flex', gap: 'var(--space-xxs)' }}>
                        <div style={{ width: 3, height: 12, background: 'var(--brand-white)', borderRadius: 1 }} />
                        <div style={{ width: 3, height: 12, background: 'var(--brand-white)', borderRadius: 1 }} />
                      </div>
                    ) : (
                      <div style={{ width: 0, height: 0, borderLeft: '10px solid #7c3aed', borderTop: '6px solid transparent', borderBottom: '6px solid transparent', marginLeft: 'var(--space-xxs)' }} />
                    )}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 2px', lineHeight: 1.3 }}>{ep.title}</p>
                    {ep.description && (
                      <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: '0 0 4px', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {ep.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'center', flexWrap: 'wrap' }}>
                      {ep.duration && <span style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)' }}>{ep.duration}</span>}
                      {ep.duration && ep.date && <span style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)' }}>·</span>}
                      {ep.date && <span style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)' }}>{ep.date}</span>}
                    </div>
                  </div>
                </div>

                {/* Progress bar when playing */}
                {isPlaying && (
                  <div style={{ marginTop: 'var(--space-sm)', marginLeft: 46 }}>
                    <div style={{ height: 3, borderRadius: 2, background: 'var(--neutral-100)', cursor: 'pointer' }}
                      onClick={e => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const pct  = (e.clientX - rect.left) / rect.width;
                        if (audioRef.current?.duration) {
                          audioRef.current.currentTime = pct * audioRef.current.duration;
                        }
                      }}>
                      <div style={{ height: '100%', width: `${progress}%`, borderRadius: 2, background: '#7c3aed', transition: 'width 0.5s linear' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--space-xxs)' }}>
                      <span style={{ fontSize: 'var(--font-size-micro)', color: 'var(--neutral-400)' }}>{currentTime}</span>
                      <span style={{ fontSize: 'var(--font-size-micro)', color: 'var(--neutral-400)' }}>{ep.duration}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Listen link */}
          {listenUrl && (
            <a href={ensureUrl(listenUrl)} target="_blank" rel="noopener noreferrer"
              style={{ display: 'block', textAlign: 'center', fontSize: 'var(--font-size-xs)', color: '#7c3aed', fontWeight: 600, textDecoration: 'none', marginTop: 'var(--space-sm)' }}>
              Listen on Spotify / Apple →
            </a>
          )}
        </div>
      )}

      {/* ── Edit Modal ── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)' }}
          onClick={() => setShowModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'relative', width: 400, background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--neutral-100)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>🎙️ My Podcast</h3>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Connect your podcast RSS feed and your latest episodes show up automatically.</p>
                </div>
                <button onClick={() => setShowModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', color: 'var(--neutral-400)', cursor: 'pointer', lineHeight: 1, padding: 'var(--space-none)' }}>×</button>
              </div>
            </div>

            <div style={{ padding: '16px 24px 24px' }}>

              {/* Podcast name */}
              <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Podcast Name</p>
              <input value={podcastName} onChange={e => setPodcastName(e.target.value)}
                placeholder="Your podcast name"
                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-14)' }} />

              {/* RSS URL + Connect */}
              <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 6px' }}>RSS Feed URL</p>
              <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-6)' }}>
                <input value={rssUrl} onChange={e => { setRssUrl(e.target.value); setConnected(false); }}
                  placeholder="https://anchor.fm/s/.../podcast/rss"
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }} />
                <button onClick={handleConnect} disabled={!rssUrl.trim() || fetching}
                  style={{ padding: '10px 16px', background: connected ? '#ecfdf5' : fetching ? 'var(--neutral-100)' : '#7c3aed', color: connected ? 'var(--feedback-success)' : fetching ? 'var(--neutral-400)' : 'var(--brand-white)', border: connected ? '1px solid #a7f3d0' : 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer', flexShrink: 0, minWidth: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  {fetching
                    ? <><div style={{ width: 10, height: 10, border: '2px solid var(--neutral-300)', borderTopColor: 'var(--neutral-500)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.8s linear infinite' }} /> Fetching...</>
                    : connected ? '✓ Connected' : 'Connect'}
                </button>
              </div>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '0 0 14px' }}>
                Find your RSS feed in your podcast hosting dashboard (Spotify for Podcasters, Buzzsprout, Anchor, etc.)
              </p>

              {/* Connected box + last fetched + refresh */}
              {connected && (
                <div style={{ padding: '10px 14px', background: 'var(--feedback-success-bg)', borderRadius: 'var(--radius-10)', border: '1px solid #bbf7d0', marginBottom: 'var(--space-14)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-success)', margin: '0 0 2px', fontWeight: 600 }}>✓ Feed connected</p>
                      <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: 'var(--space-none)', lineHeight: 1.5 }}>
                        We pull your 3 most recent episodes automatically.
                      </p>
                    </div>
                    <button onClick={() => fetchLatestEpisodes(rssUrl)} disabled={fetching}
                      style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 600, color: fetching ? 'var(--neutral-400)' : '#7c3aed', background: 'none', border: 'none', cursor: fetching ? 'not-allowed' : 'pointer', padding: 'var(--space-none)', flexShrink: 0, marginLeft: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <span style={{ animation: fetching ? 'spin 0.8s linear infinite' : 'none', display: 'inline-block' }}>🔄</span>
                      {fetching ? 'Refreshing...' : 'Refresh now'}
                    </button>
                  </div>
                  {lastFetched && (
                    <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '6px 0 0' }}>
                      Last refreshed: {new Date(lastFetched).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              )}

              {/* Where to listen */}
              <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 6px' }}>
                Where can people listen? <span style={{ fontWeight: 400, color: 'var(--neutral-400)' }}>(optional)</span>
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', marginBottom: 'var(--space-14)' }}>
                {[
                  { key: 'spotify', icon: '🟢', value: spotifyUrl, onChange: setSpotifyUrl, placeholder: 'https://open.spotify.com/show/...' },
                  { key: 'apple',   icon: '🟣', value: appleUrl,   onChange: setAppleUrl,   placeholder: 'https://podcasts.apple.com/...' },
                ].map(p => (
                  <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                    <span style={{ fontSize: 'var(--font-size-md)' }}>{p.icon}</span>
                    <input value={p.value} onChange={e => p.onChange(e.target.value)} placeholder={p.placeholder}
                      style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                ))}
              </div>

              {/* OR ADD MANUALLY */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', margin: '12px 0' }}>
                <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
                <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', fontWeight: 600, letterSpacing: 0.5 }}>OR ADD MANUALLY</span>
                <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', marginBottom: 'var(--space-14)' }}>
                <input value={manualTitle} onChange={e => setManualTitle(e.target.value)}
                  placeholder="Episode title"
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }} />
                <input value={manualUrl} onChange={e => setManualUrl(e.target.value)}
                  placeholder="Episode URL (Spotify, Apple, YouTube...)"
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }} />
                <button onClick={addEpisodeManually} disabled={!manualTitle.trim() || !manualUrl.trim()}
                  style={{ padding: 'var(--space-sm)', background: manualTitle.trim() && manualUrl.trim() ? 'var(--neutral-800)' : 'var(--brand-sand)', color: manualTitle.trim() && manualUrl.trim() ? 'var(--brand-paper)' : 'var(--neutral-400)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: manualTitle.trim() && manualUrl.trim() ? 'pointer' : 'default' }}>
                  + Add Episode
                </button>
              </div>

              {/* Currently showing */}
              {episodes.length > 0 && (
                <div style={{ marginBottom: 'var(--space-lg)' }}>
                  <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 600, color: 'var(--neutral-400)', letterSpacing: 0.5, textTransform: 'uppercase', margin: '0 0 8px' }}>Currently showing</p>
                  {episodes.map((ep, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '8px 0', borderTop: i > 0 ? '1px solid var(--neutral-100)' : 'none' }}>
                      <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>🎙️</span>
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-800)', margin: 'var(--space-none)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ep.title}</p>
                      {ep.date && <span style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', flexShrink: 0 }}>{ep.date}</span>}
                      <button onClick={() => removeEpisode(i)}
                        style={{ background: 'none', border: 'none', color: 'var(--neutral-300)', cursor: 'pointer', fontSize: 'var(--font-size-md)', padding: '0 2px', flexShrink: 0 }}
                        onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                        onMouseLeave={e => e.currentTarget.style.color = '#d6d3d1'}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              <SaveError message={podcastError} style={{ margin: '0 0 10px', fontSize: 'var(--font-size-sm)' }} />
              <button onClick={save} disabled={saving}
                style={{ width: '100%', padding: 'var(--space-md)', background: 'var(--neutral-800)', color: 'var(--brand-paper)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer' }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// CustomLinksBlock — up to three links to places with no block of their own.
//
// What belongs here and what does not: this block is for destinations the
// profile has no home for. Anything that already has one goes there instead —
// Substack and Medium are the `substack` block, TikTok is a social handle on
// user_profiles (one of four fixed platforms, not a block), Etsy lives inside
// My Shop, and Bookshop is not a profile feature at all, it is a retailer in
// src/lib/retailers.js for buy links.
//
// Storage is the block's own `data`, the same per-block jsonb CustomShelfBlock
// uses, not a column on user_profiles. That is what gives each link an id an
// admin removal can name — see src/lib/customLinks.js.
//
// readOnly is a real prop rather than the noop-onUpdate the other blocks are
// given on the public profile. A visitor must not see the editor at all, and
// they get the report control in its place, which only makes sense on someone
// else's page.
// ─────────────────────────────────────────────────────────────────────────────
export function CustomLinksBlock({ blockData, blockId, profile, onUpdateBlock, readOnly = false }) {
  const links = linksFromBlockData(blockData);
  const notice = blockData?.removed_notice || null;

  const [editing,   setEditing]   = useState(false);
  const [label,     setLabel]     = useState('');
  const [url,       setUrl]       = useState('');
  const [formError, setFormError] = useState('');
  const [uploading, setUploading] = useState(null);   // link id mid-upload
  const [saving,    setSaving]    = useState(false);
  const [reporting, setReporting] = useState(null);   // link being reported
  const fileRef  = useRef(null);
  const uploadFor = useRef(null);
  const rootRef  = useRef(null);

  // This block edits IN PLACE -- every other block opens a modal with its own
  // way out -- and the pencil toggling it back was the only exit, which
  // nothing said. A reader finished adding links and had to refresh the page
  // to leave. So: a Done button (below), Escape, and a click anywhere outside
  // the block all close it. Not while a save or an upload is in flight,
  // because the error for either is only shown in edit mode, and closing
  // would hide it.
  useEffect(() => {
    if (!editing || saving || uploading) return;
    function onOutside(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setEditing(false);
    }
    function onKey(e) { if (e.key === 'Escape') setEditing(false); }
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [editing, saving, uploading]);

  const full = links.length >= MAX_CUSTOM_LINKS;

  // Every write goes through here so a failed save is never silent and the
  // editor never shows a state the database does not have.
  //
  // It sends `links` ALONE and relies on the other side MERGING it into the
  // block's existing data: ReaderProfile.jsx:1292 is
  // `data: { ...(b.data || {}), ...data }`, and that is the only onUpdateBlock
  // this block is ever given when it is editable (ReaderProfile.jsx:325, wired
  // from :1928, :1964 and :1988 — the public profile passes a noop and readOnly).
  // So `removed_notice` survives a reorder, a delete, an add and an image swap,
  // and dismissNotice is the only thing that clears it.
  //
  // If that merge ever becomes a replace, this block loses the admin-removal
  // notice on the owner's next edit — and every other block on the profile
  // loses whatever else it keeps in `data`. Change one, read both.
  //
  // onUpdateBlock answers true or false; it never throws. This used to wait
  // for a throw, so the alert below could never fire and a failed save looked
  // like a good one until the next visit (audit 7.1). On a failure saveBlocks
  // also puts the screen back, so the list shows what is really stored.
  async function writeLinks(next, extra = {}) {
    setSaving(true);
    let ok;
    try {
      ok = await saved(onUpdateBlock({ links: next, ...extra }));
    } finally {
      setSaving(false);
    }
    if (!ok) {
      alert("That didn't save. Check your connection and try again.");
      return false;
    }
    return true;
  }

  async function addLink() {
    setFormError('');
    if (full) return;

    const cleanLabel = normalizeLinkLabel(label);
    if (!cleanLabel) { setFormError('Give it a label so people know what it is.'); return; }

    const result = normalizeLinkUrl(url);
    if (!result.ok) { setFormError(result.reason); return; }

    const ok = await writeLinks([
      ...links,
      { id: newLinkId(), label: cleanLabel, url: result.url, image_url: null },
    ]);
    if (ok) { setLabel(''); setUrl(''); }
  }

  async function removeLink(id) {
    await writeLinks(links.filter(l => l.id !== id));
  }

  // Up/down rather than drag: three rows, and the profile already spends its
  // drag affordance on moving whole blocks around.
  async function move(id, delta) {
    const i = links.findIndex(l => l.id === id);
    const j = i + delta;
    if (i === -1 || j < 0 || j >= links.length) return;
    const next = [...links];
    [next[i], next[j]] = [next[j], next[i]];
    await writeLinks(next);
  }

  function pickImage(id) {
    uploadFor.current = id;
    fileRef.current?.click();
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    const id   = uploadFor.current;
    // Reset immediately: without this, picking the same file twice in a row
    // fires no change event the second time and the upload silently does not
    // happen.
    e.target.value = '';
    uploadFor.current = null;
    if (!file || !id) return;

    if (!file.type.startsWith('image/')) { alert('Pick an image file.'); return; }
    if (file.size > MAX_IMAGE_BYTES) { alert('Image must be under 1MB.'); return; }

    setUploading(id);
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('You need to be signed in.');
      const ext  = (file.name.split('.').pop() || 'png').toLowerCase();
      const path = `${LINK_IMAGE_PREFIX}/${user.id}/${Date.now()}.${ext}`;

      const { error } = await supabase.storage
        .from(LINK_IMAGE_BUCKET)
        .upload(path, file, { upsert: true });
      if (error) throw error;

      const { data } = supabase.storage.from(LINK_IMAGE_BUCKET).getPublicUrl(path);
      const publicUrl = data?.publicUrl;
      if (!publicUrl) throw new Error('Could not get the image URL.');

      await writeLinks(links.map(l => l.id === id ? { ...l, image_url: publicUrl } : l));
    } catch (err) {
      // Falls back to the default icon rather than a broken image: image_url
      // is simply never written, so the link keeps rendering.
      alert('Upload failed, so the link kept its default icon. ' + (err?.message || ''));
    } finally {
      setUploading(null);
    }
  }

  async function clearImage(id) {
    await writeLinks(links.map(l => l.id === id ? { ...l, image_url: null } : l));
  }

  async function dismissNotice() {
    // Only the field being changed. It used to re-send `links` as well, which
    // was harmless but read as "the other side replaces rather than merges" and
    // sent a reviewer looking for a bug that was not there. It merges — see
    // writeLinks above.
    await onUpdateBlock({ removed_notice: null });
  }

  // The icon IS the uploaded image — their logo, or any picture. Square frame
  // with object-fit: cover, which is how every other image on the profile is
  // fitted. No cropping: there is no crop code anywhere in this app, and
  // writing one means canvas work and a second stored image for a result the
  // reader cannot tell apart from this.
  function LinkIcon({ link, size }) {
    return (
      <div style={{
        width: size, height: size, borderRadius: size > 30 ? 12 : 9, flexShrink: 0,
        background: link.image_url ? 'var(--brand-white)' : 'var(--brand-cream)',
        border: '1px solid var(--brand-sand)', overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.round(size * 0.45),
      }}>
        {link.image_url
          ? <img src={link.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                 onError={e => { e.currentTarget.style.display = 'none'; }} />
          : '🔗'}
      </div>
    );
  }

  const Header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-md)', paddingRight: readOnly ? 0 : TITLE_CLEAR_OF_EDIT }}>
      <span style={{ fontSize: 'var(--font-size-md)', flexShrink: 0 }}>🔗</span>
      <span style={{
        fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)',
        letterSpacing: 1, textTransform: 'uppercase',
      }}>My Links</span>
    </div>
  );

  // ── Public view ───────────────────────────────────────────────────────────
  if (readOnly) {
    if (!links.length) return null;
    return (
      <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px' }}>
        {Header}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
          {links.map(link => (
            <div key={link.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <a
                href={link.url}
                target="_blank"
                rel="noreferrer noopener"
                style={{
                  flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                  border: '1px solid var(--brand-sand)', borderRadius: 999, padding: '7px 12px',
                  textDecoration: 'none', color: 'var(--neutral-800)',
                }}
              >
                <LinkIcon link={link} size={26} />
                <span style={{
                  fontSize: 'var(--font-size-xs)', fontWeight: 700, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{link.label}</span>
                <span style={{ marginLeft: 'auto', color: 'var(--neutral-400)', fontSize: 'var(--font-size-xs)' }}>↗</span>
              </a>
              <KebabMenu>
                <KebabItem danger onClick={() => setReporting(link)}>🚩 Report link</KebabItem>
              </KebabMenu>
            </div>
          ))}
        </div>
        {reporting && createPortal(
          <ReportModal
            entityType="custom_link"
            entityId={customLinkEntityId(profile?.user_id, blockId, reporting.id)}
            reportedUserId={profile?.user_id || null}
            contextNote={reporting.url}
            onClose={() => setReporting(null)}
          />,
          document.body,
        )}
      </div>
    );
  }

  // ── Owner view ────────────────────────────────────────────────────────────
  return (
    <div ref={rootRef} style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: '14px 16px', position: 'relative' }}>
      <EDIT_BTN onClick={() => setEditing(e => !e)} />
      {Header}

      <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ display: 'none' }} />

      {/* A link an admin removed. It sits where the link was, because this is
          the one screen where the owner can do anything about it. There is no
          notification system to route it through and one is not being built
          for this. */}
      {notice && (
        <div style={{
          background: 'var(--feedback-error-bg, #fdeeed)', border: '1px solid #f2c2be',
          borderRadius: 11, padding: '9px 13px', marginBottom: 'var(--space-sm)',
          fontSize: 'var(--font-size-xs)', color: '#7a4a45',
        }}>
          One of your links was removed for violating our guidelines
          {notice.label ? <>: <b>{notice.label}</b></> : null}. Questions? Find Help.
          <button onClick={dismissNotice} style={{
            marginLeft: 8, border: 'none', background: 'none', color: '#b3261e',
            cursor: 'pointer', fontWeight: 700, fontSize: 'var(--font-size-xs)',
          }}>dismiss</button>
        </div>
      )}

      {links.length === 0 && !editing && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: 'var(--space-none)' }}>
          Somewhere else people can find you: your site, your Linktree, your shop.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
        {links.map((link, i) => editing ? (
          <div key={link.id} style={{
            display: 'flex', gap: 'var(--space-sm)', alignItems: 'center',
            border: '1px solid var(--brand-sand)', borderRadius: 12, padding: '8px 11px',
          }}>
            <LinkIcon link={link} size={38} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)' }}>{link.label}</div>
              <div style={{
                fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{link.url}</div>
            </div>
            <button onClick={() => move(link.id, -1)} disabled={i === 0 || saving} title="Move up"
              style={{ border: 'none', background: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? 'var(--brand-sand)' : 'var(--neutral-500)' }}>↑</button>
            <button onClick={() => move(link.id, 1)} disabled={i === links.length - 1 || saving} title="Move down"
              style={{ border: 'none', background: 'none', cursor: i === links.length - 1 ? 'default' : 'pointer', color: i === links.length - 1 ? 'var(--brand-sand)' : 'var(--neutral-500)' }}>↓</button>
            <button
              onClick={() => link.image_url ? clearImage(link.id) : pickImage(link.id)}
              disabled={uploading === link.id || saving}
              style={{
                border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', borderRadius: 999,
                cursor: 'pointer', color: 'var(--neutral-500)', fontSize: 'var(--font-size-xxs)',
                fontWeight: 700, padding: '4px 10px', whiteSpace: 'nowrap',
              }}>
              {uploading === link.id ? 'uploading…' : link.image_url ? 'remove image' : 'upload logo'}
            </button>
            <button onClick={() => removeLink(link.id)} disabled={saving} title="Delete link"
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--neutral-400)' }}>✕</button>
          </div>
        ) : (
          <a key={link.id} href={link.url} target="_blank" rel="noreferrer noopener" style={{
            display: 'flex', gap: 'var(--space-sm)', alignItems: 'center',
            border: '1px solid var(--brand-sand)', borderRadius: 999, padding: '7px 12px',
            textDecoration: 'none', color: 'var(--neutral-800)',
          }}>
            <LinkIcon link={link} size={26} />
            <span style={{
              fontSize: 'var(--font-size-xs)', fontWeight: 700, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{link.label}</span>
            <span style={{ marginLeft: 'auto', color: 'var(--neutral-400)', fontSize: 'var(--font-size-xs)' }}>↗</span>
          </a>
        ))}
      </div>

      {editing && (full ? (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '9px 0 0' }}>
          {MAX_CUSTOM_LINKS} of {MAX_CUSTOM_LINKS} used. Remove one to add another, three keeps it a profile, not a link farm.
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 'var(--space-6)', marginTop: 'var(--space-10)', flexWrap: 'wrap' }}>
            <input
              value={label}
              onChange={e => setLabel(e.target.value.slice(0, MAX_LABEL_LENGTH))}
              placeholder="label"
              maxLength={MAX_LABEL_LENGTH}
              style={{
                flex: 1, minWidth: 110, padding: '9px 12px', borderRadius: 10,
                border: '1px solid var(--brand-sand)', fontSize: 'var(--font-size-xs)',
                outline: 'none', fontFamily: 'var(--font-body)',
              }}
            />
            <input
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addLink(); }}
              placeholder="mysite.com or https://…"
              style={{
                flex: 1.4, minWidth: 150, padding: '9px 12px', borderRadius: 10,
                border: '1px solid var(--brand-sand)', fontSize: 'var(--font-size-xs)',
                outline: 'none', fontFamily: 'var(--font-body)',
              }}
            />
            <button onClick={addLink} disabled={saving} style={{
              border: 'none', background: 'var(--brand-rust)', color: 'var(--brand-white)',
              borderRadius: 10, padding: '9px 15px', fontSize: 'var(--font-size-xs)',
              fontWeight: 700, cursor: saving ? 'default' : 'pointer',
            }}>+ add</button>
          </div>
          {formError && (
            <p style={{ fontSize: 'var(--font-size-xxs)', color: '#b3261e', margin: '6px 0 0' }}>{formError}</p>
          )}
          <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '8px 0 0' }}>
            No image uploaded means the plain 🔗. Bare domains get https:// added for you.
            Substack, TikTok, Etsy and your shop have their own spots, this is for everywhere else.
          </p>
        </>
      ))}

      {editing && (
        <button onClick={() => setEditing(false)} disabled={saving || !!uploading} style={{
          display: 'block', width: '100%', marginTop: 'var(--space-10)', padding: '9px',
          background: 'var(--neutral-100)', border: 'none', borderRadius: 10,
          fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-600)',
          cursor: saving || uploading ? 'default' : 'pointer',
        }}>Done</button>
      )}
    </div>
  );
}
